/**
 * Chat Logs Page
 * WhatsApp-style two-column chat: conversation list | chat view
 * - Template messages rendered as preview cards
 * - 24-hour customer service window indicator + reply input
 */
import EmptyState from '@/components/ui/EmptyState';
import StatusChip from '@/components/ui/StatusChip';
import { formatPhoneDisplay } from '@/components/ui/PhoneField';
import { useAuth } from '@/hooks/useAuth';
import {
  useWebSocket,
  type NewMessageEvent,
  type MessageStatusEvent,
  type ConversationUnreadEvent,
  type ConversationRoutingEvent,
  type MediaQuotaEvent,
  type MessageMediaEvent,
} from '@/hooks/useWebSocket';
import { patchUsage } from '@/hooks/useUsageLimits';
import adminApi, {
  TeamMember,
  WhatsAppTemplate,
  WhatsAppTemplateCarouselCard,
  ConversationRouting,
  ConversationRoutingState,
  RoutingHistoryEntry,
  MediaAsset,
  MediaAssetType,
  MediaBrowseResult,
  WHATSAPP_MEDIA_SIZE_LIMIT_BYTES,
  INSTAGRAM_MEDIA_SIZE_LIMIT_BYTES,
} from '@/services/admin.api';
import { usePlanGate } from '@/hooks/usePlanGate';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { isPlanLockTagged, showPlanLockIfTagged } from '@/utils/plan-lock.util';
import ContactPanel, { ContactPanelRoutingEvent } from '@/components/chat/ContactPanel';
import { isPermissionLockTagged } from '@/utils/permission-lock.util';
import { renderWhatsAppText } from '@/utils/whatsapp-format.util';
import TemplatePickerPopover from '@/components/admin/TemplatePickerPopover';
import {
  ArrowBack,
  Article,
  AssignmentInd,
  AttachFile,
  MicNone,
  ChatBubbleOutline,
  Check,
  ChevronRight,
  Close,
  Description,
  FolderOutlined,
  DoneAll,
  ErrorOutline,
  WarningAmberRounded,
  FilterList,
  Flag as FlagIcon,
  Image as ImageIcon,
  Link,
  MoreVert,
  Person,
  Phone,
  Refresh,
  Search,
  Send,
  VideoCameraBack,
  WhatsApp,
  CampaignOutlined,
  TaskAltOutlined,
} from '@mui/icons-material';
import MediaPreviewDialog from '@/components/common/MediaPreviewDialog';
import InstagramLogo from '@/components/common/InstagramLogo';
import ContactProfileDialog from '@/components/chat/ContactProfileDialog';
import { cachedRequest } from '@/utils/admin/requestCache';
import MessageBody, { MessageReactions } from '@/components/chat/MessageBody';
import type { MessageAttachment, MessageBodyProps } from '@/components/chat/MessageBody';
import {
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

// ── Types ─────────────────────────────────────────────────────────────────────

type ConversationChannel = 'whatsapp' | 'instagram';

interface ChannelIdentity {
  type: string;
  value: string;
}

interface Conversation {
  _id: string;
  channel?: ConversationChannel;
  channelIdentity?: ChannelIdentity;
  customerPhone: string;
  customerName?: string;
  /** Instagram only — @handle from Meta's User Profile API. WhatsApp rows never have this. */
  username?: string;
  /** Instagram only — ephemeral per Meta docs (URLs expire within days), display-only. */
  profilePictureUrl?: string;
  /** Instagram profile enrichment (User Profile API) — for the profile popover.
   *  Optional: undefined = unknown (app lacks Advanced Access), distinct from
   *  a real false. WhatsApp rows never carry these. */
  followerCount?: number;
  isUserFollowBusiness?: boolean;
  isBusinessFollowUser?: boolean;
  isVerifiedUser?: boolean;
  messageCount: number;
  lastMessage: string;
  lastMessageAt: string;
  status: string;
  tags?: string[];
  windowExpiresAt?: string;
  lastInboundMessageAt?: string;
  assignedTo?: {
    _id?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
  };
  /** Unread inbound messages — 0/absent once the conversation has been opened. */
  unreadCount?: number;
  /** Present for both channels as of Phase 1.4 — Instagram's own `routing` has
   *  no `state`/`escalation` (no bot yet), only `assignedAt`/`assignedBy`/
   *  `assignmentMethod`/`history`, which is why `ConversationRouting.state`
   *  is optional. Every `routing?.state` read in this file already treats
   *  absence as 'bot' via `?? 'bot'`, which is also the correct display for
   *  an Instagram thread (no bot chip is ever shown for one — see the
   *  `channel !== 'instagram'` guards around RoutingStateChip). */
  routing?: ConversationRouting;
  /** Absent means the current assignee hasn't opened it yet — the "New"
   *  badge's data source (same semantics as pages/admin/Contacts.tsx's
   *  field of the same name). Present for both channels. */
  assigneeSeenAt?: string | null;
  /** Set by an automation's "Flag for Review" node (aiControl.flagged).
   *  WhatsApp-only, same carve-out as `routing` — the Instagram store has no
   *  aiControl. Cleared from the row's own "Mark reviewed" action. */
  flagged?: boolean;
  /** Optional author-written note from the flag node, shown as the chip's tooltip. */
  flagReason?: string;
  flaggedAt?: string;
  /** Which automation raised the flag — the fallback label when the author left
   *  the (optional) reason blank, so a flag is never anonymous. */
  flagWorkflowName?: string;
  flagNodeLabel?: string;
  /** Automation-set agent pin, present only while still in force — the backend
   *  drops an expired one so the UI can never disagree with the reply pipeline
   *  about whether it applies. */
  agentPin?: { agentId: string; expiresAt?: string } | null;
  /** Automation-set Smart Answers override, same "only while in force" rule.
   *  `expiresAt: null` means it has no expiry at all. */
  ragOverride?: { value: 'force_on' | 'force_off'; expiresAt?: string | null } | null;
  metadata?: {
    /** Meta's click-to-WhatsApp ad attribution. Now refreshed on EVERY ad
     *  click, not just the first — so it reflects the most recent ad that
     *  brought this person back. Absent for non-ad-originated chats. */
    ctwaAttribution?: {
      sourceUrl?: string;
      sourceId?: string;
      headline?: string;
      capturedAt?: string;
      /** 'ad' | 'post' — paid vs organic. */
      sourceType?: string;
      body?: string;
      mediaType?: string;
      ctwaClid?: string;
    };
  };
  /** Instagram click-to-Direct ad attribution. A different shape from
   *  WhatsApp's: Meta sends `ad_id` + creative context and no ctwa_clid. */
  adReferral?: {
    ref?: string;
    source?: string;
    type?: string;
    adId?: string;
    adTitle?: string;
    photoUrl?: string;
    videoUrl?: string;
    capturedAt?: string;
  };
}

/** Contact identifier appropriate to the channel — a phone for WhatsApp, the
 *  @handle for Instagram (resolved server-side via Meta's User Profile API on
 *  first inbound message — see instagram.service.ts's processInboundEvent).
 *  Falls back to the raw Instagram-scoped id only for the rare thread whose
 *  profile lookup never succeeded (e.g. it predates that feature, or the
 *  lookup failed and was never retried). */
function formatContactIdentity(
  conv: Pick<Conversation, 'channel' | 'channelIdentity' | 'customerPhone' | 'username'>
): string {
  if (conv.channel === 'instagram') {
    if (conv.username) return `@${conv.username}`;
    return conv.channelIdentity?.value ? `Instagram user ${conv.channelIdentity.value}` : 'Instagram';
  }
  return formatPhoneDisplay(conv.customerPhone);
}

/** First-letter avatar fallback — customerPhone is empty for Instagram contacts, so it can't
 *  be blindly indexed like the WhatsApp-only code before this did. */
function contactInitial(conv: Pick<Conversation, 'channel' | 'channelIdentity' | 'customerPhone' | 'customerName'>): string {
  if (conv.customerName) return conv.customerName[0]?.toUpperCase() || '?';
  if (conv.channel === 'instagram') return 'IG';
  return conv.customerPhone?.[0]?.toUpperCase() || '?';
}

/** Best-effort human-readable message out of an axios error. */
function extractErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || fallback;
}

/** Who/what actually produced an outgoing (assistant-role) message —
 *  `metadata.source` + `selectedAgent`/`sentBy` already carry this, they were
 *  just never resolved to a display name before. Falls back to a plain "AI"
 *  for messages predating this metadata (source absent) so old history still
 *  renders sensibly. */
/** A concrete, human-readable name for how the reply was produced, from the
 *  message's `source`. Used as the last segment of the "handled by" caption. */
function sourceLabel(source: string | undefined): string {
  switch (source) {
    case 'rag':
      return 'Smart Answer';
    case 'openrouter':
      return 'AI';
    case 'qasources':
      return 'Knowledge base';
    case 'workflow_canned_reply':
      return 'Response';
    case 'system_handoff':
      return 'Handoff';
    default:
      return 'AI';
  }
}

/**
 * Resolves the "handled by" attribution for an assistant message into a chip.
 *
 * The routing-legibility invariant (Phase 1) is that every assistant message
 * carries `metadata.workflow` — a flow id, or the literal 'default' for the
 * always-on AI tier. This turns that + the existing `source`/`selectedAgent`
 * into a caption like:
 *   • Default · Maya · Smart Answer   (workflow==='default', RAG, agent Maya)
 *   • Break Word Flow · Instant Reply (a flow's canned reply)
 * Colour: violet for the default AI tier, cyan for a flow, teal for a human,
 * amber for campaigns.
 *
 * `caption` stays the single joined string the avatar tooltip needs. The
 * popover instead reads the same attribution in PARTS (`principal`, `agentName`,
 * `methodLabel`) so "Message info" can say, in as many words, whether a human
 * or the AI sent this — which the joined caption ("Default · Maya · Smart
 * Answer") never actually stated. Now that the AI can write to customer
 * records on its own, "who sent this" is the first question an admin asks.
 */
interface SenderInfo {
  avatarLabel: string;
  avatarBg: string;
  /** Joined, for the avatar tooltip / compact contexts. */
  caption: string;
  /** Who acted, in plain words: 'AI', a team member's name, 'Campaign', … */
  principal: string;
  /** Which agent persona produced it, when one is known. */
  agentName?: string;
  /** How it was produced — 'Smart Answer', 'Instant Reply', 'Handoff', … */
  methodLabel?: string;
}

function resolveSenderInfo(
  source: string | undefined,
  selectedAgent: string | undefined,
  sentBy: string | undefined,
  agentsList: Array<{ _id: string; name: string }>,
  teamMembersList: TeamMember[],
  campaignName: string | undefined,
  workflow?: string,
  workflowName?: string,
  clientName?: string
): SenderInfo {
  // Human / campaign / external-API / bare-system paths are unchanged — they
  // aren't part of the flow-vs-default routing model.
  if (source === 'manual') {
    const member = teamMembersList.find((m) => m._id === sentBy);
    const name = member ? `${member.firstName} ${member.lastName}`.trim() : 'Team member';
    return {
      avatarLabel: name[0]?.toUpperCase() || 'H',
      avatarBg: '#0f766e',
      caption: name,
      principal: name,
    };
  }
  if (source === 'campaign') {
    return {
      avatarLabel: 'C',
      avatarBg: '#b45309',
      caption: campaignName ? `Campaign · ${campaignName}` : 'Campaign',
      principal: campaignName ? `Campaign · ${campaignName}` : 'Campaign',
    };
  }
  if (source === 'external_api') {
    return {
      avatarLabel: 'API',
      avatarBg: '#6d28d9',
      caption: clientName ? `API · ${clientName}` : 'External API',
      principal: clientName ? `API · ${clientName}` : 'External API',
    };
  }

  const agentName = agentsList.find((a) => a._id === selectedAgent)?.name;
  const method = sourceLabel(source);

  // A flow handled (or influenced) the reply.
  if (workflow && workflow !== 'default') {
    const parts = [workflowName || 'Flow', agentName, method].filter(Boolean);
    return {
      avatarLabel: '⚡',
      avatarBg: '#0e7490',
      caption: parts.join(' · '),
      // Still the AI acting — a flow decides WHICH reply, not who sends it.
      principal: 'AI',
      ...(agentName ? { agentName } : {}),
      methodLabel: [workflowName || 'Flow', method].filter(Boolean).join(' · '),
    };
  }

  // The default AI tier — or an older message with no attribution recorded yet.
  if (source && source.startsWith('system') && source !== 'system_handoff') {
    return { avatarLabel: 'i', avatarBg: '#64748b', caption: 'System', principal: 'System' };
  }
  const parts = ['Default', agentName, method].filter(Boolean);
  return {
    avatarLabel: 'AI',
    avatarBg: '#4f46e5',
    caption: parts.join(' · '),
    principal: 'AI',
    ...(agentName ? { agentName } : {}),
    ...(method ? { methodLabel: method } : {}),
  };
}

/** Compact chip visualising routing.state — sized to match the tags row's
 *  existing Chip convention (height 18, 0.625rem) rather than a table-cell
 *  chip, since it lives in a dense list row / header, not a table. */
function RoutingStateChip({
  state,
  assignedToName,
}: {
  state: ConversationRoutingState | undefined;
  /** Shown appended to the label when state is 'human' — e.g. "Human · Priya Nair". */
  assignedToName: string | undefined;
}) {
  const s = state ?? 'bot';
  const chipSx = { height: 18, fontSize: '0.625rem', fontWeight: 700, '& .MuiChip-label': { px: 0.75 } };
  if (s === 'pending_human') {
    return <Chip label="Needs Human" size="small" sx={{ ...chipSx, bgcolor: '#fff3e0', color: '#e65100' }} />;
  }
  if (s === 'human') {
    return (
      <Chip
        label={assignedToName ? `Human · ${assignedToName}` : 'Human'}
        size="small"
        sx={{ ...chipSx, bgcolor: '#e0e7ff', color: '#3730a3' }}
      />
    );
  }
  return <Chip label="Bot" size="small" sx={{ ...chipSx, bgcolor: '#e2e8f0', color: '#475569' }} />;
}

/**
 * What the AI *did* on a turn, as opposed to what it said — the record writes
 * (a captured lead, a recorded appointment request, a filled contact field)
 * that leave no other trace in the conversation. A sent file or a handoff is
 * deliberately absent: each persists its own message, so it already shows.
 *
 * Rendered as an outlined row under the bubble rather than a filled chip: this
 * is a quiet audit note, not a status the reader has to act on, and the
 * transcript's own convention is that the bubble stays body + time + ticks.
 * Unknown keys still render (title-cased) so a tool added on the backend is
 * never silently invisible here.
 */
const TOOL_ACTION_LABELS: Record<string, string> = {
  create_lead: 'Lead captured',
  update_contact_field: 'Contact updated',
  request_appointment: 'Appointment requested',
  reschedule_appointment: 'Appointment rescheduled',
};

function humaniseToolName(name: string): string {
  return name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function MessageActions({
  tools,
  onOpenAction,
}: {
  tools: string[] | undefined;
  /** Chips for tools that produced a navigable record (lead, appointment) become clickable. */
  onOpenAction?: ((tool: string) => void) | undefined;
}) {
  if (!tools || tools.length === 0) return null;
  const NAVIGABLE = new Set(['create_lead', 'request_appointment', 'reschedule_appointment']);
  return (
    <Tooltip title="Records this reply created or updated" arrow placement="left">
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        {tools.map((t) => {
          const open = onOpenAction && NAVIGABLE.has(t) ? () => onOpenAction(t) : undefined;
          return (
            <Chip
              key={t}
              size="small"
              variant="outlined"
              icon={<TaskAltOutlined sx={{ fontSize: 12 }} />}
              label={TOOL_ACTION_LABELS[t] || humaniseToolName(t)}
              {...(open ? { onClick: open, clickable: true } : {})}
              sx={{
                height: 20,
                fontSize: '0.625rem',
                fontWeight: 600,
                color: open ? 'primary.main' : 'text.secondary',
                borderColor: open ? 'primary.light' : 'rgba(15, 23, 42, 0.14)',
                bgcolor: 'transparent',
                '& .MuiChip-label': { px: 0.75 },
                '& .MuiChip-icon': { color: open ? 'primary.main' : 'text.disabled', ml: 0.5, mr: -0.25 },
              }}
            />
          );
        })}
      </Stack>
    </Tooltip>
  );
}

/**
 * The one accent used by everything flag-related — chip, banner icon, and the
 * "Flagged" quick-view tab. Solid rose-600 rather than the pale tinted fill the
 * routing chips use: a flag is an exception that should catch the eye as you
 * scan a dense list, and a soft pastel among other soft pastels doesn't. Kept
 * as a single constant so those three surfaces can never drift apart.
 */
const FLAG_ACCENT = '#e11d48';

/**
 * What to call a flag, in one place.
 *
 * The Reason field is optional in the builder, so a flag frequently arrives
 * with nothing written on it. Falling back to the automation's own name means
 * a reviewer can always trace a mark back to the flow that raised it, instead
 * of seeing an anonymous "something flagged this". Node label is appended only
 * when the author renamed it — otherwise it just repeats "Flag for Review".
 */
function flagLabel(conv: Pick<Conversation, 'flagReason' | 'flagWorkflowName' | 'flagNodeLabel'>): string {
  if (conv.flagReason) return conv.flagReason;
  if (conv.flagWorkflowName) {
    const node =
      conv.flagNodeLabel && conv.flagNodeLabel !== 'Flag for Review' ? ` › ${conv.flagNodeLabel}` : '';
    return `Automation · ${conv.flagWorkflowName}${node}`;
  }
  return 'Flagged by an automation';
}

/** How long an automation's sticky state has left, from an ISO expiry.
 *  Distinct from formatRemaining(ms) below, which formats the 24h window from a
 *  duration — this one takes a timestamp and has to express "no expiry" too. */
function formatExpiryRemaining(expiresAt: string | null | undefined): string {
  if (!expiresAt) return 'no expiry';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const minutes = Math.round(ms / 60000);
  return minutes < 60 ? `${minutes}m left` : `${Math.round(minutes / 60)}h left`;
}

/**
 * Automation state currently steering this chat's AI — a pinned agent and/or a
 * Smart Answers override.
 *
 * Shown because both are invisible side effects of a flow that a person taking
 * over the chat has no other way to discover: "why is this answering as the
 * Japan agent" and "why are smart answers off" were previously only knowable
 * by reading the workflow. Only rendered while actually in force (the backend
 * drops expired ones), so it never reports state the reply pipeline ignores.
 */
function AutomationStateChips({
  conv,
  agentsList,
}: {
  conv: Pick<Conversation, 'agentPin' | 'ragOverride'>;
  /** Reuses the list already loaded for the "which AI agent replied" label —
   *  no extra request, and the same name resolution as the message rows. */
  agentsList: Array<{ _id: string; name: string }>;
}) {
  if (!conv.agentPin && !conv.ragOverride) return null;
  const pinnedName = conv.agentPin
    ? agentsList.find((a) => a._id === conv.agentPin!.agentId)?.name
    : undefined;
  const chipSx = {
    height: 18,
    fontSize: '0.625rem',
    fontWeight: 700,
    '& .MuiChip-label': { px: 0.75 },
  };
  return (
    <>
      {conv.agentPin && (
        <Tooltip
          title={`An automation pinned this chat to ${
            pinnedName || 'a specific AI agent'
          } — ${formatExpiryRemaining(conv.agentPin.expiresAt)}. Taking the chat over clears it.`}
        >
          <Chip
            label={pinnedName || 'Pinned agent'}
            size="small"
            sx={{ ...chipSx, bgcolor: '#ede9fe', color: '#5b21b6' }}
          />
        </Tooltip>
      )}
      {conv.ragOverride && (
        <Tooltip
          title={`An automation turned Smart Answers ${
            conv.ragOverride.value === 'force_off' ? 'OFF' : 'ON'
          } for this chat — ${formatExpiryRemaining(conv.ragOverride.expiresAt)}.`}
        >
          <Chip
            label={`Smart Answers ${conv.ragOverride.value === 'force_off' ? 'off' : 'on'}`}
            size="small"
            sx={{ ...chipSx, bgcolor: '#ecfccb', color: '#3f6212' }}
          />
        </Tooltip>
      )}
    </>
  );
}

/** "Flagged" chip — the visible read side of an automation's Flag node.
 *  Sized to match RoutingStateChip above so the two sit together in a row.
 *  The reason (or its automation-name fallback) is the tooltip, keeping the
 *  chip hover-discoverable rather than widening the dense list row. */
function FlaggedChip({ title }: { title: string }) {
  return (
    <Tooltip title={title}>
      <Chip
        icon={<FlagIcon sx={{ fontSize: '0.7rem !important', color: '#fff !important' }} />}
        label="Flagged"
        size="small"
        sx={{
          height: 18,
          fontSize: '0.625rem',
          fontWeight: 700,
          bgcolor: FLAG_ACCENT,
          color: '#fff',
          '& .MuiChip-label': { px: 0.5 },
        }}
      />
    </Tooltip>
  );
}

/** "Handled by" chip on an assistant message — the visible answer to
 *  "which layer replied, as which agent, how". Fed by resolveSenderInfo. */
/** `${firstName} ${lastName}`.trim(), falling back to `name`/a generic label —
 *  mirrors the fallback chain the assign-menu label already used. */
function formatAssigneeName(assignedTo: Conversation['assignedTo']): string | undefined {
  if (!assignedTo) return undefined;
  const full = `${assignedTo.firstName || ''} ${assignedTo.lastName || ''}`.trim();
  return full || assignedTo.name || undefined;
}

/** RoutingHistoryEntry's resolved actor/targetUser carry `email` instead of `name`, but
 *  are otherwise the same {_id, firstName?, lastName?} shape formatAssigneeName reads —
 *  reused as-is rather than a second name-formatting function. */
function formatEventUserName(
  user: RoutingHistoryEntry['actor'] | RoutingHistoryEntry['targetUser']
): string | undefined {
  return formatAssigneeName(user || undefined);
}

/** Walks routing.history chronologically (oldest → newest — the order the API
 *  already returns it in) and, for each entry, returns who held the
 *  conversation immediately before it took effect. The schema deliberately
 *  stores no `previousUserId` (Conversation.model.ts: "who held the chat
 *  before is already answered by the preceding entry") — this is that walk,
 *  done once so describeRoutingEvent can render "reassigned from A to B"
 *  without re-deriving it per row.
 *
 *  Nobody holds the conversation right after: an 'assigned' event with no
 *  target (the backend's stand-in for "unassigned" — see
 *  admin.controller.ts's bulkUnassignConversations), an 'escalated' that
 *  found no eligible agent, or a 'handed_back' to the bot. */
function computePreviousHolders(
  history: RoutingHistoryEntry[]
): Array<RoutingHistoryEntry['targetUser']> {
  const holders: Array<RoutingHistoryEntry['targetUser']> = [];
  let current: RoutingHistoryEntry['targetUser'] = undefined;
  for (const event of history) {
    holders.push(current);
    if (event.targetUser) {
      current = event.targetUser;
    } else if (event.type === 'assigned' || event.type === 'escalated' || event.type === 'handed_back') {
      current = undefined;
    }
  }
  return holders;
}

/** One routing.history entry -> what the timeline row shows. Colors mirror the
 *  RoutingStateChip convention (warning = needs a human, info = human-owned, success =
 *  a positive action just taken, default = back to the bot) rather than inventing a
 *  parallel palette for the same underlying states.
 *
 *  `previousHolder` (from computePreviousHolders) is only used by the
 *  'assigned' case, to tell an initial assignment apart from a reassignment. */
function describeRoutingEvent(
  event: RoutingHistoryEntry,
  previousHolder?: RoutingHistoryEntry['targetUser']
): {
  color: 'info' | 'warning' | 'success' | 'default';
  label: string;
  detail?: string;
} {
  // No top-level 'Someone' fallback here on purpose: 'assigned' needs to tell
  // "no actor at all" (the assignment-rules engine — actorId is deliberately
  // omitted on rule-driven writes, see contact-assignment-rules.service.ts)
  // apart from "an actor we just couldn't resolve a name for". The other
  // cases (claim/hand-back) always carry a real actor, so they fall back to
  // 'Someone' locally instead.
  const actorName = formatEventUserName(event.actor);
  const targetName = formatEventUserName(event.targetUser);
  const previousHolderName = formatEventUserName(previousHolder || undefined);

  const triggerDetail = ((): string | undefined => {
    const trigger = event.trigger;
    if (!trigger) return undefined;
    if (trigger.triggerType === 'explicit_request') {
      return trigger.matchedPhrase
        ? `Customer said “${trigger.matchedPhrase}” (${trigger.phraseSource === 'tenant' ? 'your phrase' : 'built-in phrase'})`
        : 'Customer asked for a person';
    }
    return `Bot replied with low confidence${typeof trigger.confidence === 'number' ? ` (${trigger.confidence}%)` : ''} twice in a row`;
  })();

  // How the assignee was picked — mirrors AssignmentMethod on the backend.
  // 'role_round_robin'/'owner_fallback' only ever appear here once a tenant has
  // configured a role-targeted handoff (Settings → Human Handoff, or a "Send to
  // a Team Member" node override); every pre-existing conversation's history
  // only ever has 'round_robin', unaffected.
  const assignmentMethodDetail = ((): string => {
    switch (event.method) {
      case 'role_round_robin':
        return 'role round-robin';
      case 'owner_fallback':
        return 'owner — configured role had no one available';
      case 'round_robin':
      default:
        return 'round-robin';
    }
  })();

  switch (event.type) {
    case 'escalated':
      return targetName
        ? {
            color: 'info',
            label: 'Escalated to human',
            detail: `Assigned to ${targetName} (${assignmentMethodDetail})${triggerDetail ? ` — ${triggerDetail}` : ''}`,
          }
        : {
            color: 'warning',
            label: 'Escalated — no agent available',
            ...(triggerDetail ? { detail: triggerDetail } : {}),
          };
    case 'assigned': {
      // No target = the backend's stand-in for "unassigned" — RoutingEventType
      // has no dedicated value for it (bulkUnassignConversations pushes an
      // 'assigned' event with an actor and no target rather than invent one).
      if (!event.targetUser) {
        return {
          color: 'default',
          label: 'Unassigned',
          detail: `by ${actorName || 'Someone'}`,
        };
      }
      // No actor at all on an 'assigned' event only ever happens on the LIVE
      // auto-trigger — a human explicitly re-running the rules engine (the
      // "Apply rules now" button, or a bulk-unassign's "re-apply rules" flag)
      // DOES stamp actorId now, same as an ordinary direct reassign. `method
      // === 'rule_manual'` is what tells those two apart (see
      // AssignmentMethod's doc comment, Conversation.model.ts) — without it
      // "assigned by Himanshu Jain" would read identically whether Himanshu
      // picked the target directly or a rule did.
      const assignerName = actorName || 'Assignment rules';
      const rulesSuffix = event.method === 'rule_manual' ? ' (assignment rules applied)' : '';
      if (previousHolder && previousHolder._id !== event.targetUser._id) {
        return {
          color: 'info',
          label: 'Reassigned',
          detail: `From ${previousHolderName || 'a previous team member'} to ${targetName} by ${assignerName}${rulesSuffix}`,
        };
      }
      return {
        color: 'info',
        label: 'Assigned',
        detail: `${targetName || 'a team member'} — assigned by ${assignerName}${rulesSuffix}`,
      };
    }
    case 'claimed':
      return { color: 'success', label: 'Claimed', detail: `${actorName || 'Someone'} claimed this conversation` };
    case 'handed_back':
      return { color: 'default', label: 'Handed back to bot', detail: `by ${actorName || 'Someone'}` };
    default:
      return { color: 'default', label: event.type };
  }
}

/**
 * Message types that are captured and stored, but deliberately NOT drawn as
 * bubbles in the transcript.
 *
 * `reaction` — an AUDIT ROW, not a turn. Its emoji is rendered as a badge on
 *   the message it targets (see MessageReactions), so a bubble here would
 *   duplicate it and break the reading order: the customer reacted to
 *   something far above, they did not "say" an emoji at this point.
 *
 * `unsupported` / `unknown` — Meta could not represent what the customer sent
 *   (error 131051: polls, view-once media, animated GIFs, deleted messages,
 *   consumer-app contact cards). Meta delivers a content-less stub with no
 *   media id, so there is nothing to show. It arrives ALONGSIDE a normal
 *   send — e.g. selecting a GIF with three photos yields three `image`
 *   messages plus one stub — and rendering the stub made a successful send
 *   look half-failed. Both spellings are matched because Meta uses `unknown`
 *   in its type enum and `unsupported` in its prose, and both appear live.
 *
 * All of these remain PERSISTED and are still visible via the API — this
 * filter only governs what the chat transcript draws.
 */
const NON_TRANSCRIPT_TYPES = new Set(['reaction', 'unsupported', 'unknown']);

/**
 * "From Ad" attribution chip — one component for both channels.
 *
 * WhatsApp and Instagram deliver ad attribution in different shapes: WhatsApp
 * sends `referral.headline` + `ctwa_clid`, Instagram sends `ad_id` +
 * `ads_context_data.ad_title` and has no click id at all. Both mean the same
 * thing to an operator ("this person arrived from an ad"), so they share one
 * chip and the differences live here rather than in two call sites.
 *
 * Paid vs organic matters: Meta's `source_type` distinguishes an ad from an
 * organic post link, and an agent handling a paid lead should know that.
 */
function AdSourceChip({
  conv,
  ctwaAttribution,
}: {
  conv: Conversation;
  /** From the detail response — the list row never carries it (see the
   *  ctwaAttribution state in ChatLogs). Falls back to the row's own copy. */
  ctwaAttribution?: NonNullable<Conversation['metadata']>['ctwaAttribution'];
}) {
  const wa = ctwaAttribution ?? conv.metadata?.ctwaAttribution;
  const ig = conv.adReferral;
  if (!wa && !ig) return null;

  // Instagram's `source: 'ADS'` and WhatsApp's `sourceType: 'ad'` both mean
  // paid. Anything else (an organic post link) is labelled accordingly rather
  // than being passed off as an ad.
  const isPaid = ig ? ig.source === 'ADS' : wa?.sourceType !== 'post';
  const title = ig?.adTitle || wa?.headline;
  const adId = ig?.adId || wa?.sourceId;

  const lines = [
    title ? `"${title}"` : null,
    adId ? `Ad ID ${adId}` : null,
    wa?.body || null,
    wa?.ctwaClid ? 'Conversion tracking available' : null,
  ].filter(Boolean);

  return (
    <Tooltip
      title={
        lines.length > 0
          ? lines.join(' · ')
          : isPaid
            ? 'Started from an ad'
            : 'Started from a link'
      }
    >
      <Chip
        icon={<CampaignOutlined sx={{ fontSize: 13 }} />}
        label={isPaid ? 'From Ad' : 'From Link'}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.6875rem',
          fontWeight: 600,
          bgcolor: '#fef3c7',
          color: '#92400e',
          '& .MuiChip-icon': { color: '#92400e', ml: 0.5 },
        }}
      />
    </Tooltip>
  );
}

function isTranscriptMessage(m: Message): boolean {
  return !m.messageType || !NON_TRANSCRIPT_TYPES.has(m.messageType);
}

interface Message {
  _id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  /** Meta's inbound type discriminator — decides how the body renders.
   *  Absent on outbound sends and on messages predating inbound capture, both
   *  of which fall through to plain text. */
  messageType?: string | undefined;
  attachments?: MessageAttachment[] | undefined;
  location?: MessageBodyProps['location'];
  contacts?: MessageBodyProps['contacts'];
  order?: MessageBodyProps['order'];
  system?: MessageBodyProps['system'];
  interactive?: MessageBodyProps['interactive'];
  providerErrors?: MessageBodyProps['providerErrors'];
  /** Reactions OTHERS placed on this message (normalized server-side from the
   *  stored Map). Rendered as badges under the bubble, not as their own row. */
  reactions?: Array<{ by: string; emoji: string }> | undefined;
  /** Present only on a reaction ROW — an audit record that is filtered out of
   *  the transcript, since its content is shown on its target instead. */
  reaction?: { targetMessageId?: string; emoji?: string } | undefined;
  status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | undefined;
  whatsappMessageId?: string | undefined;
  /** Instagram's provider message id — the socket layer reuses the `whatsappMessageId`
   *  payload key for both channels (see socket.util.ts), but the REST-fetched message
   *  document itself stores Instagram ids here, so status-matching needs to check both. */
  providerMessageId?: string | undefined;
  metadata?:
    | {
        model?: string;
        tokensUsed?: number;
        confidence?: number;
        selectedAgent?: string;
        source?: string;
        /** User id — only set on `source: 'manual'` messages (sent via this
         *  page's own composer, or the legacy manual-reply endpoint). */
        sentBy?: string;
        /** Actual values substituted into the sent template's {{n}} slots, captured at
         *  send time — present on messages sent after this field was introduced. */
        templateParams?: {
          header?: string;
          body?: string[];
          /** The actual per-send link behind a dynamic URL button (the template's
           *  `…/{{1}}` url with the slot filled). Lets the transcript show/open the
           *  real link that was sent instead of the unfilled placeholder. */
          buttonUrl?: string;
        };
        /** Present when status is 'failed' — the reason the WhatsApp send was rejected. */
        sendError?: string;
        /** Present only on `source: 'campaign'` — which campaign triggered this send. */
        campaignName?: string;
        /** Present only on `source: 'external_api'` — which ExternalApiClient
         *  (integration) triggered this send via the external gateway. */
        clientName?: string;
        /** Routing attribution (Phase 1): 'default' for the always-on AI tier,
         *  or a flow's id when a conversation-flow handled/influenced the reply. */
        workflow?: string;
        /** Human-readable name of the flow when `workflow` is a flow id. */
        workflowName?: string;
        /** The keyword phrase that matched the flow, for the reply's tooltip. */
        matchedPhrase?: string;
        /** AI tool-calling: the data writes this turn actually made (a captured
         *  lead, a recorded appointment request, a filled contact field). Absent
         *  when none fired. A sent file or a handoff is NOT listed — each of
         *  those persists its own message, so it is already visible in the
         *  transcript. See the backend's ToolTurnOutcome.toolsUsed. */
        toolsUsed?: string[];
        /** Instagram-only (Phase 2.3) — see MessageBody.tsx's own doc comment
         *  on why these were computed by the backend since Phase 2.3 but
         *  reached no UI until this pass. */
        storyContext?: MessageBodyProps['storyContext'];
        quickReplyPayload?: string;
        isUnsupported?: boolean;
      }
    | undefined;
}

interface ParsedTemplate {
  name: string;
  lang: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const TEMPLATE_RE = /^\[template:([^\]]+)\]\[lang:([^\]]+)\]/;

function parseTemplateMessage(content: string): ParsedTemplate | null {
  const match = content.match(TEMPLATE_RE);
  if (!match) return null;
  return { name: match[1] ?? '', lang: match[2] ?? '' };
}

/** Ordered list of `{{n}}`/`{{name}}` variable tokens in a template body —
 *  used by the manual template-send dialog to render one text field per slot,
 *  in the same order Meta expects them in the send-time `parameters` array. */
function templateBodyVariableTokens(bodyText: string | undefined): string[] {
  if (!bodyText) return [];
  return Array.from(bodyText.matchAll(/\{\{([^}]+)\}\}/g), (m) => m[1] as string);
}

/** Does this template have a URL button whose link ends in a `{{n}}` slot?
 *  Mirrors CampaignForm.tsx's helper of the same name and the backend's own
 *  detection (whatsapp-template-components.util.ts's DYNAMIC_PLACEHOLDER) —
 *  AUTHENTICATION templates are filtered out of `sendableTemplates` below
 *  entirely, so unlike the backend there's no auto-resolve case to consider
 *  here; a match always means the field is required. */
function hasDynamicUrlButton(template: WhatsAppTemplate | undefined): boolean {
  return !!template?.components.buttons?.some(
    (b) => b.type === 'URL' && /\{\{\s*\d+\s*\}\}/.test(b.url || '')
  );
}

/** A template header's Meta type as the library's own media category, or
 *  `undefined` for a TEXT header / no header — i.e. "this template cannot carry
 *  a file". Meta approves the header's TYPE, not the file in it, which is what
 *  makes swapping the file legal at all; swapping the type is not. */
function mediaTypeForHeader(
  headerType: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | undefined
): MediaAssetType | undefined {
  switch (headerType) {
    case 'IMAGE':
      return 'image';
    case 'VIDEO':
      return 'video';
    case 'DOCUMENT':
      return 'document';
    default:
      return undefined;
  }
}


/** Best-effort prefill for a template variable from data already on screen —
 *  the template author's own label for the slot (set in the template editor,
 *  e.g. "Customer name") is matched against what this conversation already
 *  knows. Anything that doesn't match is left blank rather than guessed —
 *  a wrong autofill sent to a customer is worse than an empty required field. */
function guessTemplateVariableValue(
  label: string | undefined,
  conversation: Pick<Conversation, 'customerName' | 'customerPhone'>
): string {
  if (!label) return '';
  const l = label.toLowerCase();
  if (/name/.test(l)) return conversation.customerName || '';
  if (/phone|mobile number|whatsapp number/.test(l)) {
    return conversation.customerPhone ? formatPhoneDisplay(conversation.customerPhone) : '';
  }
  return '';
}

function formatTemplateName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatLangCode(code: string): string {
  try {
    const tag = code.replace('_', '-');
    return new Intl.DisplayNames([tag], { type: 'language' }).of(tag) || code;
  } catch {
    return code;
  }
}

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
};

/** Cache key for the library root, which has no folder id of its own. */
const MEDIA_ROOT_KEY = 'root';

/** Compact byte size for the media picker — "4.2MB", "812KB". */
function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

/** One icon per WhatsApp media category, matching MessageBody's own mapping so
 *  a file looks the same in the picker as it will in the sent bubble. */
function mediaTypeIcon(type: string, fontSize = 16) {
  const sx = { fontSize, color: '#4f46e5', flexShrink: 0 };
  if (type === 'image') return <ImageIcon sx={sx} />;
  if (type === 'video') return <VideoCameraBack sx={sx} />;
  if (type === 'audio') return <MicNone sx={sx} />;
  return <Description sx={sx} />;
}

type WindowStatus = 'open' | 'expired' | 'never';

function getWindowStatus(windowExpiresAt?: string): { status: WindowStatus; remainingMs: number } {
  if (!windowExpiresAt) return { status: 'never', remainingMs: 0 };
  const remaining = new Date(windowExpiresAt).getTime() - Date.now();
  if (remaining <= 0) return { status: 'expired', remainingMs: 0 };
  return { status: 'open', remainingMs: remaining };
}

function formatRemaining(ms: number): string {
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h > 0) return `${h}h ${m}m remaining`;
  return `${m}m remaining`;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Resolve a template body/header's `{{n}}` / `{{name}}` placeholders to display text,
 * preferring (in order):
 *  1. `actualParams` — the exact values substituted in at send time (stored on the
 *     message itself for sends after templateParams was introduced).
 *  2. For legacy messages with no stored params: if the template has exactly one
 *     variable, it's overwhelmingly a personalized greeting — the contact's current
 *     name is a far better guess than Meta's generic review example.
 *  3. Meta's template example value.
 *  4. A bracketed placeholder label, as a last resort.
 */
/** The masked stand-in for a hidden variable value. Fixed length so it never
 *  leaks how many digits the real code was either. */
const MASKED_VALUE = '••••••';

function formatBodyText(
  text: string,
  opts: {
    bodyExample: { body_text?: string[][]; body_text_named_params?: Array<{ param_name: string; example?: string }> } | undefined;
    actualParams: string[] | undefined;
    fallbackName: string | undefined;
    /** Render every `{{n}}`/`{{name}}` slot as a mask instead of its real value.
     *  Used for AUTHENTICATION templates, whose only body variable is the user's
     *  live verification code — an authentication credential that must not be
     *  readable (or copyable) by dashboard operators. Applied at render time, so
     *  it covers historical messages too; the real code is never put in the DOM. */
    maskVariables?: boolean | undefined;
  } = { bodyExample: undefined, actualParams: undefined, fallbackName: undefined }
): string {
  const { bodyExample, actualParams, fallbackName, maskVariables } = opts;
  const positionalExamples = bodyExample?.body_text?.[0] ?? [];
  const namedExamples: Record<string, string> = {};
  bodyExample?.body_text_named_params?.forEach((p) => {
    if (p.example) namedExamples[p.param_name] = p.example;
  });

  const variableTokens = new Set<string>();
  for (const m of text.matchAll(/\{\{([^}]+)\}\}/g)) variableTokens.add(m[1] as string);
  const isSingleVariableTemplate = variableTokens.size === 1;

  return text.replace(/\{\{([^}]+)\}\}/g, (_, p: string): string => {
    // First line of the callback, before any value lookup, so a masked slot can
    // never fall through to actualParams / example / fallbackName.
    if (maskVariables) return MASKED_VALUE;

    const idx = parseInt(p, 10);
    const isPositional = !isNaN(idx);

    if (isPositional && actualParams?.[idx - 1] !== undefined) return actualParams[idx - 1] as string;
    if (isSingleVariableTemplate && fallbackName) return fallbackName;
    if (isPositional && positionalExamples[idx - 1] !== undefined) return positionalExamples[idx - 1] as string;
    if (namedExamples[p] !== undefined) return namedExamples[p];
    return `[${p}]`;
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

/** WhatsApp-style delivery indicator for an outgoing (assistant) message: single grey
 *  check = sent, double grey check = delivered, double blue check = read, red icon with
 *  the failure reason on hover = the WhatsApp send was rejected (e.g. expired token,
 *  ecosystem-engagement throttling). Renders nothing for 'pending' or missing status so
 *  legacy messages without a tracked status don't show a misleading icon. */
const MessageStatusIndicator: React.FC<{
  status: Message['status'];
  sendError?: string | undefined;
}> = ({ status, sendError }) => {
  if (!status || status === 'pending') return null;

  if (status === 'failed') {
    return (
      <Tooltip title={sendError || 'Not delivered'}>
        <ErrorOutline sx={{ fontSize: 13, color: '#dc2626' }} />
      </Tooltip>
    );
  }

  if (status === 'sent') {
    return <Check sx={{ fontSize: 14, color: '#94a3b8' }} />;
  }

  return <DoneAll sx={{ fontSize: 14, color: status === 'read' ? '#4f46e5' : '#94a3b8' }} />;
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Sending…',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Not delivered',
};

/** WhatsApp's own pattern: the bubble shows body + time + ticks only; who/how
 *  it was sent lives behind an on-demand "Message info" popover instead of an
 *  inline chip competing with the conversation for attention. Outgoing
 *  messages only — there's nothing to show for an incoming customer message. */
function MessageInfoTrigger({
  time,
  status,
  sendError,
  sender,
  fullTimestamp,
  confidence,
  matchedPhrase,
  templateName,
  templateLanguage,
  toolsUsed,
  model,
}: {
  time: string;
  status: Message['status'];
  sendError?: string | undefined;
  /** Attribution in parts — see resolveSenderInfo's doc comment for why the
   *  panel doesn't just print the joined caption. */
  sender: SenderInfo;
  fullTimestamp: string;
  confidence?: number;
  matchedPhrase?: string;
  /** Only for template-backed messages — surfaced here instead of a
   *  permanent footer bar on the bubble itself. */
  templateName?: string;
  templateLanguage?: string;
  /** Tool names the AI executed on this turn (metadata.toolsUsed). Shown as a
   *  named row here — the bubble's own MessageActions strip is glanceable, this
   *  is the audit read. */
  toolsUsed?: string[];
  /** The LLM that actually generated this reply (metadata.model — e.g.
   *  "openai/gpt-4o-mini"). "Sent by: AI" alone doesn't say WHICH model, and
   *  that matters: two agents can point at different models with different
   *  cost/behaviour, and a silently-substituted model (see
   *  MODEL_SUBSTITUTED_DISABLED in the backend) needs to be visible here, not
   *  just in a super-admin alert. Only meaningful when `sender.principal`
   *  is 'AI' — campaign/manual/external_api messages stamp metadata.model
   *  with a non-model sentinel ("campaign") or nothing at all, and the caller
   *  is expected not to pass this prop for those rows in the first place. */
  model?: string;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'pointer', '&:hover': { opacity: 0.65 } }}
      >
        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>
          {time}
        </Typography>
        <MessageStatusIndicator status={status} sendError={sendError} />
      </Box>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ p: 1.5, minWidth: 200 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={0.75}>
            Message info
          </Typography>
          <Stack spacing={0.5}>
            {[
              // Stated outright, not inferred from a joined caption: an AI that
              // can write to customer records has to be named as the sender.
              { label: 'Sent by', value: sender.principal },
              ...(sender.agentName ? [{ label: 'Agent', value: sender.agentName }] : []),
              ...(sender.methodLabel ? [{ label: 'Generated by', value: sender.methodLabel }] : []),
              // "Sent by: AI" alone never said WHICH model — two agents can run
              // different models with different cost/behaviour, and a model that
              // got silently substituted (see the backend's
              // MODEL_SUBSTITUTED_DISABLED alert) needs to be visible here, not
              // buried in a super-admin-only alert. Gated on sender.principal
              // (not just model's presence) because metadata.model is stamped
              // with the literal string "campaign" on campaign-sent messages —
              // a sender label, not a model id — and would be actively
              // misleading shown as "Model".
              ...(sender.principal === 'AI' && model ? [{ label: 'Model', value: model }] : []),
              { label: 'Time', value: fullTimestamp },
              { label: 'Status', value: STATUS_LABEL[status || ''] || 'Unknown' },
              ...(toolsUsed?.length
                ? [
                    {
                      label: 'Actions',
                      value: toolsUsed.map((t) => TOOL_ACTION_LABELS[t] || humaniseToolName(t)).join(', '),
                    },
                  ]
                : []),
              ...(templateName ? [{ label: 'Template', value: formatTemplateName(templateName) }] : []),
              ...(templateLanguage ? [{ label: 'Language', value: formatLangCode(templateLanguage) }] : []),
              ...(matchedPhrase ? [{ label: 'Matched', value: `"${matchedPhrase}"` }] : []),
              ...(confidence && confidence > 0 ? [{ label: 'Confidence', value: confidence.toFixed(2) }] : []),
              ...(status === 'failed' && sendError ? [{ label: 'Error', value: sendError }] : []),
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {label}
                </Typography>
                <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}

/** One card of a CAROUSEL template, rendered as its own small strip entry
 *  below the intro body — mirrors TemplateCard's own header+body+buttons
 *  layout but scoped to a single card (a card never has a footer). */
const CarouselCardMini: React.FC<{
  card: WhatsAppTemplateCarouselCard;
  mediaUrl: string | undefined;
}> = ({ card, mediaUrl }) => {
  const header = card.header;
  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 1.5,
        overflow: 'hidden',
        minWidth: 150,
        maxWidth: 150,
        flexShrink: 0,
        bgcolor: '#fff',
      }}
    >
      {header?.type === 'VIDEO' && mediaUrl ? (
        <Box sx={{ bgcolor: '#000', lineHeight: 0 }}>
          <video src={mediaUrl} controls preload="metadata" style={{ width: '100%', height: 84, display: 'block', objectFit: 'cover' }} />
        </Box>
      ) : header?.type === 'IMAGE' && mediaUrl ? (
        <Box sx={{ lineHeight: 0 }}>
          <img src={mediaUrl} alt="Card header" style={{ width: '100%', height: 84, display: 'block', objectFit: 'cover' }} />
        </Box>
      ) : (
        <Box
          sx={{
            height: 84,
            bgcolor: '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {header?.type === 'VIDEO' ? (
            <VideoCameraBack sx={{ fontSize: 22, color: '#94a3b8' }} />
          ) : (
            <ImageIcon sx={{ fontSize: 22, color: '#94a3b8' }} />
          )}
        </Box>
      )}
      <Box sx={{ px: 1, pt: 0.75, pb: card.buttons?.length ? 0.25 : 0.75 }}>
        <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4, fontSize: '0.7rem' }}>
          {renderWhatsAppText(formatBodyText(card.body?.text || '', { bodyExample: card.body?.example, actualParams: undefined, fallbackName: undefined }))}
        </Typography>
      </Box>
      {card.buttons && card.buttons.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {card.buttons.map((btn, i) => (
            <Box
              key={i}
              sx={{
                px: 1,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.4,
                borderTop: '1px solid #f1f5f9',
              }}
            >
              {btn.type === 'URL' && <Link sx={{ fontSize: 11, color: '#4f46e5' }} />}
              {btn.type === 'PHONE_NUMBER' && <Phone sx={{ fontSize: 11, color: '#4f46e5' }} />}
              <Typography variant="caption" sx={{ color: '#4f46e5', fontWeight: 600, fontSize: '0.65rem' }} noWrap>
                {btn.text}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const TemplateCard: React.FC<{
  parsed: ParsedTemplate;
  fullTemplate: WhatsAppTemplate | undefined;
  mediaUrl: string | undefined;
  /** The actual values substituted in at send time, if captured (preferred over the
   *  template's Meta-review example values). */
  actualParams: { header?: string; body?: string[]; buttonUrl?: string } | undefined;
  /** The contact's current name — used as a last-resort reconstruction for legacy
   *  single-variable templates sent before actualParams was captured. */
  fallbackName: string | undefined;
  /** CAROUSEL templates only — one signed media URL per card, index-aligned
   *  with `fullTemplate.components.carousel.cards`. */
  carouselMediaUrls?: Array<string | undefined> | undefined;
}> = ({ parsed, fullTemplate, mediaUrl, actualParams, fallbackName, carouselMediaUrls }) => {
  const header = fullTemplate?.components.header;
  const body = fullTemplate?.components.body;
  const bodyText = body?.text;
  const footer = fullTemplate?.components.footer;
  const buttons = fullTemplate?.components.buttons;
  // An AUTHENTICATION template's variable IS the user's live verification code.
  // Mask it in the operator console so staff can't read or copy a customer's
  // one-time credential out of the transcript (account-takeover / data-minimisation
  // risk). The "Copy code" button below is already an inert label here — it carries
  // no code and does nothing on click — so masking the body is the whole fix.
  const isAuthTemplate = fullTemplate?.category === 'AUTHENTICATION';
  const cards = fullTemplate?.components.carousel?.cards;
  const isCarousel = !!cards && cards.length > 0;
  const hasMedia =
    !isCarousel &&
    (header?.type === 'IMAGE' || header?.type === 'VIDEO' || header?.type === 'DOCUMENT');
  /** The header media, opened in-app. Owned by the card because the card is
   *  the only thing that knows which signed URL it is showing. */
  const [headerPreviewOpen, setHeaderPreviewOpen] = useState(false);
  const headerFileName = header?.headerMediaFileName || 'Template header';

  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        overflow: 'hidden',
        minWidth: 240,
        maxWidth: 320,
        bgcolor: '#fff',
      }}
    >
      {/* ── Media header ── */}
      {hasMedia && (
        <>
          {header?.type === 'VIDEO' && mediaUrl ? (
            // Render actual video from S3 signed URL
            <Box sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: '#000', lineHeight: 0 }}>
              <video
                src={mediaUrl}
                controls
                preload="metadata"
                style={{ width: '100%', maxHeight: 180, display: 'block', objectFit: 'cover' }}
              />
            </Box>
          ) : header?.type === 'IMAGE' && mediaUrl ? (
            // Render actual image from S3 signed URL; click opens it full-size
            // in the in-app preview rather than navigating to the signed URL.
            <Box
              component="button"
              type="button"
              onClick={() => setHeaderPreviewOpen(true)}
              aria-label={`Preview ${headerFileName}`}
              sx={{
                display: 'block',
                width: '100%',
                p: 0,
                border: 'none',
                bgcolor: 'transparent',
                borderBottom: '1px solid #e2e8f0',
                lineHeight: 0,
                cursor: 'pointer',
              }}
            >
              <img
                src={mediaUrl}
                alt="Template header"
                style={{ width: '100%', maxHeight: 180, display: 'block', objectFit: 'cover' }}
              />
            </Box>
          ) : header?.type === 'DOCUMENT' && mediaUrl ? (
            // Document: opens in the in-app preview, never as a navigation.
            <Box
              component="button"
              type="button"
              onClick={() => setHeaderPreviewOpen(true)}
              aria-label={`Preview ${headerFileName}`}
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1.25,
                bgcolor: '#f8fafc',
                border: 'none',
                borderBottom: '1px solid #e2e8f0',
                textDecoration: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Article sx={{ fontSize: 24, color: '#4f46e5' }} />
              <Typography variant="caption" sx={{ color: '#4f46e5', fontWeight: 600 }} noWrap>
                {header.headerMediaFileName || 'document'}
              </Typography>
            </Box>
          ) : (
            // Fallback placeholder when signed URL not yet loaded
            <Box
              sx={{
                height: 100,
                bgcolor: '#f1f5f9',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              {header?.type === 'IMAGE' && <ImageIcon sx={{ fontSize: 28, color: '#94a3b8' }} />}
              {header?.type === 'VIDEO' && (
                <VideoCameraBack sx={{ fontSize: 28, color: '#94a3b8' }} />
              )}
              {header?.type === 'DOCUMENT' && (
                <Article sx={{ fontSize: 28, color: '#94a3b8' }} />
              )}
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                {header?.type?.toLowerCase()}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* ── Text header ── */}
      {header?.type === 'TEXT' && header.text && (
        <Box sx={{ px: 1.5, pt: 1.25 }}>
          <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.35 }}>
            {formatBodyText(header.text, {
              bodyExample: header.example?.header_text ? { body_text: [header.example.header_text] } : undefined,
              actualParams: actualParams?.header ? [actualParams.header] : undefined,
              fallbackName,
              maskVariables: isAuthTemplate,
            })}
          </Typography>
        </Box>
      )}

      {/* ── Body (a carousel's is the intro text — cards below carry their own) ── */}
      <Box sx={{ px: 1.5, pt: header ? 1 : 1.25, pb: footer || buttons?.length || isCarousel ? 0.5 : 1 }}>
        {bodyText ? (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55, fontSize: '0.8375rem' }}
          >
            {renderWhatsAppText(
              formatBodyText(bodyText, {
                bodyExample: body?.example,
                actualParams: actualParams?.body,
                fallbackName,
                maskVariables: isAuthTemplate,
              })
            )}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            {formatTemplateName(parsed.name)}
          </Typography>
        )}
      </Box>

      {/* ── Carousel cards ── */}
      {isCarousel && (
        <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', px: 1.5, pb: 1 }}>
          {cards!.map((card, i) => (
            <CarouselCardMini key={i} card={card} mediaUrl={carouselMediaUrls?.[i]} />
          ))}
        </Box>
      )}

      {/* ── Footer ── */}
      {footer?.text && (
        <Box sx={{ px: 1.5, pb: 0.75 }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6875rem' }}>
            {renderWhatsAppText(footer.text)}
          </Typography>
        </Box>
      )}

      {/* ── Buttons ── */}
      {buttons && buttons.length > 0 && (
        <>
          <Divider />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {buttons.map((btn, i) => {
              // The openable link for a URL button: a STATIC url button uses its
              // own url; a DYNAMIC ({{n}}) one uses the actual per-send link we
              // captured (actualParams.buttonUrl) — so clicking opens the real
              // link that was sent, not the template's unfilled placeholder. A
              // dynamic button on an older message (before capture) stays a plain
              // label. Non-URL buttons (quick-reply / phone) are never links here.
              const isDynamicUrl = btn.type === 'URL' && /\{\{\s*\d+\s*\}\}/.test(btn.url || '');
              const candidateHref =
                btn.type === 'URL'
                  ? isDynamicUrl
                    ? actualParams?.buttonUrl
                    : btn.url
                  : undefined;
              // Only ever turn an http(s) URL into a link — guards against a
              // `javascript:`/`data:` scheme (e.g. a fully-dynamic {{1}} button
              // filled with a malicious value) becoming XSS-on-click.
              const href =
                candidateHref && /^https?:\/\//i.test(candidateHref) ? candidateHref : undefined;
              const rowSx = {
                px: 1.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                borderTop: i > 0 ? '1px solid #f1f5f9' : 'none',
                color: '#4f46e5',
                textDecoration: 'none',
                ...(href ? { cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } } : {}),
              } as const;
              const inner = (
                <>
                  {btn.type === 'URL' && <Link sx={{ fontSize: 13, color: '#4f46e5' }} />}
                  {btn.type === 'PHONE_NUMBER' && <Phone sx={{ fontSize: 13, color: '#4f46e5' }} />}
                  <Typography
                    variant="caption"
                    sx={{ color: '#4f46e5', fontWeight: 600, fontSize: '0.75rem' }}
                  >
                    {btn.text}
                  </Typography>
                </>
              );
              return href ? (
                // title={href} lets you hover to SEE the full link too.
                <Box
                  key={i}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={href}
                  sx={rowSx}
                >
                  {inner}
                </Box>
              ) : (
                <Box key={i} sx={rowSx}>
                  {inner}
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {/* Template name + language used to show here as a footer meta bar on
          every template bubble — moved into the "Message info" popover
          instead (see MessageInfoTrigger) so the transcript matches what the
          customer actually received, with the metadata a click away rather
          than permanently on-screen for every message. */}

      {hasMedia && mediaUrl && (
        <MediaPreviewDialog
          open={headerPreviewOpen}
          onClose={() => setHeaderPreviewOpen(false)}
          url={mediaUrl}
          fileName={headerFileName}
          {...(header?.type === 'IMAGE'
            ? { kind: 'image' as const }
            : header?.type === 'VIDEO'
              ? { kind: 'video' as const }
              : {})}
        />
      )}
    </Box>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

/** Conversation-scoped filter subset — the "saved views" pattern (Intercom-style):
 *  inbox filters reuse the same underlying fields as the Contacts page's segment
 *  filters (status/tags/assignedTo), but only the fields that make sense on a
 *  live, per-conversation view — not contact-history concepts like date-added or
 *  quality risk, which belong to audience-building, not day-to-day triage. */
// Only 'active'/'archived' are options: PATCH /admin/contacts/:id/status (the
// only place anything ever changes a contact's status) rejects any other
// value, so 'resolved'/'spam' — while still valid values in the Conversation
// schema's enum for future use — can never actually match a document today.
// Listing them here would just be a filter option that silently always
// returns zero results.
const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];
const ASSIGNED_TO_ALL = 'all';
const ASSIGNED_TO_UNASSIGNED = 'unassigned';

// WhatsApp-only (see the `routing` field's doc comment) — selecting anything
// other than 'all' skips the Instagram leg of fetchConversations entirely
// rather than silently ignoring the filter for those rows.
type RoutingFilter = 'all' | 'needs_human' | 'human' | 'bot';
const ROUTING_FILTER_OPTIONS: Array<{ value: RoutingFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'needs_human', label: 'Needs Human' },
  { value: 'human', label: 'Human' },
  { value: 'bot', label: 'Bot' },
];

// 'incoming' is WhatsApp-only (mirrors RoutingFilter's Instagram carve-out above) —
// Instagram rows have no activityBucket/lastActivityAt concept, so that leg of
// fetchConversations is skipped entirely while this is active, same as routingFilter.
type SortFilter = 'recent' | 'incoming';
const SORT_FILTER_OPTIONS: Array<{ value: SortFilter; label: string }> = [
  { value: 'recent', label: 'Most recent activity' },
  { value: 'incoming', label: 'Incoming messages' },
];

/**
 * "Date added" — when a chat FIRST appeared in the inbox (the contact/thread's
 * own `createdAt`), which is a different question from when it was last active
 * (`lastMessageAt`, what the list is already sorted by). Answers "what came in
 * today / this week", the triage question the sort order can't.
 *
 * Both channels support it: WhatsApp through the shared contact-selection
 * builder's Tier 2 `dateAddedFrom`/`dateAddedTo` pair, Instagram through the
 * matching pair added to its own list endpoint — so unlike routing/flagged/sort
 * this filter needs no Instagram carve-out and never silently empties half of
 * an "All channels" inbox.
 */
export type DateAddedPreset = 'all' | 'today' | 'yesterday' | 'last7' | 'last30' | 'custom';
const DATE_ADDED_OPTIONS: Array<{ value: DateAddedPreset; label: string }> = [
  { value: 'all', label: 'Any time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  // Rolling windows, not calendar weeks/months. "This week" would have to pick
  // a week-start, which differs by locale and would quietly mean different
  // things to different admins on the same team; a rolling 7 days reads the
  // same everywhere and is what triage actually wants.
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom range…' },
];

const startOfDay = (d: Date): Date => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};
const endOfDay = (d: Date): Date => {
  const copy = new Date(d);
  copy.setHours(23, 59, 59, 999);
  return copy;
};
const daysAgo = (n: number): Date => {
  const copy = new Date();
  copy.setDate(copy.getDate() - n);
  return copy;
};

/**
 * Resolves the picked preset (or custom range) into the exact query params the
 * two list endpoints take. Returns `{}` for 'all' so the params are simply
 * absent rather than sent empty.
 *
 * ABSOLUTE INSTANTS, NOT BARE DATES. Both backends parse these with
 * `new Date(string)`, which reads a bare 'YYYY-MM-DD' as UTC midnight — so an
 * admin in IST asking for "Today" would have silently got a window starting
 * 5:30am the previous day, pulling in yesterday evening's chats and cutting
 * today's last 5.5 hours. Day boundaries are therefore computed in the admin's
 * OWN timezone and sent as full ISO instants.
 *
 * Every preset except 'yesterday' is deliberately open-ended at the top: there
 * is no upper bound to express when the window runs up to now, and pinning one
 * would go stale the moment the clock passes it.
 */
export function resolveDateAddedRange(
  preset: DateAddedPreset,
  customFrom: string,
  customTo: string
): { dateAddedFrom?: string; dateAddedTo?: string } {
  switch (preset) {
    case 'today':
      return { dateAddedFrom: startOfDay(new Date()).toISOString() };
    case 'yesterday':
      return {
        dateAddedFrom: startOfDay(daysAgo(1)).toISOString(),
        dateAddedTo: endOfDay(daysAgo(1)).toISOString(),
      };
    // Inclusive of today, so "Last 7 days" spans today plus the six before it
    // rather than stopping at yesterday.
    case 'last7':
      return { dateAddedFrom: startOfDay(daysAgo(6)).toISOString() };
    case 'last30':
      return { dateAddedFrom: startOfDay(daysAgo(29)).toISOString() };
    case 'custom':
      // Each bound stands alone: "added since 1 May" and "added up to 1 May"
      // are both legitimate, so a half-filled range still filters instead of
      // being ignored until the other box is touched. The 'T00:00:00' suffix
      // is what makes the browser read the date input's value as a LOCAL day
      // rather than a UTC one — the same trap as above, on the way in.
      return {
        ...(customFrom
          ? { dateAddedFrom: startOfDay(new Date(`${customFrom}T00:00:00`)).toISOString() }
          : {}),
        ...(customTo
          ? { dateAddedTo: endOfDay(new Date(`${customTo}T00:00:00`)).toISOString() }
          : {}),
      };
    case 'all':
    default:
      return {};
  }
}

/** Conversations fetched per list page — the inbox loads one of these at a time
 *  and appends the next as the admin scrolls to the bottom (WhatsApp-style
 *  infinite scroll), rather than being hard-capped at a single page. */
const CONVERSATION_PAGE_SIZE = 50;
/** Instagram's inbox endpoint hard-caps `limit` at 100 server-side
 *  (instagram-inbox.controller.ts) — the WhatsApp one does not. Only the
 *  depth-preserving refresh below can exceed it, so it clamps against this and
 *  lets the next scroll re-fetch whatever the clamp left off. */
const INSTAGRAM_LIST_LIMIT_CAP = 100;
/** Ceiling on the depth a reconciling refresh re-requests in one shot. An admin
 *  who has scrolled 40 pages deep should not turn every bot-stop click into a
 *  2000-row query; past this the list simply collapses back to the cap and
 *  scrolls again. */
const CONVERSATION_MAX_REFRESH_PAGES = 6;
/** Distance from the bottom of the list (px) at which the next page starts
 *  loading — early enough that it is usually ready before the admin reaches
 *  the edge. Mirrors handleMessagesScroll's 120px top threshold. */
const CONVERSATION_SCROLL_THRESHOLD = 240;

const ChatLogs: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  /** Deep-link resolution guards — see the initial-selection effect below. */
  const deepLinkFetchStarted = useRef(false);
  const selectionMadeRef = useRef(false);
  /** A deep-linked conversation that is not in the most-recent-50 page. Held so
   *  the list fetch, which replaces `conversations` wholesale, can keep it. */
  const deepLinkPinnedRef = useRef<Conversation | null>(null);
  // Set right before a setMessages() call that needs a specific scroll
  // response, consumed (and reset) by the layout effect below. Anything that
  // doesn't set this (e.g. a delivery-status-only patch) leaves scroll alone.
  const pendingScrollActionRef = useRef<'jump' | 'smooth' | 'preserve' | null>(null);
  // The open conversation's id as of the latest render — lets an async re-sync
  // (resyncConversationTail) drop its result if the user switched chats meanwhile.
  const selectedConversationIdRef = useRef<string | undefined>(undefined);
  // Captured right before prepending an older page, so the layout effect can
  // restore the same visual scroll position after the DOM grows above it.
  const scrollAnchorRef = useRef<{ prevScrollHeight: number; prevScrollTop: number } | null>(null);
  const { guard } = usePlanGate();
  const { hasPermission } = usePermissionAccess();
  // Plan-side gate (plan.moduleGrants ⊕ tenant.moduleOverrides), which is a
  // SEPARATE axis from hasPermission's RBAC set — see the Instagram leg of
  // fetchConversations for why both are needed there.
  const { hasModuleAccess } = useModuleAccess();

  /**
   * Is the Instagram channel actually reachable for this admin? Mirrors the
   * two independent axes the backend's requirePermission('instagram') gate
   * enforces (admin.routes.ts) and denies on EITHER of:
   *   - hasModuleAccess: the tenant's PLAN includes the module
   *     (plan.moduleGrants ⊕ tenant.moduleOverrides)
   *   - hasPermission:   this role's RBAC set grants it
   * Checking only the RBAC axis was the reported bug — a tenant_owner has
   * every permission by definition, so on a plan without Instagram the page
   * fired a guaranteed-403 fetch on every load (see fetchConversations).
   *
   * Single source of truth for every Instagram touchpoint on this page: the
   * fetch leg AND the Channel filter option, which must not offer a channel
   * that can only ever come back empty.
   */
  const instagramAvailable = hasModuleAccess('instagram') && hasPermission('instagram');

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  selectedConversationIdRef.current = selectedConversation?._id;

  /**
   * A "Lead captured" / "Appointment requested" chip under an AI reply jumps to that record.
   * The message only carries the tool NAME, so the record is resolved by the conversation's
   * phone (same lookup-not-guess approach as Leads.tsx::openContactForLead). Before leaving,
   * the current URL is rewritten to `?conversationId=` so the browser Back button returns to
   * this exact conversation — Chat Logs already restores its selection from that param.
   */
  const openActionRecord = async (tool: string) => {
    const conv = selectedConversation;
    const phone = conv?.customerPhone;
    if (!conv || !phone) return;
    const prefix = routerLocation.pathname.startsWith('/admin') ? '/admin' : '';
    window.history.replaceState(
      window.history.state,
      '',
      `${routerLocation.pathname}?conversationId=${encodeURIComponent(conv._id)}`
    );
    if (tool === 'create_lead') {
      let leadId = '';
      try {
        const res: any = await adminApi.getLeads({ search: phone, status: 'all', limit: 1 });
        leadId = (res?.data || [])[0]?._id || '';
      } catch {
        /* fall through to the search-only landing below */
      }
      navigate(
        leadId
          ? `${prefix}/activity/leads?lead=${encodeURIComponent(leadId)}`
          : `${prefix}/activity/leads?status=all&search=${encodeURIComponent(phone)}`
      );
      return;
    }
    navigate(`${prefix}/appointments?phone=${encodeURIComponent(phone)}`);
  };
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  // Template lookup: name → WhatsAppTemplate (fetched once on mount)
  const [templateMap, setTemplateMap] = useState<Record<string, WhatsAppTemplate>>({});
  // Signed S3 preview URLs: templateName → URL (fetched in parallel with template list)
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  // Same, but for CAROUSEL templates: templateName → per-card URL (index-aligned
  // with components.carousel.cards; undefined entries render the media placeholder).
  const [carouselMediaUrls, setCarouselMediaUrls] = useState<Record<string, Array<string | undefined>>>({});
  const [search, setSearch] = useState('');
  // Right-hand contact panel: collapsible (see ContactPanel.tsx's `open` prop),
  // persisted across sessions like every other cited competitor does — an agent
  // who prefers it open shouldn't have to reopen it on every reload. Defaults
  // CLOSED (a slim icon-rail — see ContactPanel.tsx) so the thread gets the
  // width by default; opening it is one click on the rail's own control.
  const [showContactPanel, setShowContactPanel] = useState(
    () => localStorage.getItem('chatLogs.showContactPanel') === 'true'
  );
  const toggleContactPanel = () => {
    setShowContactPanel((prev) => {
      const next = !prev;
      localStorage.setItem('chatLogs.showContactPanel', String(next));
      return next;
    });
  };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [windowActiveFilter, setWindowActiveFilter] = useState(false);
  /** Date-added filter: a preset, plus the two 'YYYY-MM-DD' box values that
   *  only apply while the preset is 'custom'. The raw box values are kept even
   *  when the preset moves off 'custom' so flipping back doesn't wipe a range
   *  the admin just typed. */
  const [dateAddedFilter, setDateAddedFilter] = useState<DateAddedPreset>('all');
  const [dateAddedFrom, setDateAddedFrom] = useState('');
  const [dateAddedTo, setDateAddedTo] = useState('');
  const [assignedToFilter, setAssignedToFilter] = useState(ASSIGNED_TO_ALL);
  const [channelFilter, setChannelFilter] = useState<'all' | 'whatsapp' | 'instagram'>('all');
  const [routingFilter, setRoutingFilter] = useState<RoutingFilter>('all');
  /** "Flagged" quick view — WhatsApp-only, same carve-out as routingFilter
   *  (the Instagram store has no aiControl.flagged to filter on). */
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [sortFilter, setSortFilter] = useState<SortFilter>('recent');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [agents, setAgents] = useState<{ _id: string; name: string }[]>([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [loadingList, setLoadingList] = useState(true);
  /** Infinite scroll bookkeeping for the conversation list.
   *
   *  `loadedPagesRef` is shared by BOTH channels rather than tracked per
   *  channel: a channel is only ever skipped once it has run out entirely
   *  (hasMore false), and an exhausted channel never needs a higher page
   *  number, so one counter stays correct for both legs. It is a ref, not
   *  state, because nothing renders it — and because the scroll handler and
   *  the reconciling refetches read it from closures React does not re-create
   *  per keystroke, where a stale value would either re-request the page just
   *  loaded or silently reset the list's depth. The two hasMore flags need
   *  both forms: state to render the footer, refs for those same closures. */
  const [hasMoreWhatsapp, setHasMoreWhatsapp] = useState(false);
  const [hasMoreInstagram, setHasMoreInstagram] = useState(false);
  const [loadingMoreList, setLoadingMoreList] = useState(false);
  const loadedPagesRef = useRef(1);
  const hasMoreWhatsappRef = useRef(false);
  const hasMoreInstagramRef = useRef(false);
  /** Guards against the scroll handler firing a second page load while the
   *  first is still in flight — `loadingMoreList` alone can't, since a scroll
   *  event can arrive before React has re-rendered with the new state. */
  const loadingMoreRef = useRef(false);
  /** Bumped by every fresh (non-appending) list query. A page load that was
   *  already in flight when a filter change lands compares its captured token
   *  against this and drops its rows rather than pasting the previous query's
   *  page onto the new result set — the 300ms search debounce makes that
   *  narrow, not impossible. */
  const listQueryTokenRef = useRef(0);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const hasMoreConversations = hasMoreWhatsapp || hasMoreInstagram;
  // Recomputed per render rather than memoised: every preset resolves to a
  // day boundary, so the strings are stable for the whole day and the effect
  // below can depend on them directly (it depends on the two STRINGS, never on
  // this object, which is a fresh reference each render).
  const dateAddedRange = resolveDateAddedRange(dateAddedFilter, dateAddedFrom, dateAddedTo);
  /** 'custom' with both boxes still empty is a non-default control that filters
   *  nothing — it counts as "touched" for the badge and Clear all, but not as
   *  an applied range. */
  const dateAddedApplied = Boolean(dateAddedRange.dateAddedFrom || dateAddedRange.dateAddedTo);
  const customRangeInverted = Boolean(
    dateAddedFilter === 'custom' && dateAddedFrom && dateAddedTo && dateAddedFrom > dateAddedTo
  );
  // Hybrid routing: claim / assign / hand-back to bot.
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  // Instagram's assignable pool (Full on the 'instagram' module), fetched when
  // its assign dialog opens. `null` = not loaded yet → fall back to the full
  // directory rather than show an empty picker.
  const [instagramAssignableIds, setInstagramAssignableIds] = useState<string[] | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [initialSelectionDone, setInitialSelectionDone] = useState(false);
  const [showChat, setShowChat] = useState(false);
  /** Transient success toast for ⋮ actions (archive/restore). */
  const [actionNotice, setActionNotice] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  // Typing indicator (Phase 2.4, Instagram only — WhatsApp's Cloud API has no
  // such concept). Refs, not state: firing on every keystroke must never
  // trigger a re-render, and the debounce/auto-off timers must survive across
  // renders without resetting. `typingIgsidRef` tracks which igsid the last
  // typing_on was sent for, so switching conversations (or the same
  // conversation losing its igsid) reliably sends typing_off to the RIGHT
  // recipient rather than the newly-selected one.
  const typingIgsidRef = useRef<string | null>(null);
  const typingOffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Stops any in-flight indicator for whichever igsid we last signalled —
   *  called on send, on blur, on unmount, and on conversation switch. A
   *  fire-and-forget best-effort call (see sendInstagramTyping's own doc
   *  comment); never awaited by a caller that cares about its result. */
  const stopTypingIndicator = () => {
    if (typingOffTimerRef.current) {
      clearTimeout(typingOffTimerRef.current);
      typingOffTimerRef.current = null;
    }
    const igsid = typingIgsidRef.current;
    if (igsid) {
      typingIgsidRef.current = null;
      void adminApi.sendInstagramTyping(igsid, 'typing_off').catch(() => undefined);
    }
  };

  /** Called on every composer keystroke. Sends typing_on at most once per
   *  "burst" of typing (not per keystroke — Meta's own guidance warns against
   *  an unnatural on/off cadence), and auto-sends typing_off after a pause
   *  with no further keystrokes, so an agent who stops mid-sentence or
   *  switches tabs doesn't leave "typing…" showing forever. */
  const TYPING_AUTO_OFF_MS = 5000;
  const signalTyping = (igsid: string | undefined) => {
    if (!igsid) return;
    const isNewBurst = typingIgsidRef.current !== igsid;
    typingIgsidRef.current = igsid;
    if (isNewBurst) {
      void adminApi.sendInstagramTyping(igsid, 'typing_on').catch(() => undefined);
    }
    if (typingOffTimerRef.current) clearTimeout(typingOffTimerRef.current);
    typingOffTimerRef.current = setTimeout(() => {
      typingOffTimerRef.current = null;
      typingIgsidRef.current = null;
      void adminApi.sendInstagramTyping(igsid, 'typing_off').catch(() => undefined);
    }, TYPING_AUTO_OFF_MS);
  };
  // Manual template send — the only way back into a conversation once the 24h
  // window has closed. Lives inline in the composer (WhatsApp-app-style) rather
  // than a separate dialog: picking a template from the popover below swaps the
  // text field for a small preview + variable inputs, still sent via the same
  // Send arrow. `activeTemplateName` non-empty IS "composer is in template mode".
  const [templateMenuAnchorEl, setTemplateMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [activeTemplateName, setActiveTemplateName] = useState('');
  const [activeTemplateValues, setActiveTemplateValues] = useState<string[]>([]);
  const [activeButtonUrlValue, setActiveButtonUrlValue] = useState('');
  // Whether the live preview's scroll box actually has more content below the
  // fold — drives the bottom fade hint so a capped-height preview reads as
  // "scroll for more" instead of "this is cut off and broken".
  const previewScrollRef = useRef<HTMLDivElement>(null);
  const [previewOverflowing, setPreviewOverflowing] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [templateSendError, setTemplateSendError] = useState('');
  // Set when a send fails with TEMPLATE_MEDIA_UNFIT — drives a transient toast
  // (not a sticky banner) carrying the deep link to the template EDITOR. The
  // template is also deselected, so the composer resets; the toast is the only
  // lingering cue, with a one-click new-tab CTA to edit it.
  const [templateUnfitToast, setTemplateUnfitToast] = useState<{ editUrl: string } | null>(null);
  // ── Header media chosen for the active template ──────────────────────────
  // A template with an IMAGE/VIDEO/DOCUMENT header is the ONLY way to deliver a
  // file once the 24h window has closed (free-form media is undeliverable
  // there, which is why the paperclip disables outside it). Until now the
  // header always carried whatever file the template was created with; this
  // swaps in a library file for one send. `null` means "send the template's own
  // default", which is exactly the behaviour that shipped before.
  const [templateHeaderAsset, setTemplateHeaderAsset] = useState<MediaAsset | null>(null);
  // The library picker serves two masters now: staging a free-form attachment
  // (the paperclip) and choosing a template's header file. Same browser, same
  // cache, same search — only what a click DOES differs, so the mode lives here
  // rather than in a second copy of the picker.
  const [mediaPickMode, setMediaPickMode] = useState<'attach' | 'templateHeader'>('attach');
  // ── Manual media send — the composer's third mode ────────────────────────
  // Deliberately library-only: the picker lists files an admin already curated
  // (and the bot already sends via the send_media flow node) rather than
  // offering an ad-hoc upload, so nothing an agent attaches can be a file the
  // tenant never approved. `activeMedia` non-null IS "composer is in media
  // mode", mirroring how activeTemplateName drives template mode.
  const [mediaMenuAnchorEl, setMediaMenuAnchorEl] = useState<HTMLElement | null>(null);
  // The library is a folder TREE, so the picker browses it one level at a time
  // rather than flattening hundreds of files into one list. One cached
  // MediaBrowseResult per folder ('root' for the top) — walking back up costs
  // nothing, and re-opening the picker resumes where the agent left off, which
  // is what makes sending three files from the same folder bearable.
  const [mediaFolderId, setMediaFolderId] = useState<string | null>(null);
  const [mediaBrowse, setMediaBrowse] = useState<Record<string, MediaBrowseResult>>({});
  const [loadingMediaBrowse, setLoadingMediaBrowse] = useState(false);
  // Search is a deliberately SEPARATE flat view across every folder — the thing
  // that keeps a nested picker from being slower than the flat one it replaced.
  // Its list is fetched once, and only if the agent actually types.
  const [mediaSearch, setMediaSearch] = useState('');
  const [mediaAllAssets, setMediaAllAssets] = useState<MediaAsset[] | null>(null);
  const [loadingMediaSearch, setLoadingMediaSearch] = useState(false);
  const [activeMedia, setActiveMedia] = useState<MediaAsset | null>(null);
  // Asset id whose thumbnail failed to load, so the tile falls back to its
  // type icon instead of a broken image. Keyed by id rather than a boolean so
  // picking a different file re-tries on its own — no reset effect needed.
  const [mediaPreviewFailedFor, setMediaPreviewFailedFor] = useState<string | null>(null);
  const [mediaCaption, setMediaCaption] = useState('');
  const [sendingMedia, setSendingMedia] = useState(false);
  const [mediaSendError, setMediaSendError] = useState('');
  // Separate state for fields that come from the detail API (windowExpiresAt, lastInboundMessageAt).
  // Kept separate so updating them never touches selectedConversation and never re-fires the
  // useEffect that depends on selectedConversation._id — which would cause an infinite loop.
  const [windowDetail, setWindowDetail] = useState<{
    windowExpiresAt?: string;
    lastInboundMessageAt?: string;
  }>({});
  // Same reason as windowDetail above: getConversations (the list) strips routing.history
  // to keep the page payload small (see admin.controller.ts), so it only ever arrives via
  // getConversationDetail. Kept in its own state for the identical reason — writing it
  // onto selectedConversation would create a new object reference and re-fire the
  // conversation-switch effect that depends on selectedConversation._id.
  const [routingHistory, setRoutingHistory] = useState<RoutingHistoryEntry[]>([]);
  // Which conversation is open right now, readable from async callbacks — so a
  // routing refresh that resolves after the operator switched chats can never
  // write one conversation's history onto another.
  const openConversationIdRef = useRef<string | null>(null);
  useEffect(() => {
    openConversationIdRef.current = selectedConversation?._id ?? null;
  }, [selectedConversation?._id]);

  useEffect(() => {
    if (!assignDialogOpen || selectedConversation?.channel !== 'instagram') return;
    let cancelled = false;
    adminApi
      .getInstagramAssignableUserIds()
      .then((ids) => {
        if (!cancelled) setInstagramAssignableIds(ids);
      })
      .catch(() => {
        if (!cancelled) setInstagramAssignableIds(null);
      });
    return () => {
      cancelled = true;
    };
  }, [assignDialogOpen, selectedConversation?.channel]);
  // Same reason again: the list serializer (contact-row.serializer.ts) never projects
  // `metadata.ctwaAttribution`, so a WhatsApp chat's "From Ad" chip can only be fed
  // from the detail response. Own state for the identical re-fire reason.
  const [ctwaAttribution, setCtwaAttribution] =
    useState<NonNullable<Conversation['metadata']>['ctwaAttribution']>(undefined);
  // tick increments every 60s purely to force a re-render, so `canReply` below
  // (derived from windowDetail.windowExpiresAt at render time) notices the
  // window expiring even if nothing else — no new message, no navigation —
  // triggers a render in the meantime. Without this, the chat input would
  // stay visible past expiry until something unrelated happened to redraw.
  const [, setTick] = useState(0);

  // `user.tenantId` is set directly from the JWT on every non-superadmin login and is the
  // more fundamental source; `tenant.id` comes from a separate object that isn't always
  // repopulated (e.g. some refresh paths). Prefer user.tenantId, fall back to tenant.id.
  const { user, tenant } = useAuth();
  const wsTenantId = user?.tenantId || tenant?.id;

  // Real-time: a message was created (inbound from the customer, or an outbound reply —
  // auto or manual). Patch the list row in place (and bump it to the top, like every chat
  // inbox does), append to the open chat if it's showing this conversation, and — for a
  // fresh inbound message — refresh the 24h window countdown immediately instead of waiting
  // for a reselect.
  const handleNewMessage = ({ conversationId, message }: NewMessageEvent) => {
    // Tenant-room stubs (`message` absent — the server omits content tenant-
    // wide to prevent cross-scope leaks) fire for EVERY message, on EVERY
    // conversation, either direction. Refetching the whole list on each one
    // reloaded this panel constantly under any real traffic — that was the
    // regression. A conversation already in the visible list is patched in
    // place instead (below); only a conversation NOT currently visible (new,
    // or outside the active filter/page — nothing here to patch, and a stub
    // carries no content to seed a row with anyway) needs the full refetch.
    const known = conversations.some((c) => c._id === conversationId);
    if (!known) {
      // A message for a conversation not currently in the list (a brand-new
      // thread, or one outside the scrolled pages/filter). Reconcile in the
      // BACKGROUND — `background` keeps the skeleton loaders off, so the whole
      // inbox no longer blanks and re-renders for a beat every time a new
      // contact writes in; the new row simply appears. preserveDepth keeps the
      // admin's scroll position, same as before.
      fetchConversations({ preserveDepth: true, background: true });
      return;
    }

    // Types hidden from the transcript must not become the list-row preview
    // either — otherwise a conversation would show "Unsupported message: ..."
    // as its latest activity while that row is invisible in the chat itself.
    // The row still bumps (activity is real), only the preview text is kept.
    // A stub has no `message` at all, so it can only bump the recency fields,
    // never the preview — that stays whatever it last was until a real
    // conversation-room event or the next fetchConversations() corrects it.
    const showsInList = message ? isTranscriptMessage(message as unknown as Message) : false;
    setConversations((prev) => {
      const patched = prev.map((c) =>
        c._id === conversationId
          ? {
              ...c,
              ...(message && showsInList ? { lastMessage: message.content } : {}),
              lastMessageAt: message?.createdAt ?? new Date().toISOString(),
              messageCount: c.messageCount + 1,
            }
          : c
      );
      // 'recent' bumps to top on every message, either direction — matches
      // the backend's unconditionally-bumped lastInteractionAt. 'incoming'
      // only bumps on an inbound (customer) message: an outbound-only send
      // must never reorder an already-engaged contact, the same invariant
      // the backend's activityBucket enforces server-side (see
      // admin.controller.ts's getConversations). A stub carries no `message`
      // at all, so direction is unknown — treated the same as "not inbound"
      // (no reorder) rather than guessing, same tolerated imprecision as the
      // bucket-promotion gap already documented below.
      // An outbound send to a bucket 1/2 contact CAN promote it server-side,
      // but replicating that here would need each row's bucket, which this
      // list doesn't carry — left to the next fetchConversations() rather
      // than approximated.
      return sortFilter === 'incoming' && message?.role !== 'user'
        ? patched
        : patched.sort(
            (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
          );
    });

    // Transcript append / mark-read below needs actual message content, which
    // a tenant-room stub never carries — restrict this whole block to a real
    // conversation-room event, same as before the list-patching logic above
    // was widened to also handle stubs.
    if (message && selectedConversation?._id === conversationId) {
      // Only queue a scroll response for an actual append — not the no-op
      // branch below (a duplicate/already-seen message), which returns the
      // same array reference and so never triggers the layout effect that
      // would otherwise consume+reset this. Checked against the outer-scope
      // `messages` (this render's snapshot); the updater re-checks against
      // `prev` too, since the two could theoretically diverge between here
      // and the update actually applying.
      if (!messages.some((m) => m._id === message._id)) {
        const container = messagesContainerRef.current;
        const wasNearBottom = container
          ? container.scrollHeight - container.scrollTop - container.clientHeight < 120
          : true;
        pendingScrollActionRef.current = wasNearBottom ? 'smooth' : null;
      }

      setMessages((prev) => {
        if (prev.some((m) => m._id === message._id)) return prev;
        // Same rule the fetched-history paths use — see NON_TRANSCRIPT_TYPES.
        // Shared deliberately: filtering only on fetch would let a live-arriving
        // reaction or unsupported stub appear, then vanish on the next reload.
        if (!isTranscriptMessage(message as unknown as Message)) return prev;
        // Spread, don't re-list. This object was rebuilt field-by-field, which
        // silently dropped every inbound-capture field (messageType,
        // attachments, location, contacts, order, system) — so a socket-
        // delivered image/location/contact rendered as a bare placeholder and
        // only became correct after a reload re-fetched it from the API.
        return [...prev, { ...(message as unknown as Message) }];
      });

      if (message.role === 'user') {
        setWindowDetail({
          lastInboundMessageAt: message.createdAt,
          windowExpiresAt: new Date(
            new Date(message.createdAt).getTime() + 24 * 60 * 60 * 1000
          ).toISOString(),
        });
        // The admin is looking at this thread right now — an inbound landing
        // here is read the moment it renders. Without this, the DB unread
        // counter (incremented server-side on every inbound) would drift up
        // and light the sidebar badge for a message that was actually seen.
        // Not the user's action — opening a chat marks it read in the background. In a read-only
        // (suspended/inactive) workspace that write is refused; it must not pop the dialog on every open.
        const quietly = { suppressPermissionLock: true, suppressTenantBlocked: true };
        void (selectedConversation.channel === 'instagram'
          ? adminApi.markInstagramConversationRead(conversationId, quietly)
          : adminApi.markConversationRead(conversationId, quietly)
        ).catch(() => {
          /* non-critical — next open resets it */
        });
      }
    }
  };

  // Real-time: an existing message's delivery status changed (sent/delivered/read/failed).
  // Only relevant while that conversation is open.
  const handleMessageStatus = ({
    conversationId,
    whatsappMessageId,
    status,
    sendError,
    content,
  }: MessageStatusEvent) => {
    if (selectedConversation?._id !== conversationId) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.whatsappMessageId === whatsappMessageId || m.providerMessageId === whatsappMessageId
          ? {
              ...m,
              status: status as Message['status'],
              ...(sendError ? { metadata: { ...m.metadata, sendError } } : {}),
              // Instagram unsend — see MessageStatusEvent.content's doc comment.
              ...(content !== undefined ? { content } : {}),
            }
          : m
      )
    );
  };

  // Real-time: a conversation's unread count changed (new inbound message, or
  // marked read by opening it — here or in another open tab/admin). Payload
  // is always the absolute count, so just overwrite — no drift possible even
  // if an intermediate event was missed.
  const handleConversationUnread = ({ conversationId, unreadCount }: ConversationUnreadEvent) => {
    setConversations((prev) =>
      prev.map((c) => (c._id === conversationId ? { ...c, unreadCount } : c))
    );
  };

  // Live routing update (claim / assign / hand-back-to-bot / auto-escalation),
  // any admin. Patches the list row in place and the open detail pane if it's
  // the same conversation, so nobody needs a manual refetch to see it. The
  // `assignedTo` key must be OMITTED (not set to `undefined`) when clearing —
  // this file's tsconfig has `exactOptionalPropertyTypes: true`, which rejects
  // an explicit `undefined` value on an optional property.
  const handleConversationRouting = ({
    conversationId,
    state,
    assignedTo,
    assignmentMethod,
  }: ConversationRoutingEvent) => {
    const patch = (c: Conversation): Conversation => {
      const { assignedTo: _prevAssignedTo, ...rest } = c;
      // An explicitly-sent assignee always wins: `bot-state` resume hands the chat
      // back to the bot but DELIBERATELY keeps its human owner and says so. Only
      // the legacy hand-back sends state 'bot' with no assignee, and that one
      // really does clear ownership server-side.
      // `assignedTo: ''` is an explicit clear (Instagram's unassign emits it;
      // WhatsApp never sends an empty string). It used to fall through to
      // "keep the previous assignee", so an unassign never showed on screen.
      const nextAssignedTo = assignedTo
        ? teamMembers.find((m) => m._id === assignedTo) || { _id: assignedTo }
        : assignedTo === '' || state === 'bot'
          ? undefined
          : c.assignedTo;
      return {
        ...rest,
        ...(nextAssignedTo !== undefined ? { assignedTo: nextAssignedTo } : {}),
        // Ownership-only events (tag rules) carry no `state` — leave the existing
        // state untouched, or the routing.state-driven Stop-bot toggle would blank
        // out mid-session on someone else's assignment.
        ...(state ? { routing: { ...(c.routing ?? {}), state } as ConversationRouting } : {}),
        // The backend clears the agent pin on every route to or from the bot
        // (escalate / claim / stop / hand-back), so drop it here too rather than keep
        // advertising a pin the server already deleted. A manual reassign is the
        // exception — reassignConversation only touches ownership and leaves the pin
        // alone, so clearing it here would show a change that never happened.
        ...(state && assignmentMethod !== 'manual' ? { agentPin: null } : {}),
      } as Conversation;
    };
    setConversations((prev) => prev.map((c) => (c._id === conversationId ? patch(c) : c)));
    setSelectedConversation((prev) => (prev && prev._id === conversationId ? patch(prev) : prev));
    // The event carries no history entry, so the open chat's routing trail
    // (and the assignee's resolved name) is re-read from the server — this is
    // also how a colleague's claim/assign shows up live on YOUR screen.
    if (selectedConversation?._id === conversationId) {
      void refreshRouting(conversationId, selectedConversation.channel);
    }
  };

  /**
   * A reaction landed on an already-rendered message. Patch that message's
   * badge row in place — the reaction row itself is never appended to the
   * transcript (see isTranscriptMessage), so this is the ONLY way a live
   * reaction becomes visible without a refetch.
   */
  const handleMessageReaction = (data: {
    conversationId: string;
    whatsappMessageId: string;
    by: string;
    emoji: string | null;
  }) => {
    setMessages((prev) =>
      prev.map((m) => {
        // Same dual-field check as handleMessageStatus — found missing here
        // 2026-09-28 (UI-wiring audit): an Instagram message fetched via REST
        // stores its provider id under `providerMessageId`, not
        // `whatsappMessageId` (see the Message interface's own comment on
        // that field), so a live reaction on an Instagram thread's already-
        // loaded message silently failed to match and patch in.
        if (m.whatsappMessageId !== data.whatsappMessageId && m.providerMessageId !== data.whatsappMessageId) {
          return m;
        }
        const others = (m.reactions || []).filter((r) => r.by !== data.by);
        return {
          ...m,
          reactions: data.emoji ? [...others, { by: data.by, emoji: data.emoji }] : others,
        };
      })
    );
  };

  /**
   * An attachment's bytes finished downloading. Patch that one attachment in
   * place so the bubble swaps from its loading state to the real thumbnail —
   * media is captured and rendered BEFORE the download runs, so without this
   * the operator would have to reload to see the image.
   */
  const handleMessageMedia = (data: MessageMediaEvent) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m._id !== data.messageId || !m.attachments?.length) return m;
        const attachments: MessageAttachment[] = m.attachments.map((att, i) =>
          i === data.attachmentIndex
            ? ({
                ...att,
                storageState: data.storageState as MessageAttachment['storageState'],
                ...(data.thumbUrl ? { thumbUrl: data.thumbUrl } : {}),
                ...(data.s3Url ? { s3Url: data.s3Url } : {}),
                ...(data.size ? { size: data.size } : {}),
                ...(data.mimeType ? { mimeType: data.mimeType } : {}),
                ...(data.width ? { width: data.width } : {}),
                ...(data.height ? { height: data.height } : {}),
                ...(data.error ? { error: data.error } : {}),
                // Spread unconditionally — `false` is the meaningful value
                // here. It is what UNLOCKS a tile the moment space is freed
                // and the file is re-fetched, so the usual
                // `...(x ? {x} : {})` idiom would drop exactly the update
                // this event exists to deliver.
                ...(data.quotaLocked === undefined
                  ? {}
                  : { quotaLocked: data.quotaLocked }),
              } as MessageAttachment)
            : att
        );
        return { ...m, attachments };
      })
    );
  };

  /**
   * The tenant's inbound-media allowance changed — a capture consumed (or was
   * refused) bytes, or someone freed space from the Incoming media page, quite
   * possibly in another tab.
   *
   * Two jobs, both purely local state so the transcript updates in the same
   * tick the event lands:
   *
   *  1. Patch the shared usage cache, so any meter on screen is correct without
   *     a refetch.
   *  2. Re-request the open conversation IF it is showing quota-locked media
   *     and the tenant is no longer over quota. Presigned URLs are minted once,
   *     at fetch time — the server will now issue the ones it previously
   *     withheld, but only if we ask again. Attachments the recovery pass
   *     re-fetches arrive on `message:media` and patch themselves; this covers
   *     the ones that only needed the gate lifted.
   */
  const handleMediaQuota = (data: MediaQuotaEvent) => {
    patchUsage('conversationMedia', {
      used: data.used,
      limit: data.limit,
      remaining: data.limit > 0 ? Math.max(0, data.limit - data.used) : 0,
      exceeded: data.overQuota,
      usagePercent: data.limit > 0 ? Math.min(100, (data.used / data.limit) * 100) : 0,
      unbounded: data.limit <= 0,
      skippedBytes: data.skippedBytes,
      skippedCount: data.skippedCount,
    });

    if (data.overQuota || !selectedConversation) return;
    const hasLocked = messages.some((m) =>
      (m.attachments || []).some((att) => att?.quotaLocked)
    );
    // `silent` — no spinner and no scroll jump. The operator did not ask for
    // this; the tiles should just quietly become viewable where they are.
    if (hasLocked) {
      void fetchConversationDetail(selectedConversation._id, selectedConversation.channel, {
        silent: true,
      });
    }
  };

  const { subscribeToConversation, unsubscribeFromConversation } = useWebSocket(wsTenantId, {
    onNewMessage: handleNewMessage,
    onMessageReaction: handleMessageReaction,
    onMessageMedia: handleMessageMedia,
    onMediaQuota: handleMediaQuota,
    onMessageStatus: handleMessageStatus,
    onConversationUnread: handleConversationUnread,
    onConversationRouting: handleConversationRouting,
    // Arrow, not a direct reference: resyncConversationTail is declared further down.
    onSubscriptionGap: (conversationId) => void resyncConversationTail(conversationId),
  });

  // Re-query the backend — not the already-loaded list — whenever the search text or tag
  // filter changes, so a match outside the current page (e.g. an older conversation, or one
  // that simply isn't in the most-recently-active batch) is still found. Debounced so free
  // typing in the search box doesn't fire a request per keystroke; the tag multi-select fires
  // immediately after the debounce window since it's a discrete action either way.
  //
  // This is the one path that deliberately DOES reset the list to page 1: a
  // changed filter is a different query, so the pages already scrolled through
  // no longer describe it. Scroll is sent back to the top with it — leaving the
  // viewport parked where page 4 of the previous query used to be would land
  // the admin in the middle of an unrelated result set.
  useEffect(() => {
    const handle = setTimeout(() => {
      listContainerRef.current?.scrollTo({ top: 0 });
      fetchConversations();
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    tagFilter,
    statusFilter,
    windowActiveFilter,
    assignedToFilter,
    channelFilter,
    routingFilter,
    sortFilter,
    flaggedOnly,
    // The RESOLVED instants, not the preset plus its two raw box values: those
    // three change on every keystroke in a half-typed custom range (and when
    // the preset moves off 'custom' while the boxes keep their values), none of
    // which changes the query being asked. These two strings change only when
    // the window actually moves.
    dateAddedRange.dateAddedFrom,
    dateAddedRange.dateAddedTo,
  ]);

  // Un-wedge the inbox if Instagram stops being reachable while a
  // channelFilter of 'instagram' is active (a plan downgrade or a role edit
  // landing on the next /auth/me refresh, or the pre-hydration window
  // resolving). The Channel picker is hidden in that state, so without this
  // the list would sit permanently empty with the control that set it gone.
  useEffect(() => {
    if (!instagramAvailable && channelFilter === 'instagram') {
      setChannelFilter('all');
    }
  }, [instagramAvailable, channelFilter]);

  // These four reference lists barely change, but this page refetched all of
  // them on every mount — and it remounts on every navigation back into Chat
  // Logs. Served from the shared request cache instead.
  useEffect(() => {
    cachedRequest('chatLogs:tags', () =>
      adminApi
        .getContactTags({ suppressPermissionLock: true })
        .then((t) => (Array.isArray(t) ? t : []))
    )
      .then(setAllTags)
      .catch(() => { /* non-critical */ });
  }, []);

  // Populates the "Assigned to" picker in the Filters popover.
  useEffect(() => {
    cachedRequest('chatLogs:teamDirectory', () =>
      adminApi
        .getTeamDirectory({ suppressPermissionLock: true })
        .then((list) => (Array.isArray(list) ? list : []))
    )
      .then(setTeamMembers)
      .catch(() => { /* non-critical */ });
  }, []);

  // Name lookup for the "which AI agent replied" sender label on bot messages.
  useEffect(() => {
    cachedRequest('chatLogs:agents', () =>
      adminApi
        .getAgents({ limit: 200 }, { suppressPermissionLock: true })
        .then((r) => r.data || [])
    )
      .then(setAgents)
      .catch(() => { /* non-critical */ });
  }, []);

  // Fetch all templates once and build name→template lookup for message rendering.
  // Suppressed defensively even though the backend's orModule fallback
  // (activity.chatLogs read) already covers the common case for these two
  // routes specifically — a role could still lack BOTH permissions.
  useEffect(() => {
    cachedRequest('chatLogs:templates', () =>
      adminApi.getWhatsAppTemplates({ suppressPermissionLock: true })
    )
      .then((templates) => {
        const map: Record<string, WhatsAppTemplate> = {};
        templates.forEach((t) => {
          map[t.name] = t;
        });
        setTemplateMap(map);
      })
      .catch(() => {
        /* non-critical */
      });
  }, []);

  /** Re-fetch templates FRESH (bypassing the mount cache) and refresh templateMap.
   *  Called after a send flags a template media-unfit so the picker reflects the
   *  new state immediately, without a manual page refresh. */
  const reloadTemplateMap = () => {
    adminApi
      .getWhatsAppTemplates({ suppressPermissionLock: true })
      .then((templates) => {
        const map: Record<string, WhatsAppTemplate> = {};
        templates.forEach((t) => {
          map[t.name] = t;
        });
        setTemplateMap(map);
      })
      .catch(() => {
        /* non-critical */
      });
  };

  /**
   * Signed S3 preview URLs for template media, fetched ON DEMAND.
   *
   * This used to run over the WHOLE tenant on mount: one request per
   * media-header template plus one per carousel template, whether or not any of
   * them were ever displayed. That is O(templates) requests on every page load,
   * growing every time someone adds a template — and the URLs it collected are
   * short-lived, so most had expired before anything could use them.
   *
   * Only two things render template media: the transcript (for templates that
   * actually appear in the open conversation) and the composer preview (for the
   * one template being composed). Both ask for exactly what they need, which is
   * typically one or two rather than all of them. Mirrors what WorkflowCanvas
   * already did correctly with the same two endpoints.
   */
  const requestedPreviews = useRef<Set<string>>(new Set());
  const ensureTemplatePreviews = useCallback(
    (names: string[]) => {
      names.forEach((name) => {
        if (!name || requestedPreviews.current.has(name)) return;
        const t = templateMap[name];
        // Not marked as requested until the definition is known, so this retries
        // once `templateMap` arrives rather than giving up permanently.
        if (!t) return;
        requestedPreviews.current.add(name);

        const header = t.components.header;
        const hasHeaderMedia =
          (header?.type === 'IMAGE' || header?.type === 'VIDEO' || header?.type === 'DOCUMENT') &&
          !!header.headerMediaS3Key;
        if (hasHeaderMedia) {
          adminApi
            .getWhatsAppTemplateHeaderMediaPreview(t.name, t.language, {
              suppressPermissionLock: true,
            })
            .then((res) => {
              if (res.previewUrl) {
                setMediaUrls((prev) => ({ ...prev, [t.name]: res.previewUrl as string }));
              }
            })
            .catch(() => {
              /* best-effort, same as before */
            });
        }

        if ((t.components.carousel?.cards?.length ?? 0) > 0) {
          adminApi
            .getWhatsAppTemplateCarouselMediaPreview(t.name, t.language, {
              suppressPermissionLock: true,
            })
            .then((cards) => {
              setCarouselMediaUrls((prev) => ({
                ...prev,
                [t.name]: cards.map((c) => c.previewUrl ?? undefined),
              }));
            })
            .catch(() => {
              /* best-effort, same as before */
            });
        }
      });
    },
    [templateMap]
  );

  // Whatever templates the OPEN conversation actually shows.
  useEffect(() => {
    const names = messages
      .map((m) => parseTemplateMessage(m.content)?.name)
      .filter((n): n is string => !!n);
    if (names.length > 0) ensureTemplatePreviews(names);
  }, [messages, ensureTemplatePreviews]);

  // And the one being previewed in the composer.
  useEffect(() => {
    if (activeTemplateName) ensureTemplatePreviews([activeTemplateName]);
  }, [activeTemplateName, ensureTemplatePreviews]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Stop any in-flight typing indicator when the open conversation changes
  // (or on unmount) — otherwise switching threads mid-sentence leaves the
  // PREVIOUS customer's chat showing "typing…" forever, since typing_off was
  // keyed to whichever igsid the composer last signalled for.
  useEffect(() => {
    return () => stopTypingIndicator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?._id]);

  useEffect(() => {
    if (initialSelectionDone) return;
    const conversationId = searchParams.get('conversationId');
    const phone = searchParams.get('phone');
    // A deep link names the conversation outright, so it does NOT need the list
    // to arrive first. Gating on `conversations.length` put the whole list fetch
    // on the critical path before the chat pane could even start loading — and
    // when the target was not in the top-50 page, that list request bought
    // nothing at all. Matching by phone still needs the list.
    if (conversations.length === 0 && !conversationId) return;
    const preselected =
      conversations.find((c) => conversationId && c._id === conversationId) ||
      conversations.find((c) => phone && c.customerPhone === phone);

    if (preselected) {
      selectionMadeRef.current = true;
      setSelectedConversation(preselected);
      if (isMobile) setShowChat(true);
      setInitialSelectionDone(true);
      return;
    }

    // Not present in the just-fetched top-50-most-recently-active page — e.g. a
    // link from an advanced-filter view on the Contacts page (never-delivered,
    // active-24h, etc.) can point at a conversation that isn't among the most
    // recently updated ones. Rather than silently show an empty chat pane, fetch
    // that one conversation directly by id (the same detail endpoint already used
    // by fetchConversationDetail below) and splice it into the list.
    if (conversationId) {
      if (deepLinkFetchStarted.current) return;
      deepLinkFetchStarted.current = true;
      adminApi
        .getConversationDetail(conversationId)
        .then((raw: any) => {
          // The list may have landed first and already selected it; splicing a
          // second copy in would only reshuffle the list under the user.
          if (selectionMadeRef.current) return;
          const constructed: Conversation = {
            _id: raw._id,
            channel: raw.channel || 'whatsapp',
            channelIdentity: raw.channelIdentity,
            customerPhone: raw.customerPhone,
            messageCount: Array.isArray(raw.messages)
              ? raw.messages.length
              : raw.analytics?.messageCount ?? 0,
            lastMessage:
              Array.isArray(raw.messages) && raw.messages.length > 0
                ? raw.messages[raw.messages.length - 1]?.content || ''
                : '',
            lastMessageAt: raw.analytics?.lastMessageAt || raw.updatedAt,
            status: raw.status,
            ...(raw.customerName ? { customerName: raw.customerName } : {}),
            ...(raw.metadata?.tags?.length ? { tags: raw.metadata.tags } : {}),
            ...(raw.windowExpiresAt ? { windowExpiresAt: raw.windowExpiresAt } : {}),
            ...(raw.lastInboundMessageAt
              ? { lastInboundMessageAt: raw.lastInboundMessageAt }
              : {}),
            ...(raw.assignedTo ? { assignedTo: raw.assignedTo } : {}),
          };
          setConversations((prev) => [
            constructed,
            ...prev.filter((c) => c._id !== constructed._id),
          ]);
          selectionMadeRef.current = true;
          // Pinned, because the list fetch may still be in flight: it replaces
          // the whole array when it lands, which would drop this row straight
          // back out of the sidebar.
          deepLinkPinnedRef.current = constructed;
          setSelectedConversation(constructed);
          if (isMobile) setShowChat(true);
        })
        .catch((error) => {
          // Conversation may have been deleted since the filter result was
          // computed — nothing valid to select, leave the empty state up.
          console.error('Error fetching linked conversation:', error);
        })
        .finally(() => setInitialSelectionDone(true));
      return;
    }

    setInitialSelectionDone(true);
  }, [conversations, searchParams, isMobile, initialSelectionDone]);

  // Depend only on the conversation ID so switching conversations loads the detail exactly once.
  // Never put selectedConversation (the full object) here — fetchConversationDetail previously
  // called setSelectedConversation, which would create a new object reference and re-fire this
  // effect on every fetch, causing an infinite loop.
  useEffect(() => {
    if (!selectedConversation) return;
    setWindowDetail({});          // clear stale window data from previous conversation
    setRoutingHistory([]);        // clear stale routing history from previous conversation
    setCtwaAttribution(undefined); // clear stale ad attribution from previous conversation
    setMessages([]);              // clear stale messages
    setHasMoreMessages(false);    // clear stale pagination state — set fresh once the new conversation loads
    fetchConversationDetail(selectedConversation._id, selectedConversation.channel);
    if (isMobile) setShowChat(true);
    subscribeToConversation(selectedConversation._id);
    return () => {
      unsubscribeFromConversation(selectedConversation._id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?._id]);

  // Applies whatever scroll response the change to `messages` calls for —
  // never a blanket "always animate to bottom" (that's what made opening a
  // long conversation look like it was playing through from message one).
  // useLayoutEffect, not useEffect: this must run and paint before the
  // browser shows the frame, so there's no visible flash of the wrong
  // scroll position first.
  useLayoutEffect(() => {
    const action = pendingScrollActionRef.current;
    pendingScrollActionRef.current = null;
    const container = messagesContainerRef.current;
    if (!container || !action) return;

    if (action === 'jump') {
      // Opening a conversation (or switching to one) — land on the latest
      // message instantly, no animation to "play through" on the way there.
      container.scrollTop = container.scrollHeight;
    } else if (action === 'smooth') {
      // A new message arrived while the admin was already at (or near) the
      // bottom — animate down to it, same as every chat app does.
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    } else if (action === 'preserve') {
      // An older page was just prepended above the current view — keep
      // whatever the admin was looking at in the same visual spot rather
      // than jumping them to the top or bottom of the now-taller list.
      const anchor = scrollAnchorRef.current;
      if (anchor) {
        container.scrollTop = container.scrollHeight - anchor.prevScrollHeight + anchor.prevScrollTop;
      }
      scrollAnchorRef.current = null;
    }
  }, [messages]);

  const loadOlderMessages = async () => {
    if (!selectedConversation || loadingOlderMessages || !hasMoreMessages || messages.length === 0) return;
    const oldest = messages[0];
    if (!oldest) return;
    setLoadingOlderMessages(true);
    try {
      const cursorParams = {
        beforeMessageId: oldest._id,
        beforeCreatedAt: oldest.createdAt,
      };
      const older =
        selectedConversation.channel === 'instagram'
          ? await adminApi.getInstagramConversationDetail(selectedConversation._id, cursorParams)
          : await adminApi.getConversationDetail(selectedConversation._id, cursorParams);
      const container = messagesContainerRef.current;
      if (container) {
        scrollAnchorRef.current = { prevScrollHeight: container.scrollHeight, prevScrollTop: container.scrollTop };
      }
      pendingScrollActionRef.current = 'preserve';
      setMessages((prev) => [...(older.messages || []).filter(isTranscriptMessage), ...prev]);
      setHasMoreMessages(Boolean(older.hasMoreMessages));
    } catch (error) {
      console.error('Error loading older messages:', error);
    } finally {
      setLoadingOlderMessages(false);
    }
  };

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    // Near the top (not necessarily exactly at 0 — trigger a little early so
    // the next page is ready before the admin actually hits the edge).
    if (container.scrollTop < 120 && hasMoreMessages && !loadingOlderMessages) {
      loadOlderMessages();
    }
  };

  // Two channel stores, one inbox: WhatsApp lives in /admin/conversations
  // (which doubles as the campaign contact store) and Instagram in
  // /admin/instagram/conversations (its own split collections). The "All
  // channels" view fetches both recency-sorted pages and merge-sorts
  // client-side — the standard fan-in for a split-store omnichannel inbox.
  //
  // Three modes, all through one function so the (substantial) filter and
  // per-channel RBAC handling below is never duplicated:
  //   - default            → fresh query, page 1 (a filter or search changed)
  //   - { preserveDepth }  → same query, but re-requesting every page the
  //                          admin has already scrolled through in ONE wider
  //                          request, so a reconciling refetch does not yank
  //                          the list back to its first 50 rows
  //   - { append }         → the next page, appended (infinite scroll)
  const fetchConversations = async (opts?: { append?: boolean; preserveDepth?: boolean; background?: boolean }) => {
    const append = opts?.append ?? false;
    // A background reconcile (a new-conversation socket event, a routing patch)
    // must NOT flip the list into its skeleton state — that blanked the whole
    // inbox for a beat every time a message arrived for a not-yet-listed chat.
    // The rows just update in place when the request resolves. Only a genuine
    // query change (filter/search/first load) shows skeletons.
    const background = opts?.background ?? false;
    const depth = opts?.preserveDepth
      ? Math.min(loadedPagesRef.current, CONVERSATION_MAX_REFRESH_PAGES)
      : 1;
    const page = append ? loadedPagesRef.current + 1 : 1;
    const limit = append ? CONVERSATION_PAGE_SIZE : CONVERSATION_PAGE_SIZE * depth;
    // Once a channel has reported it has no further pages, stop spending a
    // round-trip on it — it can only come back empty. Only applies while
    // appending; a fresh/depth-preserving query always re-asks both legs.
    const skipExhaustedWhatsapp = append && !hasMoreWhatsappRef.current;
    const skipExhaustedInstagram = append && !hasMoreInstagramRef.current;

    const commonParams = {
      page,
      limit,
      search,
      ...(tagFilter.length > 0 ? { tags: tagFilter.join(',') } : {}),
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(windowActiveFilter ? { windowActive: true } : {}),
      ...(assignedToFilter !== ASSIGNED_TO_ALL ? { assignedTo: assignedToFilter } : {}),
      // Already absent when no range is picked (resolveDateAddedRange returns
      // {} for 'all'), so this spreads to nothing rather than needing a guard.
      // Goes in commonParams, not the WhatsApp leg's extras: both endpoints
      // take this pair, which is the whole point of adding it to the Instagram
      // one rather than carving that channel out.
      ...dateAddedRange,
    };
    // Instagram's endpoint clamps `limit` to 100 server-side while WhatsApp's
    // does not, so a deep depth-preserving refresh would silently come back
    // short on that leg. Clamp it here too and let hasNext (which the backend
    // computes against the limit it actually used) carry the remainder into
    // the next scroll rather than pretending those rows don't exist.
    const instagramParams = {
      ...commonParams,
      limit: Math.min(limit, INSTAGRAM_LIST_LIMIT_CAP),
    };
    const emptyLeg = { rows: [] as Conversation[], hasMore: false };
    if (!append) listQueryTokenRef.current += 1;
    const queryToken = listQueryTokenRef.current;
    try {
      if (append) {
        setLoadingMoreList(true);
      } else if (!background) {
        setLoadingList(true);
      }
      // Promise.allSettled, not Promise.all — the two channels now enforce
      // INDEPENDENT RBAC permissions (activity.chatLogs vs instagram). A
      // role granted only one of the two must still see that channel's real
      // data; Promise.all would let either leg's 403 wipe out BOTH results,
      // since a role without 'instagram' access legitimately gets denied on
      // that leg alone. Each leg logs and falls back to [] independently
      // rather than failing the whole fetch.
      const [whatsappResult, instagramResult] = await Promise.allSettled([
        // No permission pre-check on the WhatsApp leg, deliberately — this
        // is the page's PRIMARY content, and usePermissionAccess's
        // hasPermission fails CLOSED (denies) whenever effectivePermissions
        // hasn't hydrated yet (e.g. briefly right after login, before the
        // first /auth/me response lands) — using it as a gate here would
        // risk hiding a legitimate tenant_owner/tenant_admin's real chat
        // data during that window, a materially worse bug than the one
        // being fixed below. The backend 403 + Snackbar fallback already
        // handles a genuine denial correctly.
        channelFilter === 'instagram' || skipExhaustedWhatsapp
          ? Promise.resolve(emptyLeg)
          : adminApi
              .getConversations({
                ...commonParams,
                ...(routingFilter !== 'all' ? { routingState: routingFilter } : {}),
                ...(sortFilter !== 'recent' ? { sort: sortFilter } : {}),
                ...(flaggedOnly ? { flagged: true as const } : {}),
              })
              .then((r) => ({ rows: r.data || [], hasMore: Boolean(r.meta?.hasNext) })),
        // Instagram has no routing/escalation backend yet (see the `routing`
        // field's doc comment) — while a routing filter is active, skip this
        // leg entirely rather than returning Instagram rows a filter that
        // doesn't apply to them can't actually narrow. Same reasoning for
        // sortFilter==='incoming': Instagram rows have no activityBucket, so
        // there's nothing meaningful to merge them in by under that order.
        //
        // Also skip when this admin genuinely can't have this channel's
        // data, so there's no point spending a round-trip just to 403 (this
        // was exactly the reported bug: a permission-denied dialog flashing
        // on every page load for a channel the admin was never going to
        // see). Safe to use these hooks' fail-CLOSED behavior here
        // specifically, unlike the WhatsApp leg above — Instagram is the
        // page's SECONDARY channel, so worst case during a transitional
        // pre-hydration window is a brief, self-healing gap in supplementary
        // data, not the loss of the page's main content.
        //
        // `instagramAvailable` covers BOTH axes the backend gate denies on —
        // see its definition above for why the RBAC axis alone was the bug.
        channelFilter === 'whatsapp' ||
        routingFilter !== 'all' ||
        sortFilter === 'incoming' ||
        // Same carve-out: aiControl.flagged exists only on the WhatsApp
        // Conversation store, so Instagram rows can't satisfy this filter.
        flaggedOnly ||
        !instagramAvailable ||
        skipExhaustedInstagram
          ? Promise.resolve(emptyLeg)
          : adminApi
              .getInstagramConversations(instagramParams, { suppressPermissionLock: true })
              .then((r) => ({ rows: r.data || [], hasMore: Boolean(r.meta?.hasNext) })),
      ]);
      if (whatsappResult.status === 'rejected') {
        console.error('Error fetching WhatsApp conversations:', whatsappResult.reason);
        // A permission denial (role lacks 'activity.chatLogs') routes to the
        // global PermissionLockDialog, and a plan lock to PlanLockDialog the
        // same way the rest of this file does — only fall back to the inline
        // Snackbar for errors that are neither. Raw-toasting a plan/permission
        // 403 would show the backend's "upgrade your plan" copy as if it were
        // a fetch failure, duplicating a gate the user already has proper UI
        // for (the route's own ModuleAccessGuard panel).
        if (
          !isPermissionLockTagged(whatsappResult.reason) &&
          !showPlanLockIfTagged(whatsappResult.reason)
        ) {
          setActionError(extractErrorMessage(whatsappResult.reason, 'Failed to load chat conversations'));
        }
      }
      if (instagramResult.status === 'rejected') {
        console.error('Error fetching Instagram conversations:', instagramResult.reason);
        // Only surface this one if WhatsApp already succeeded — otherwise it
        // would silently overwrite the (more relevant) WhatsApp error above.
        //
        // isPlanLockTagged (pure check), NOT showPlanLockIfTagged: unlike the
        // WhatsApp leg, a plan lock on this SECONDARY channel is an expected,
        // non-actionable outcome for a tenant whose plan omits Instagram, so
        // it must stay fully silent — a modal on every page load would be the
        // same bug in a different shape. The skip condition above should mean
        // we never get here at all; this is the second line of defense for
        // the pre-hydration window and for any plan/RBAC drift.
        if (
          whatsappResult.status !== 'rejected' &&
          !isPermissionLockTagged(instagramResult.reason) &&
          !isPlanLockTagged(instagramResult.reason)
        ) {
          setActionError(extractErrorMessage(instagramResult.reason, 'Failed to load Instagram conversations'));
        }
      }
      // Superseded by a newer query while this one was in flight — the `finally`
      // below still clears the loading flags, but nothing here may touch the
      // list or the paging flags, which now describe a different result set.
      if (queryToken !== listQueryTokenRef.current) return;
      const whatsappRows = whatsappResult.status === 'fulfilled' ? whatsappResult.value.rows : [];
      const instagramRows =
        instagramResult.status === 'fulfilled' ? instagramResult.value.rows : [];
      // A rejected leg reports "no more pages" rather than keeping its previous
      // flag: the scroll handler fires on every scroll event while the admin
      // sits at the bottom of the list, so a leg that keeps failing fast would
      // otherwise retry in a tight loop. The error is surfaced above, and the
      // refresh button (or any filter change) re-opens the channel.
      const nextHasMoreWhatsapp =
        whatsappResult.status === 'fulfilled' ? whatsappResult.value.hasMore : false;
      const nextHasMoreInstagram =
        instagramResult.status === 'fulfilled' ? instagramResult.value.hasMore : false;
      setHasMoreWhatsapp(nextHasMoreWhatsapp);
      setHasMoreInstagram(nextHasMoreInstagram);
      hasMoreWhatsappRef.current = nextHasMoreWhatsapp;
      hasMoreInstagramRef.current = nextHasMoreInstagram;
      // 'incoming' mode: the backend already returned whatsappRows in its
      // activityBucket/lastActivityAt order (Instagram excluded above, so
      // there's nothing to interleave) — re-sorting by lastMessageAt here
      // would silently throw that order away and fall back to plain recency.
      // 'recent' mode: lastMessageAt already IS most-recent-activity-of-
      // either-direction (same semantics as the backend's lastInteractionAt),
      // so this sort is only doing the merge/interleave of the two channels,
      // not changing the ordering semantics.
      //
      // No truncation to one page's worth any more. The old `.slice(0, 50)` on
      // the two merged channel pages silently DROPPED up to 50 real rows off
      // the bottom of the inbox with no way to ever reach them; now that the
      // list pages, every row fetched is a row the admin can scroll to.
      const merged =
        sortFilter === 'incoming'
          ? whatsappRows
          : [...whatsappRows, ...instagramRows].sort(
              (a, b) =>
                new Date(b.lastMessageAt || 0).getTime() - new Date(a.lastMessageAt || 0).getTime()
            );
      if (append) {
        // By the active sort key, page N+1's rows are older than everything
        // already on screen, so they are concatenated rather than re-sorted
        // into the existing list. That is also what keeps every in-place patch
        // intact — optimistic routing/flag edits, socket message bumps and the
        // pinned deep-link row all stay exactly where they are, which a
        // wholesale re-sort (or replace) would quietly undo.
        //
        // Deduped by _id because a conversation bumped to the top by new
        // traffic between two page requests shifts everything below it down by
        // one, which hands the next page's skip window a row already on screen.
        setConversations((prev) => {
          const seen = new Set(prev.map((c) => c._id));
          const additions = merged.filter((c) => !seen.has(c._id));
          return additions.length > 0 ? [...prev, ...additions] : prev;
        });
        loadedPagesRef.current = page;
      } else {
        // Keep a deep-linked conversation that this page of results does not
        // contain — it was spliced in when it resolved, and a wholesale replace
        // would silently remove the very row the URL points at.
        const pinned = deepLinkPinnedRef.current;
        setConversations(
          pinned && !merged.some((c) => c._id === pinned._id) ? [pinned, ...merged] : merged
        );
        loadedPagesRef.current = depth;
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      if (append) {
        setLoadingMoreList(false);
        loadingMoreRef.current = false;
      } else {
        setLoadingList(false);
      }
    }
  };

  /** Next page of the conversation list (infinite scroll). The ref guard, not
   *  `loadingMoreList`, is what actually prevents a double fetch: scroll events
   *  fire far faster than React re-renders with the new state. */
  const loadMoreConversations = () => {
    if (loadingMoreRef.current || loadingList) return;
    if (!hasMoreWhatsappRef.current && !hasMoreInstagramRef.current) return;
    loadingMoreRef.current = true;
    void fetchConversations({ append: true });
  };

  const handleListScroll = () => {
    const container = listContainerRef.current;
    if (!container) return;
    // Near the bottom, not exactly at it — trigger early enough that the next
    // page is usually ready before the admin reaches the edge. Mirrors
    // handleMessagesScroll's top-edge threshold for the message transcript.
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom <= CONVERSATION_SCROLL_THRESHOLD) {
      loadMoreConversations();
    }
  };

  /** `silent`: used for the post-send confirmatory re-sync (see sendReply /
   *  handleSendTemplate) — the message the admin just sent already lands in
   *  `messages` instantly via the `message:new` socket event (the same path
   *  every other real-time arrival uses), so this call exists purely as a
   *  best-effort correctness net for a missed/late socket event, not as the
   *  primary way the UI learns about it. Without `silent` it used to blow
   *  away the thread with 4 loading skeletons and force-jump the scroll on
   *  *every* send — a visible "reload" flash right after the socket had
   *  already smoothly appended the same message a moment earlier. Silent
   *  mode skips both: no skeleton swap, and no forced scroll (leaves
   *  whatever the socket append already decided). */
  const fetchConversationDetail = async (
    id: string,
    channel?: ConversationChannel,
    opts?: { silent?: boolean }
  ) => {
    const silent = opts?.silent ?? false;
    try {
      if (!silent) setLoadingMsgs(true);
      // No cursor => first/latest page (default DEFAULT_MESSAGE_PAGE_SIZE
      // messages server-side), not the full history. Channel routes to the
      // owning store (Instagram threads live in their own collections).
      const conv =
        channel === 'instagram'
          ? await adminApi.getInstagramConversationDetail(id)
          : await adminApi.getConversationDetail(id);
      if (!silent) pendingScrollActionRef.current = 'jump';
      setMessages((conv.messages || []).filter(isTranscriptMessage));
      setHasMoreMessages(Boolean(conv.hasMoreMessages));
      // Store window fields in their own state — never touch selectedConversation here.
      setWindowDetail({
        ...(conv.windowExpiresAt ? { windowExpiresAt: conv.windowExpiresAt } : {}),
        ...(conv.lastInboundMessageAt ? { lastInboundMessageAt: conv.lastInboundMessageAt } : {}),
      });
      setRoutingHistory(Array.isArray(conv.routing?.history) ? conv.routing.history : []);
      // Refreshed on every (incl. silent) refetch, so a returning customer's newer ad
      // click shows up without reopening. Instagram threads have no such field —
      // their attribution rides the list row's `adReferral` instead.
      setCtwaAttribution(channel === 'instagram' ? undefined : conv.metadata?.ctwaAttribution);
    } catch (error) {
      console.error('Error fetching conversation detail:', error);
    } finally {
      if (!silent) setLoadingMsgs(false);
    }
  };

  /**
   * Live messages may have been missed for the open conversation (the socket's
   * subscription is confirmed a moment after opening, or it reconnected — see
   * useWebSocket's onSubscriptionGap). Fetch the latest page and APPEND only
   * what the transcript doesn't have yet: unlike a silent fetchConversationDetail,
   * this never replaces older history the user scrolled up to load, and only
   * follows to the bottom if they were already there — same as a live append.
   */
  const resyncConversationTail = async (id: string) => {
    const channel = selectedConversation?._id === id ? selectedConversation.channel : undefined;
    try {
      const conv =
        channel === 'instagram'
          ? await adminApi.getInstagramConversationDetail(id)
          : await adminApi.getConversationDetail(id);
      if (selectedConversationIdRef.current !== id) return; // switched away meanwhile
      const fresh: Message[] = (conv.messages || []).filter(isTranscriptMessage);
      const container = messagesContainerRef.current;
      const wasNearBottom = container
        ? container.scrollHeight - container.scrollTop - container.clientHeight < 120
        : true;
      setMessages((prev) => {
        const known = new Set(prev.map((m) => m._id));
        const added = fresh.filter((m) => !known.has(m._id));
        if (added.length === 0) return prev;
        pendingScrollActionRef.current = wasNearBottom ? 'smooth' : null;
        return [...prev, ...added].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      });
      setWindowDetail({
        ...(conv.windowExpiresAt ? { windowExpiresAt: conv.windowExpiresAt } : {}),
        ...(conv.lastInboundMessageAt ? { lastInboundMessageAt: conv.lastInboundMessageAt } : {}),
      });
    } catch {
      // Best-effort: the next live event or reopen corrects it.
    }
  };

  /**
   * Re-read ONE conversation's routing from the server after an ownership
   * change. The list refetch never touches the open conversation, and the
   * socket event carries no history entry — so before this, an Instagram
   * claim/assign/unassign succeeded server-side while the panel's owner and
   * "Routing history" stayed frozen until the chat was reopened. Only routing
   * is applied: messages, scroll position and pagination are left alone.
   *
   * For WhatsApp only the history is refreshed — its owner is already kept
   * current by the socket patch and optimistic updates, left as they were.
   */
  const refreshRouting = async (id: string, channel?: ConversationChannel) => {
    try {
      const conv =
        channel === 'instagram'
          ? await adminApi.getInstagramConversationDetail(id, { limit: 1 })
          : await adminApi.getConversationDetail(id);
      if (openConversationIdRef.current !== id) return;
      setRoutingHistory(Array.isArray(conv.routing?.history) ? conv.routing.history : []);
      if (channel === 'instagram') {
        const apply = (c: Conversation): Conversation => {
          const { assignedTo: _prev, ...rest } = c;
          return {
            ...rest,
            ...(conv.assignedTo ? { assignedTo: conv.assignedTo } : {}),
            ...(conv.routing ? { routing: conv.routing } : {}),
            assigneeSeenAt: conv.assigneeSeenAt,
          } as Conversation;
        };
        setConversations((prev) => prev.map((c) => (c._id === id ? apply(c) : c)));
        setSelectedConversation((prev) => (prev && prev._id === id ? apply(prev) : prev));
      }
    } catch {
      // Non-critical: the next open of this chat re-reads everything anyway.
    }
  };

  // ── Hybrid routing: claim / assign / hand back to bot ──────────────────────
  // Bot control (stop/resume) is WhatsApp-only — Instagram has no bot path
  // yet (Phase 4 of the Instagram roadmap). Assign/claim/unassign now exist
  // for BOTH channels, each gated on ITS OWN module ('activity.chatLogs' for
  // WhatsApp, 'instagram' for Instagram) — never the other's, so a role with
  // access to one channel's inbox is never silently granted the other's.

  // Full tier on activity.chatLogs, not a literal tenant_owner role check —
  // matches the backend's real gate (admin.controller.ts's assignConversation/
  // handBackToBot both check hasEffectivePermission(..., 'full')). Using the
  // stale role-literal here would hide "Assign to Team Member" from any
  // custom role the backend actually allows to use it. Safe to fail CLOSED on
  // a hydration gap (usePermissionAccess's own contract) — this only gates a
  // SECONDARY action (assign/hand-back), never this page's primary content,
  // same risk calculus as every other hasPermission call in this file.
  const canManageAssignment = hasPermission('activity.chatLogs', 'full');
  // Instagram's own equivalent — instagram-inbox.controller.ts's
  // assignConversation/unassignConversation both check hasEffectivePermission
  // (..., 'instagram', 'full') in-handler, on top of the router's 'edit' floor.
  const canManageInstagramAssignment = hasPermission('instagram', 'full');

  /** Current assignee, or someone with Full chatLogs access, may hand a conversation back to the bot. */
  const canHandBack = (conv: Conversation) => canManageAssignment || conv.assignedTo?._id === user?.id;

  // ContactPanel's tags/notes editors live under the SEPARATE `contacts`
  // module gate for WhatsApp (see admin.routes.ts), not `activity.chatLogs` —
  // a role granted only the latter must not see edit/delete affordances it
  // can't actually use, and must not even attempt a fetch that's certain to
  // 403 (see canViewContactExtras). Instagram's tags/notes are gated by the
  // `instagram` module itself instead — there is no separate "instagram
  // contacts" module, since an Instagram thread is never a Contact.
  const canEditContact = hasPermission('contacts', 'edit');
  // DELETE /contacts/:id/notes/:noteId method-derives to 'full' server-side
  // (require-permission.middleware.ts) — a strictly higher tier than the
  // 'edit' that gates adding a note.
  const canDeleteContactNotes = hasPermission('contacts', 'full');
  const canViewContactExtras = hasPermission('contacts', 'read');
  const canEditInstagram = hasPermission('instagram', 'edit');
  const canDeleteInstagramNotes = hasPermission('instagram', 'full');
  const canViewInstagramExtras = hasPermission('instagram', 'read');

  /** ⋮ → Export Conversation: server-streamed CSV transcript, saved via a
   *  transient anchor (the viewer can't download from a blob URL otherwise). */
  const handleExportConversation = async (conversation: Conversation) => {
    try {
      setActionError('');
      const blob = await adminApi.exportConversation(conversation._id, conversation.channel);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-${(conversation.customerName || conversation.customerPhone || conversation._id)
        .replace(/[^a-z0-9_-]+/gi, '_')
        .slice(0, 60)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      if (!showPlanLockIfTagged(err) && !isPermissionLockTagged(err)) {
        setActionError(err.response?.data?.error?.message || err.message || 'Failed to export conversation');
      }
    }
  };

  /** ⋮ → Archive / Unarchive: flips the contact's status via the contacts
   *  endpoint (a contact IS the conversation) for WhatsApp, or the Instagram
   *  store's own status endpoint for an Instagram thread (its `_id` is never
   *  a row in the WhatsApp Conversation collection `setContactStatus` reads —
   *  see backend/docs/instagram-roadmap.md bug B2). Reversible from the same
   *  menu or the Contacts page (WhatsApp only — Instagram threads have no
   *  Contacts page entry). */
  const handleArchiveConversation = async (conversationId: string) => {
    const current = conversations.find((c) => c._id === conversationId) || selectedConversation;
    const next: 'active' | 'archived' = current?.status === 'archived' ? 'active' : 'archived';
    try {
      setActionError('');
      if (current?.channel === 'instagram') {
        await adminApi.setInstagramConversationStatus(conversationId, next);
      } else {
        await adminApi.setContactStatus(conversationId, next);
      }
      setConversations((prev) => prev.map((c) => (c._id === conversationId ? { ...c, status: next } : c)));
      setSelectedConversation((prev) =>
        prev && prev._id === conversationId ? { ...prev, status: next } : prev
      );
      setActionNotice(next === 'archived' ? 'Conversation archived' : 'Conversation restored');
    } catch (err: any) {
      if (!showPlanLockIfTagged(err) && !isPermissionLockTagged(err)) {
        setActionError(err.response?.data?.error?.message || err.message || 'Failed to update conversation');
      }
    }
  };

  /**
   * Explicit bot-control toggle: 'stop' pauses automated replies and takes
   * over the chat; 'resume' hands it back to the bot (assignment is kept).
   * Optimistic — routing.state is patched immediately, then reconciled after
   * the round-trip (success or failure both trigger a reconciling refetch).
   */
  const handleSetBotState = async (conversationId: string, action: 'stop' | 'resume') => {
    setActioningId(conversationId);
    const optimisticState: ConversationRoutingState = action === 'stop' ? 'human' : 'bot';
    const patchRouting = (c: Conversation): Conversation => ({
      ...c,
      routing: { ...(c.routing ?? {}), state: optimisticState },
    });
    setConversations((prev) => prev.map((c) => (c._id === conversationId ? patchRouting(c) : c)));
    setSelectedConversation((prev) =>
      prev && prev._id === conversationId ? patchRouting(prev) : prev
    );
    try {
      await adminApi.setConversationBotState(conversationId, action);
      fetchConversations({ preserveDepth: true });
    } catch (error) {
      if (!isPermissionLockTagged(error)) {
        setActionError(
          extractErrorMessage(
            error,
            action === 'stop' ? 'Failed to stop bot' : 'Failed to resume bot'
          )
        );
      }
      // reconcile the optimistic update against the server
      fetchConversations({ preserveDepth: true });
    } finally {
      setActioningId(null);
    }
  };

  /**
   * Clear the review flag an automation's Flag node set.
   *
   * Patches BOTH `conversations` (the list row's chip) and
   * `selectedConversation` (the open thread's header chip + banner) — they are
   * deliberately separate state here, so touching only the list left the
   * banner and header chip of the very chat you just reviewed stale until a
   * refetch replaced the selection. handleConversationRouting above patches
   * the same pair for exactly this reason; this follows it.
   *
   * Optimistic so the banner clears on click rather than after a round trip.
   * The flag is advisory triage state, so a failed clear costs nothing worse
   * than it reappearing on the reconciling refetch (and the error is surfaced).
   */
  const handleClearFlag = async (conversationId: string) => {
    setActioningId(conversationId);
    const clearFlagFields = (c: Conversation): Conversation => {
      // Destructured out rather than set to undefined: `exactOptionalPropertyTypes`
      // rejects an explicit undefined on an optional property (same reasoning as
      // handleConversationRouting's assignedTo handling).
      const { flagReason: _r, flaggedAt: _a, flagWorkflowName: _w, flagNodeLabel: _n, ...rest } = c;
      return { ...rest, flagged: false };
    };
    setConversations((prev) =>
      prev.map((c) => (c._id === conversationId ? clearFlagFields(c) : c))
    );
    setSelectedConversation((prev) =>
      prev && prev._id === conversationId ? clearFlagFields(prev) : prev
    );
    try {
      await adminApi.clearConversationFlag(conversationId);
      fetchConversations({ preserveDepth: true });
    } catch (error) {
      if (!isPermissionLockTagged(error)) {
        setActionError(extractErrorMessage(error, 'Failed to clear the review flag'));
      }
      // reconcile the optimistic clear against the server
      fetchConversations({ preserveDepth: true });
    } finally {
      setActioningId(null);
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedConversation || !selectedUserId) return;
    setAssigning(true);
    try {
      if (selectedConversation.channel === 'instagram') {
        await adminApi.assignInstagramConversation(selectedConversation._id, selectedUserId);
        // Don't depend on the socket event arriving to show the result.
        void refreshRouting(selectedConversation._id, 'instagram');
        const member = teamMembers.find((m) => m._id === selectedUserId);
        setActionNotice(
          member ? `Assigned to ${[member.firstName, member.lastName].filter(Boolean).join(' ')}` : 'Conversation assigned'
        );
      } else {
        await adminApi.assignConversation(selectedConversation._id, selectedUserId);
      }
      setAssignDialogOpen(false);
      fetchConversations({ preserveDepth: true });
    } catch (error) {
      // Reassign requires Full access to the channel's own module
      // (activity.chatLogs for WhatsApp, instagram for Instagram — see
      // admin.controller.ts's assignConversation / instagram-inbox
      // .controller.ts's assignConversation) — anyone below that tier
      // hitting this now gets the standardized INSUFFICIENT_PERMISSIONS code,
      // routed to the same global dialog.
      if (!isPermissionLockTagged(error)) {
        setActionError(extractErrorMessage(error, 'Failed to assign conversation'));
      }
    } finally {
      setAssigning(false);
    }
  };

  /**
   * Self-assign an unclaimed (or already-mine) conversation. WhatsApp already
   * has a claim endpoint (adminApi.claimConversation) but no UI ever called it
   * — this wires the first claim button for either channel. Instagram is the
   * primary user of this action today (no bot to auto-route it, so a fresh
   * thread sits round-robin-assigned or unassigned until a human claims it);
   * kept channel-dispatching rather than Instagram-only so a future WhatsApp
   * "Claim" affordance is a one-line addition, not a new handler.
   */
  const handleClaimConversation = async (conversationId: string, channel?: ConversationChannel) => {
    setActioningId(conversationId);
    try {
      setActionError('');
      if (channel === 'instagram') {
        await adminApi.claimInstagramConversation(conversationId);
        void refreshRouting(conversationId, 'instagram');
        setActionNotice('Conversation assigned to you');
      } else {
        await adminApi.claimConversation(conversationId);
      }
      fetchConversations({ preserveDepth: true });
    } catch (error) {
      if (!isPermissionLockTagged(error)) {
        // 409 ALREADY_EXISTS ("assigned to someone else") is the one common,
        // expected failure here (a race with another agent's claim) —
        // extractErrorMessage already surfaces the server's own message for it.
        setActionError(extractErrorMessage(error, 'Failed to claim conversation'));
      }
    } finally {
      setActioningId(null);
    }
  };

  /**
   * Clear the assignment — Instagram-only for now (there is no plain
   * "unassign" endpoint for WhatsApp; its equivalent, handBackToBot, ALSO
   * clears the bot-escalation record, which Instagram has none of). Requires
   * Full access to the Instagram module, same tier as reassigning.
   */
  const handleUnassignInstagramConversation = async (conversationId: string) => {
    setActioningId(conversationId);
    try {
      setActionError('');
      await adminApi.unassignInstagramConversation(conversationId);
      // The server $unsets the owner; mirror that here rather than wait for the
      // socket, then re-read the trail so the "Unassigned" entry appears.
      const clear = (c: Conversation): Conversation => {
        const { assignedTo: _prev, ...rest } = c;
        return rest as Conversation;
      };
      setConversations((prev) => prev.map((c) => (c._id === conversationId ? clear(c) : c)));
      setSelectedConversation((prev) => (prev && prev._id === conversationId ? clear(prev) : prev));
      void refreshRouting(conversationId, 'instagram');
      setActionNotice('Conversation unassigned');
      fetchConversations({ preserveDepth: true });
    } catch (error) {
      if (!isPermissionLockTagged(error)) {
        setActionError(extractErrorMessage(error, 'Failed to unassign conversation'));
      }
    } finally {
      setActioningId(null);
    }
  };

  // Channel-dispatching send: one implementation, routed to whichever
  // endpoint owns this conversation's channel. Instagram isn't behind a
  // plan-feature gate yet (no `instagramEnabled` flag exists — see the
  // architecture dossier), so only the WhatsApp path goes through
  // usePlanGate; wrapping both would incorrectly lock Instagram behind a
  // WhatsApp-specific feature check.
  const sendReply = async () => {
    if (!messageInput.trim() || !selectedConversation || sendingMessage) return;
    const text = messageInput.trim();
    const channel = selectedConversation.channel || 'whatsapp';
    setSendingMessage(true);
    setMessageInput('');
    try {
      if (channel === 'instagram') {
        const igsid = selectedConversation.channelIdentity?.value;
        if (!igsid) {
          throw new Error('This conversation has no Instagram recipient id on record.');
        }
        stopTypingIndicator(); // the reply itself is the "I'm done typing" signal
        await adminApi.sendInstagramMessage(igsid, text);
      } else {
        await adminApi.sendMessage(selectedConversation.customerPhone, text);
      }
      await fetchConversationDetail(selectedConversation._id, selectedConversation.channel, { silent: true });
    } catch (error) {
      setMessageInput(text); // restore on error
      if (!showPlanLockIfTagged(error)) {
        console.error('Failed to send message:', error);
      }
    } finally {
      setSendingMessage(false);
    }
  };
  const sendWhatsAppReplyGuarded = guard('whatsappEnabled', sendReply);
  const handleSendMessage = () => {
    if (isArchived) return;
    if (selectedConversation?.channel === 'instagram') {
      void sendReply();
    } else {
      sendWhatsAppReplyGuarded();
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ── Manual template send — inline in the composer, WhatsApp-app-style ─────
  // Only APPROVED, non-AUTHENTICATION templates are offerable here — a PENDING/
  // REJECTED one would just fail at Meta, and AUTHENTICATION templates are
  // reserved for OTP delivery (the backend also enforces this, this just
  // avoids offering a choice that's guaranteed to error).
  const sendableTemplates = Object.values(templateMap).filter(
    (t) => t.status === 'APPROVED' && t.category !== 'AUTHENTICATION'
  );
  const isTemplateMode = activeTemplateName !== '';
  const activeTemplateDef = isTemplateMode ? templateMap[activeTemplateName] : undefined;
  const activeTemplateVariableTokens = templateBodyVariableTokens(activeTemplateDef?.components.body?.text);
  /** The media category the active template's header will accept, if any —
   *  Meta approves the header's TYPE, so an image cannot fill a DOCUMENT slot. */
  const activeTemplateHeaderMediaType = mediaTypeForHeader(activeTemplateDef?.components.header?.type);

  const openTemplateMenu = (e: React.MouseEvent<HTMLElement>) => setTemplateMenuAnchorEl(e.currentTarget);
  const closeTemplateMenu = () => setTemplateMenuAnchorEl(null);

  /** Picking a template swaps the text field for its preview + variable inputs
   *  right there in the composer — no separate panel. */
  const selectTemplate = (name: string) => {
    setTemplateSendError('');
    // The three composer modes are mutually exclusive — picking a template
    // discards a staged file rather than leaving both loaded and ambiguous.
    exitMediaMode();
    setActiveTemplateName(name);
    const template = templateMap[name];
    const tokens = templateBodyVariableTokens(template?.components.body?.text);
    const labels = template?.components.body?.variableLabels || [];
    setActiveTemplateValues(
      tokens.map((_, i) => (selectedConversation ? guessTemplateVariableValue(labels[i], selectedConversation) : ''))
    );
    // Reset rather than carry over — a previously-typed value from a different
    // template is almost never the right value for this one, and if this
    // template doesn't need it at all it'd otherwise linger unseen.
    setActiveButtonUrlValue('');
    // Same reasoning, and sharper: a file chosen for the PREVIOUS template's
    // header may not even be a legal type for this one's.
    setTemplateHeaderAsset(null);
    closeTemplateMenu();
  };

  /** Back to the normal text composer, discarding the in-progress template. */
  const exitTemplateMode = () => {
    setActiveTemplateName('');
    setActiveTemplateValues([]);
    setActiveButtonUrlValue('');
    setTemplateHeaderAsset(null);
    setTemplateSendError('');
  };

  // Re-check whether the live preview's scroll box is actually taller than
  // its capped viewport every time its content could have changed height
  // (new template, media preview finishing its fetch, or the window resizing).
  useEffect(() => {
    const el = previewScrollRef.current;
    if (!el) {
      setPreviewOverflowing(false);
      return;
    }
    const check = () => setPreviewOverflowing(el.scrollHeight > el.clientHeight + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTemplateName, mediaUrls, templateHeaderAsset]);

  const handleSendTemplate = async () => {
    if (!selectedConversation || !activeTemplateName || sendingTemplate) return;
    setSendingTemplate(true);
    setTemplateSendError('');
    setTemplateUnfitToast(null);
    try {
      await adminApi.sendWhatsAppTemplate(
        selectedConversation.customerPhone,
        activeTemplateName,
        activeTemplateDef?.language || 'en',
        activeTemplateValues,
        activeButtonUrlValue.trim() || undefined,
        // Absent = send the template's own header file, exactly as before.
        templateHeaderAsset?._id
      );
      exitTemplateMode();
      await fetchConversationDetail(selectedConversation._id, selectedConversation.channel, { silent: true });
    } catch (error) {
      if (!showPlanLockIfTagged(error)) {
        const code = (error as any)?.response?.data?.error?.code;
        if (code === 'TEMPLATE_MEDIA_UNFIT') {
          // A media-unfit template can't be sent and is fixed in the editor (which
          // re-uploads media without Meta re-approval). Rather than a sticky inline
          // error, surface a lightweight toast with an "Edit template" CTA AND
          // deselect the template so the composer returns to a clean state.
          const lang = activeTemplateDef?.language || 'en';
          const prefix = window.location.pathname.startsWith('/admin') ? '/admin' : '';
          setTemplateUnfitToast({
            editUrl: `${prefix}/whatsapp-templates/${encodeURIComponent(activeTemplateName)}/edit?language=${encodeURIComponent(lang)}`,
          });
          exitTemplateMode();
          // The backend just flagged this template media-unfit — refresh the local
          // list so the picker shows it unselectable immediately (no page refresh).
          reloadTemplateMap();
        } else {
          setTemplateSendError(extractErrorMessage(error, 'Failed to send template'));
        }
      }
    } finally {
      setSendingTemplate(false);
    }
  };

  // ── Manual media send — inline in the composer, same shape as templates ──
  const isMediaMode = activeMedia !== null;

  /**
   * Open one folder (null = the library root).
   *
   * Stale-while-revalidate rather than plain caching: a folder the agent has
   * already visited renders instantly from cache AND is refreshed underneath,
   * so re-walking a tree never shows a spinner, while a file uploaded (or
   * deleted) from the Media page in another tab still turns up. A picker that
   * offers a file the library no longer has is worse than a slow picker — the
   * server would reject the send, but only after the agent chose it.
   */
  const openMediaFolder = async (folderId: string | null) => {
    setMediaFolderId(folderId);
    const key = folderId ?? MEDIA_ROOT_KEY;
    const cached = mediaBrowse[key];
    if (!cached) setLoadingMediaBrowse(true);
    try {
      const result = await adminApi.browseMedia(folderId);
      setMediaBrowse((prev) => ({ ...prev, [key]: result }));
    } catch (error) {
      // A failed BACKGROUND refresh is not worth an error banner — the cached
      // contents are still on screen and still selectable.
      if (!cached && !showPlanLockIfTagged(error)) {
        setMediaSendError(extractErrorMessage(error, 'Could not open that folder'));
      }
    } finally {
      setLoadingMediaBrowse(false);
    }
  };

  /** Nothing is fetched on mount: most inbox sessions never attach a file, and
   *  the library would be a whole extra request per conversation otherwise. */
  const openMediaPicker = (
    e: React.MouseEvent<HTMLElement>,
    mode: 'attach' | 'templateHeader'
  ) => {
    setMediaPickMode(mode);
    setMediaMenuAnchorEl(e.currentTarget);
    setMediaSearch('');
    void openMediaFolder(mediaFolderId);
  };

  const openMediaMenu = (e: React.MouseEvent<HTMLElement>) => openMediaPicker(e, 'attach');

  /** Same browser, different verdict on a click: the chosen file fills the
   *  active template's media header instead of becoming a free-form send. */
  const openTemplateHeaderPicker = (e: React.MouseEvent<HTMLElement>) =>
    openMediaPicker(e, 'templateHeader');

  /** Closing drops the flat search index but keeps the browse cache and the
   *  current folder. So each picker session searches against a fresh list (at
   *  most one fetch, and only if the agent types), while re-opening still lands
   *  in the folder they were last working out of. */
  const closeMediaMenu = () => {
    setMediaMenuAnchorEl(null);
    setMediaSearch('');
    setMediaAllAssets(null);
    // The paperclip is the picker's default caller; leaving it in header mode
    // would make the next click do something the agent didn't ask for.
    setMediaPickMode('attach');
  };

  /** The flat all-folders list backing search — fetched once, lazily, on the
   *  first keystroke. An agent who only ever browses never pays for it. */
  const handleMediaSearchChange = (value: string) => {
    setMediaSearch(value);
    if (!value.trim() || mediaAllAssets !== null || loadingMediaSearch) return;
    setLoadingMediaSearch(true);
    adminApi
      .getMediaAssets()
      .then(setMediaAllAssets)
      .catch(() => setMediaAllAssets([])) // Browsing still works; search just finds nothing.
      .finally(() => setLoadingMediaSearch(false));
  };

  /** Picking a file swaps the text field for a chip + caption input, exactly
   *  the way picking a template swaps it for variable inputs. */
  const selectMedia = (asset: MediaAsset) => {
    // Header mode is NOT a fourth composer mode — the composer stays in
    // template mode and simply learns which file its header will carry, so
    // nothing here touches activeMedia or exits the template.
    if (mediaPickMode === 'templateHeader') {
      setTemplateSendError('');
      setTemplateHeaderAsset(asset);
      closeMediaMenu();
      return;
    }
    setMediaSendError('');
    exitTemplateMode();
    setActiveMedia(asset);
    setMediaCaption('');
    closeMediaMenu();
  };

  /** Back to the normal text composer, discarding the staged file. */
  const exitMediaMode = () => {
    setActiveMedia(null);
    setMediaCaption('');
    setMediaSendError('');
  };

  // ── What the picker is currently showing ─────────────────────────────────
  // Two mutually exclusive views over the same list: a typed query flattens
  // every folder, an empty one shows the current folder's own contents.
  const mediaQuery = mediaSearch.trim().toLowerCase();
  const currentBrowse = mediaBrowse[mediaFolderId ?? MEDIA_ROOT_KEY];
  const visibleFolders = mediaQuery ? [] : currentBrowse?.folders || [];
  const visibleAssets = mediaQuery
    ? (mediaAllAssets || []).filter((a) =>
        `${a.name || ''} ${a.originalName || ''}`.toLowerCase().includes(mediaQuery)
      )
    : currentBrowse?.assets || [];
  const mediaListBusy = mediaQuery ? loadingMediaSearch : loadingMediaBrowse;
  /** The only category the picker will accept right now, or `undefined` when
   *  anything goes. Set only while choosing a template's header file — a
   *  free-form attachment has no such constraint. */
  const headerPickType =
    mediaPickMode === 'templateHeader' ? activeTemplateHeaderMediaType : undefined;

  // Channel-dispatching send, same shape as sendReply above — one
  // implementation, routed to whichever endpoint owns this conversation's
  // channel. The attach button itself is hidden on Instagram until this
  // exists (see its own comment, just below); Phase 2.1 wires it up.
  const sendMedia = async () => {
    if (!selectedConversation || !activeMedia || sendingMedia) return;
    setSendingMedia(true);
    setMediaSendError('');
    try {
      const channel = selectedConversation.channel || 'whatsapp';
      if (channel === 'instagram') {
        const igsid = selectedConversation.channelIdentity?.value;
        if (!igsid) {
          throw new Error('This conversation has no Instagram recipient id on record.');
        }
        await adminApi.sendInstagramMedia(igsid, activeMedia._id, mediaCaption.trim() || undefined);
      } else {
        await adminApi.sendWhatsAppMedia(
          selectedConversation.customerPhone,
          activeMedia._id,
          mediaCaption.trim() || undefined
        );
      }
      exitMediaMode();
      await fetchConversationDetail(selectedConversation._id, selectedConversation.channel, {
        silent: true,
      });
    } catch (error) {
      // The file stays staged on failure — an oversized asset or a window that
      // closed mid-compose is worth re-reading, not silently re-picking.
      if (!showPlanLockIfTagged(error)) {
        setMediaSendError(extractErrorMessage(error, 'Failed to send file'));
      }
    } finally {
      setSendingMedia(false);
    }
  };
  const sendMediaGuarded = guard('whatsappEnabled', sendMedia);

  /** The composer's single Send arrow — text mode sends the typed message,
   *  template mode sends the selected template, media mode sends the file. */
  const handleComposerSend = () => {
    // Belt-and-braces match to the composer's disabled state — the Send
    // arrow shouldn't be clickable for an archived contact regardless of
    // mode. handleSendMessage carries its own matching guard, since the
    // Enter-key path (handleInputKeyDown) calls it directly rather than
    // going through here.
    if (isArchived) return;
    if (isMediaMode) {
      sendMediaGuarded();
    } else if (isTemplateMode) {
      void handleSendTemplate();
    } else {
      handleSendMessage();
    }
  };

  // ── Conversation list panel ───────────────────────────────────────────────
  // Search and tag filtering both happen server-side now (see the debounced
  // fetchConversations effect above) — `conversations` is already the filtered set.

  // Quick-view tabs (All / Mine / Needs Human / Human) — one-click presets over
  // the same assignedToFilter/routingFilter state the filter popover's Selects
  // already drive, rather than a separate parallel filter axis. Each tab is
  // mutually exclusive, so picking one resets the other dimension.
  //
  // No separate "Unassigned" tab: now that 'needs_human' means exactly
  // routing.state:'pending_human' (see admin.controller.ts), "unassigned" and
  // "needs human" describe the same set of conversations in this hybrid-
  // routing context — pending_human conversations have no assignee by
  // construction. "Human" (any assignee, not just yours) fills that slot
  // instead, giving an owner visibility into the team's total human workload
  // that neither "Mine" nor "Needs Human" cover.
  // 'flagged' sits alongside the routing views rather than under them: it is a
  // triage queue fed by automations (the Flag node), orthogonal to who/what is
  // currently handling the chat — a flagged chat can be bot-driven, escalated,
  // or already claimed. Selecting it clears the routing filter for the same
  // reason "Mine" does: these tabs are single-select by design.
  //
  // 2026-09-28 (product decision): the tabs are All / Assigned to me / Flagged
  // / Instagram. "Needs Human" and "Human" are HIDDEN for now, not removed —
  // the routing filter still exists in the Filters popover, and re-adding
  // either tab is one line in QUICK_VIEWS below. "Instagram" is a preset over
  // the existing channel filter, shown only when Instagram is reachable.
  type QuickView = 'all' | 'mine' | 'flagged' | 'instagram';
  const activeQuickView: QuickView | null = flaggedOnly
    ? 'flagged'
    : assignedToFilter === user?.id
      ? 'mine'
      : channelFilter === 'instagram'
        ? 'instagram'
        : routingFilter === 'all' && assignedToFilter === ASSIGNED_TO_ALL && channelFilter === 'all'
          ? 'all'
          : // A combination set from the Filters popover that no tab represents
            // highlights no tab, rather than falsely claiming "All".
            null;
  const setQuickView = (view: QuickView) => {
    setAssignedToFilter(view === 'mine' ? user?.id || ASSIGNED_TO_ALL : ASSIGNED_TO_ALL);
    setRoutingFilter('all');
    setFlaggedOnly(view === 'flagged');
    setChannelFilter(view === 'instagram' ? 'instagram' : 'all');
  };
  const QUICK_VIEWS: Array<{ value: QuickView; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'mine', label: 'Assigned to me' },
    { value: 'flagged', label: 'Flagged' },
    ...(instagramAvailable ? [{ value: 'instagram' as const, label: 'Instagram' }] : []),
  ];

  const listPanel = (
    <Box
      sx={{
        width: { xs: '100%', md: 340, lg: 380 },
        flexShrink: 0,
        display: { xs: isMobile && showChat ? 'none' : 'flex', md: 'flex' },
        flexDirection: 'column',
        // The page's own root Box (this component's final return) carries a
        // negative margin that cancels AdminLayout's top/bottom page padding,
        // so the full viewport height is genuinely available here — no
        // offset to subtract.
        height: { md: '100vh' },
        bgcolor: 'background.paper',
        overflow: 'hidden',
        border: { xs: 'none', md: '1px solid #e2e8f0' },
        boxShadow: { md: '0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.06)' },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}
        >
          <Typography variant="h6" fontWeight={700}>
            Chat Logs
          </Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => fetchConversations()} disabled={loadingList}>
                <Refresh sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Filters">
              <IconButton
                size="small"
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              >
                <Badge
                  variant="dot"
                  color="primary"
                  invisible={
                    tagFilter.length === 0 &&
                    statusFilter === 'all' &&
                    !windowActiveFilter &&
                    assignedToFilter === ASSIGNED_TO_ALL &&
                    channelFilter === 'all' &&
                    routingFilter === 'all' &&
                    !flaggedOnly &&
                    dateAddedFilter === 'all' &&
                    sortFilter === 'recent'
                  }
                >
                  <FilterList sx={{ fontSize: 18 }} />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {/* Conversation-scoped "saved view" filters — the same fields (status/tags/
            assignedTo) the Contacts page's segment filters use, narrowed to what's
            meaningful for a live inbox view rather than contact-history/audience
            concepts like date-added or quality risk. */}
        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { borderRadius: 2, boxShadow: '0 8px 24px rgba(15,23,42,0.12)' } } }}
        >
          <Box sx={{ width: 300, display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.25,
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Filters
              </Typography>
              <IconButton size="small" onClick={() => setFilterAnchorEl(null)} aria-label="Close filters">
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Hidden entirely when Instagram isn't reachable: WhatsApp is
                then the only channel this inbox can show, so "All channels"
                and "WhatsApp" would be the same list and an Instagram option
                could only ever come back empty. Offering it was the other
                half of the plan-gating bug — selecting it used to fire the
                guaranteed-403 Instagram fetch (red Snackbar), and once that
                fetch was correctly skipped it would instead have shown a
                silently empty inbox, which reads as data loss. */}
            {instagramAvailable && (
              <FormControl size="small" fullWidth>
                <InputLabel id="chatlogs-channel-label">Channel</InputLabel>
                <Select
                  labelId="chatlogs-channel-label"
                  label="Channel"
                  value={channelFilter}
                  onChange={(e) => setChannelFilter(e.target.value as 'all' | 'whatsapp' | 'instagram')}
                >
                  <MenuItem value="all">All channels</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="instagram">Instagram</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl size="small" fullWidth>
              <InputLabel id="chatlogs-status-label">Status</InputLabel>
              <Select
                labelId="chatlogs-status-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="chatlogs-date-added-label">Date added</InputLabel>
              <Select
                labelId="chatlogs-date-added-label"
                label="Date added"
                value={dateAddedFilter}
                onChange={(e) => setDateAddedFilter(e.target.value as DateAddedPreset)}
              >
                {DATE_ADDED_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {dateAddedFilter !== 'all' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  When the chat first came in — not its latest activity.
                </Typography>
              )}
            </FormControl>

            {/* Only the custom preset needs boxes. Rendered as a sibling rather
                than inside the FormControl above so the Select's own label/
                outline sizing isn't thrown off by children it doesn't expect. */}
            {dateAddedFilter === 'custom' && (
              <Box>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={dateAddedFrom}
                    onChange={(e) => setDateAddedFrom(e.target.value)}
                    error={customRangeInverted}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={dateAddedTo}
                    onChange={(e) => setDateAddedTo(e.target.value)}
                    error={customRangeInverted}
                    fullWidth
                  />
                </Stack>
                {/* An inverted range is a guaranteed-empty result, which reads
                    as "no chats" rather than "you typed the dates backwards" —
                    so it gets said out loud. Leaving one box empty is NOT an
                    error: that is an open-ended range, and it filters. */}
                {customRangeInverted ? (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    “From” is after “To” — no chats can match this range.
                  </Typography>
                ) : (
                  !dateAddedFrom &&
                  !dateAddedTo && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      Pick either box — one on its own is an open-ended range.
                    </Typography>
                  )
                )}
              </Box>
            )}

            <FormControlLabel
              sx={{ ml: 0, justifyContent: 'space-between' }}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={windowActiveFilter}
                  onChange={(e) => setWindowActiveFilter(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Active 24h window</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Only contacts you can still free-form reply to
                  </Typography>
                </Box>
              }
            />

            <FormControl size="small" fullWidth>
              <InputLabel id="chatlogs-assigned-label">Assigned to</InputLabel>
              <Select
                labelId="chatlogs-assigned-label"
                label="Assigned to"
                value={assignedToFilter}
                onChange={(e) => setAssignedToFilter(e.target.value)}
              >
                <MenuItem value={ASSIGNED_TO_ALL}>Anyone</MenuItem>
                <MenuItem value={ASSIGNED_TO_UNASSIGNED}>Unassigned</MenuItem>
                {teamMembers.map((m) => (
                  <MenuItem key={m._id} value={m._id}>
                    {m.firstName} {m.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="chatlogs-routing-label">Routing</InputLabel>
              <Select
                labelId="chatlogs-routing-label"
                label="Routing"
                value={routingFilter}
                onChange={(e) => setRoutingFilter(e.target.value as RoutingFilter)}
              >
                {ROUTING_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {routingFilter !== 'all' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Applies to WhatsApp only — Instagram doesn't support routing yet.
                </Typography>
              )}
            </FormControl>

            {/*
              Deliberately its own toggle rather than a fifth entry in the
              Routing dropdown above. That dropdown is single-select over
              routing.state (bot / needs-human / human), and "flagged" is not a
              routing state — it's an orthogonal review mark an automation
              writes. A chat can be flagged AND bot-driven AND assigned all at
              once, so folding it into Routing would both mis-model it and make
              those combinations unexpressible. Mirrors the "Active 24h window"
              boolean above, which is orthogonal for the same reason.
            */}
            <FormControlLabel
              sx={{ ml: 0, justifyContent: 'space-between' }}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={flaggedOnly}
                  onChange={(e) => setFlaggedOnly(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FlagIcon sx={{ fontSize: 14, color: FLAG_ACCENT }} />
                    Flagged for review
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Only chats an automation marked for review
                  </Typography>
                </Box>
              }
            />

            <FormControl size="small" fullWidth>
              <InputLabel id="chatlogs-sort-label">Sort</InputLabel>
              <Select
                labelId="chatlogs-sort-label"
                label="Sort"
                value={sortFilter}
                onChange={(e) => setSortFilter(e.target.value as SortFilter)}
              >
                {SORT_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {sortFilter === 'incoming' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Applies to WhatsApp only — sorted by customer replies, immune to bulk
                  outbound/campaign/automation sends.
                </Typography>
              )}
            </FormControl>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Tags
              </Typography>
              <Autocomplete
                multiple
                size="small"
                options={allTags}
                value={tagFilter}
                onChange={(_, value) => setTagFilter(value)}
                renderTags={(value, getTagProps) =>
                  value.map((tag, index) => (
                    <Chip label={tag} size="small" {...getTagProps({ index })} key={tag} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...(params as any)}
                    placeholder={allTags.length ? 'Select tags…' : 'No tags yet'}
                  />
                )}
              />
            </Box>

            {(tagFilter.length > 0 ||
              statusFilter !== 'all' ||
              windowActiveFilter ||
              assignedToFilter !== ASSIGNED_TO_ALL ||
              channelFilter !== 'all' ||
              routingFilter !== 'all' ||
              flaggedOnly ||
              dateAddedFilter !== 'all' ||
              sortFilter !== 'recent') && (
              <Button
                size="small"
                onClick={() => {
                  setTagFilter([]);
                  setStatusFilter('all');
                  setWindowActiveFilter(false);
                  setAssignedToFilter(ASSIGNED_TO_ALL);
                  setChannelFilter('all');
                  setRoutingFilter('all');
                  setFlaggedOnly(false);
                  // Clears the typed range too, unlike simply switching the
                  // preset away from 'custom' — "Clear all" means nothing is
                  // left armed to come back on the next visit to this menu.
                  setDateAddedFilter('all');
                  setDateAddedFrom('');
                  setDateAddedTo('');
                  setSortFilter('recent');
                }}
              >
                Clear all
              </Button>
            )}
            </Box>
          </Box>
        </Popover>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
          {QUICK_VIEWS.map((qv) => {
            const active = activeQuickView === qv.value;
            // The Flagged tab wears the flag accent instead of the theme
            // primary, so the tab, the row chips it filters to, and the
            // thread banner all read as one thing. Every other tab is
            // unchanged.
            const isFlag = qv.value === 'flagged';
            return (
              <Chip
                key={qv.value}
                label={qv.label}
                size="small"
                clickable
                {...(isFlag
                  ? {
                      icon: (
                        <FlagIcon
                          sx={{
                            fontSize: '0.8rem !important',
                            color: `${active ? '#fff' : FLAG_ACCENT} !important`,
                          }}
                        />
                      ),
                    }
                  : {})}
                {...(qv.value === 'instagram'
                  ? { icon: <InstagramLogo size={13} sx={{ ml: '6px !important' }} /> }
                  : {})}
                onClick={() => setQuickView(qv.value)}
                {...(isFlag || !active ? {} : { color: 'primary' as const })}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  ...(isFlag
                    ? active
                      ? { bgcolor: FLAG_ACCENT, color: '#fff', '&:hover': { bgcolor: '#be123c' } }
                      : { borderColor: FLAG_ACCENT, color: FLAG_ACCENT }
                    : {}),
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* List */}
      <Box ref={listContainerRef} onScroll={handleListScroll} sx={{ flex: 1, overflowY: 'auto' }}>
        {loadingList ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                <Skeleton variant="circular" width={42} height={42} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="60%" height={16} sx={{ mb: 0.75 }} />
                  <Skeleton width="90%" height={14} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : conversations.length === 0 ? (
          <EmptyState
            icon={<ChatBubbleOutline />}
            title="No conversations"
            description={
              // A date range that matches nothing looks identical to an inbox
              // that has never received a chat, so it gets named explicitly —
              // otherwise "Conversations will appear here once chats start"
              // reads as data loss to an admin who simply picked Today.
              dateAddedApplied
                ? `No chats were added in this ${dateAddedFilter === 'custom' ? 'range' : 'period'}`
                : search || tagFilter.length > 0
                  ? 'Try a different search term or tag'
                  : 'Conversations will appear here once chats start'
            }
          />
        ) : (
          <List sx={{ p: 0 }}>
            {conversations.map((conv) => {
              const isSelected = selectedConversation?._id === conv._id;
              const preview = conv.lastMessage || '';
              const isTemplate = TEMPLATE_RE.test(preview);
              const unread = conv.unreadCount || 0;
              return (
                <ListItem
                  key={conv._id}
                  disablePadding
                  sx={{ borderBottom: '1px solid #f1f5f9' }}
                >
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => {
                      setSelectedConversation(conv);
                      // Optimistic — the backend resets unreadCount and
                      // rebroadcasts conversation:unread:0 on open, but that
                      // round trip shouldn't be what clears this row visually.
                      if (unread > 0) {
                        setConversations((prev) =>
                          prev.map((c) => (c._id === conv._id ? { ...c, unreadCount: 0 } : c))
                        );
                      }
                    }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&.Mui-selected': {
                        bgcolor: 'rgba(79,70,229,0.06)',
                        borderLeft: '3px solid #4f46e5',
                      },
                      '&.Mui-selected:hover': { bgcolor: 'rgba(79,70,229,0.09)' },
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', gap: 1.5, width: '100%', alignItems: 'flex-start' }}
                    >
                      <Avatar
                        // Instagram profile picture when Meta's User Profile API resolved one
                        // (see instagram.service.ts's processInboundEvent) — MUI Avatar falls
                        // back to the initial below automatically if the (ephemeral, per Meta
                        // docs) URL has since expired and fails to load.
                        {...(conv.profilePictureUrl ? { src: conv.profilePictureUrl } : {})}
                        sx={{
                          width: 42,
                          height: 42,
                          flexShrink: 0,
                          bgcolor: isSelected ? '#4f46e5' : '#e2e8f0',
                          color: isSelected ? '#fff' : '#64748b',
                          fontSize: '1rem',
                          fontWeight: 700,
                        }}
                      >
                        {contactInitial(conv)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                            {conv.channel === 'instagram' ? (
                              <InstagramLogo size={14} />
                            ) : (
                              <WhatsApp sx={{ fontSize: 13, color: '#25D366', flexShrink: 0 }} />
                            )}
                            <Typography
                              variant="body2"
                              fontWeight={unread > 0 ? 700 : 600}
                              noWrap
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {conv.customerName || formatContactIdentity(conv)}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ flexShrink: 0, ml: 1, fontSize: '0.6875rem' }}
                          >
                            {formatTimeAgo(conv.lastMessageAt)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                            {isTemplate && (
                              <Article sx={{ fontSize: 11, color: '#4f46e5', flexShrink: 0 }} />
                            )}
                            <Typography
                              variant="caption"
                              color={unread > 0 ? 'text.primary' : 'text.secondary'}
                              noWrap
                              sx={{ lineHeight: 1.5, fontWeight: unread > 0 ? 600 : 400 }}
                            >
                              {isTemplate
                                ? formatTemplateName(
                                    preview.match(TEMPLATE_RE)?.[1] || 'Template'
                                  )
                                : preview}
                            </Typography>
                          </Box>
                          {unread > 0 && (
                            <Box
                              sx={{
                                flexShrink: 0,
                                minWidth: 18,
                                height: 18,
                                px: 0.5,
                                borderRadius: '9px',
                                bgcolor: '#4f46e5',
                                color: '#fff',
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {unread > 99 ? '99+' : unread}
                            </Box>
                          )}
                        </Box>
                        {conv.channel !== 'instagram' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <RoutingStateChip
                              state={conv.routing?.state}
                              assignedToName={formatAssigneeName(conv.assignedTo)}
                            />
                            {conv.flagged && <FlaggedChip title={flagLabel(conv)} />}
                            {(conv.routing?.state ?? 'bot') !== 'human' && (
                              <Tooltip title="Pause automated replies and take over this chat">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={actioningId === conv._id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetBotState(conv._id, 'stop');
                                    }}
                                    sx={{ p: 0.25 }}
                                  >
                                    {actioningId === conv._id ? (
                                      <CircularProgress size={12} />
                                    ) : (
                                      <AssignmentInd sx={{ fontSize: 14 }} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                        {conv.tags && conv.tags.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            {conv.tags.map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.625rem', '& .MuiChip-label': { px: 0.75 } }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
            {/* Infinite-scroll footer. Rendered inside the scroll container so
                it is itself what the admin scrolls into — a spinner while the
                next page is in flight, a tappable fallback once it is not.
                The fallback matters: on a very short viewport (or if a page
                comes back mostly filtered out) the list may not be tall enough
                to emit another scroll event, which would strand the admin with
                more rows available and no way to ask for them. */}
            {hasMoreConversations ? (
              <Box
                sx={{
                  py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                {loadingMoreList ? (
                  <>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Loading more conversations…
                    </Typography>
                  </>
                ) : (
                  <Button size="small" onClick={loadMoreConversations}>
                    Load more
                  </Button>
                )}
              </Box>
            ) : (
              conversations.length > CONVERSATION_PAGE_SIZE && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', textAlign: 'center', py: 2 }}
                >
                  You&rsquo;ve reached the end &middot; {conversations.length} conversations
                </Typography>
              )
            )}
          </List>
        )}
      </Box>
    </Box>
  );

  // ── Chat view panel ───────────────────────────────────────────────────────

  const windowStatus = getWindowStatus(windowDetail.windowExpiresAt);
  const canReply = windowStatus.status === 'open';
  // Kept separate from `canReply`: archived must block even a template send,
  // which is exactly the mechanism that bypasses a closed 24h window — merging
  // the two would risk the window-closed messaging firing for the wrong reason.
  // Backend already hard-rejects a send here (checkContactSendGuard); this just
  // stops the agent from typing into a composer that's certain to 400.
  const isArchived = selectedConversation?.status === 'archived';

  // Feeds ContactPanel's "Conversation" section — merged in from what used to
  // be the chat header's own separate "Details" tab (removed; see ContactPanel
  // .tsx). Pre-formatted here since the panel is a plain renderer with no
  // date-math/formatting logic of its own.
  const contactPanelConversationDetails = selectedConversation
    ? {
        messageCount: selectedConversation.messageCount,
        status: selectedConversation.status,
        lastActivity: formatTimeAgo(selectedConversation.lastMessageAt),
        lastCustomerReply: windowDetail.lastInboundMessageAt
          ? formatTimeAgo(windowDetail.lastInboundMessageAt)
          : 'No reply yet',
        windowLabel:
          windowStatus.status === 'open'
            ? `Open · ${formatRemaining(windowStatus.remainingMs)}`
            : windowStatus.status === 'expired'
              ? 'Expired'
              : 'Not opened',
        ...(windowStatus.status === 'open'
          ? { windowColor: 'success' as const }
          : windowStatus.status === 'expired'
            ? { windowColor: 'warning' as const }
            : {}),
      }
    : undefined;

  // Feeds ContactPanel's "Routing history" section — same source data the old
  // Details tab's table read, newest first (unchanged ordering). Previous
  // holders are computed on the chronological array BEFORE reversing —
  // computePreviousHolders reads "who held it right before event[i]", which
  // only makes sense walking oldest → newest.
  const routingHistoryPreviousHolders = computePreviousHolders(routingHistory);
  const contactPanelRoutingHistory: ContactPanelRoutingEvent[] = routingHistory
    .map((event, i) => ({ event, previousHolder: routingHistoryPreviousHolders[i] }))
    .reverse()
    .map(({ event, previousHolder }, index) => {
      const { color, label, detail } = describeRoutingEvent(event, previousHolder);
      return {
        id: String(index),
        color,
        label,
        timeAgo: formatTimeAgo(event.occurredAt),
        ...(detail !== undefined ? { detail } : {}),
      };
    });

  // A staged file must never outlive the reason it can be sent. Declared here
  // rather than beside the other media handlers because it reads `canReply`,
  // which is only derived this far down.
  //
  //  - Window closed → free-form media is undeliverable, full stop. Meta allows
  //    it ONLY inside the 24h window. The attach button disables in that state
  //    (a template with a media header is the way to send a file then — see
  //    templateHeaderAsset), so without this the composer would strand itself
  //    in a mode with no way out.
  //  - Conversation switched → a brochure picked for one customer must not
  //    still be armed when the agent moves to the next. The Send arrow sits in
  //    the same place either way, which is exactly how a mis-send happens.
  useEffect(() => {
    setActiveMedia(null);
    setMediaCaption('');
    setMediaSendError('');
    setMediaMenuAnchorEl(null);
  }, [selectedConversation?._id, canReply]);

  const selectedRoutingState: ConversationRoutingState = selectedConversation?.routing?.state ?? 'bot';
  const isPaused = selectedConversation?.channel !== 'instagram' && selectedRoutingState !== 'bot';
  // Soft nudge, not a lock — every researched platform (Chatwoot, Intercom) keeps
  // reply access at the inbox level regardless of assignment, restricting only
  // reassignment itself. This just keeps accountability visible: anyone can
  // still reply, they just can't miss whose conversation it is.
  const notYourAssignment =
    selectedConversation?.channel !== 'instagram' &&
    selectedRoutingState === 'human' &&
    !!selectedConversation?.assignedTo?._id &&
    selectedConversation.assignedTo._id !== user?.id;

  // One line explaining why the bot stopped replying. Reads the current-state escalation
  // summary (not routing.history) because that's what the list payload already carries —
  // so this renders instantly on selection, with no detail fetch to wait for.
  const escalationInfo = selectedConversation?.routing?.escalation;
  const handoffReason = ((): string | null => {
    if (!escalationInfo) return null;
    if (escalationInfo.triggerType === 'explicit_request') {
      const phrase = escalationInfo.context?.matchedPhrase;
      return phrase
        ? `Handed over because the customer said “${phrase}”.`
        : 'Handed over because the customer asked for a person.';
    }
    if (escalationInfo.triggerType === 'low_confidence') {
      return 'Handed over because the bot answered with low confidence twice in a row.';
    }
    return null;
  })();

  const chatPanel = (
    <Box
      sx={{
        flex: 1,
        display: { xs: isMobile && !showChat ? 'none' : 'flex', md: 'flex' },
        flexDirection: 'column',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        // No left border: the columns now sit flush against each other (no
        // gap), so the list panel's own right border is the seam between them.
        border: { xs: 'none', md: '1px solid #e2e8f0' },
        borderLeft: { xs: 'none', md: 0 },
        boxShadow: { md: '0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.06)' },
        // The page's own root Box (this component's final return) carries a
        // negative margin that cancels AdminLayout's top/bottom page padding,
        // so the full viewport height is genuinely available here — no
        // offset to subtract.
        height: { md: '100vh' },
      }}
    >
      {!selectedConversation ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState
            icon={<ChatBubbleOutline />}
            title="Select a conversation"
            description="Choose a conversation from the list to view the chat history"
          />
        </Box>
      ) : (
        <>
          {/* ── Header ─────────────────────────────────────────────────────── */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: '#fff',
              flexShrink: 0,
            }}
          >
            {isMobile && (
              <IconButton size="small" onClick={() => setShowChat(false)}>
                <ArrowBack sx={{ fontSize: 20 }} />
              </IconButton>
            )}
            {/* Avatar opens the profile popover (WhatsApp/Instagram-style
                "who is this"), as does the name below — both keyboard-reachable. */}
            <Box
              component="button"
              type="button"
              onClick={() => setProfileDialogOpen(true)}
              title="View profile"
              sx={{ p: 0, border: 'none', bgcolor: 'transparent', cursor: 'pointer', flexShrink: 0, borderRadius: '50%', lineHeight: 0 }}
            >
            {selectedConversation.channel === 'instagram' ? (
              // The contact's own photo, with the Instagram mark as a badge —
              // same treatment as the contact panel. Falls back to the initial
              // (MUI Avatar's broken-image fallback) until a photo is stored.
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Box sx={{ bgcolor: '#fff', borderRadius: '5px', p: '1.5px', display: 'flex', lineHeight: 0 }}>
                    <InstagramLogo size={13} />
                  </Box>
                }
                sx={{ flexShrink: 0 }}
              >
                <Avatar
                  {...(selectedConversation.profilePictureUrl
                    ? { src: selectedConversation.profilePictureUrl }
                    : {})}
                  alt={selectedConversation.customerName || 'Instagram contact'}
                  sx={{ width: 38, height: 38, bgcolor: '#4f46e5', fontSize: '0.95rem', fontWeight: 700 }}
                >
                  {contactInitial(selectedConversation)}
                </Avatar>
              </Badge>
            ) : (
              <Avatar
                {...(selectedConversation.profilePictureUrl
                  ? { src: selectedConversation.profilePictureUrl }
                  : {})}
                sx={{ width: 38, height: 38, flexShrink: 0, bgcolor: '#25D366' }}
              >
                <WhatsApp sx={{ fontSize: 20 }} />
              </Avatar>
            )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                component="button"
                type="button"
                onClick={() => setProfileDialogOpen(true)}
                sx={{ p: 0, border: 'none', bgcolor: 'transparent', cursor: 'pointer', display: 'block', maxWidth: '100%', textAlign: 'left', '&:hover': { textDecoration: 'underline' } }}
              >
                <Typography variant="body1" fontWeight={700} noWrap>
                  {selectedConversation.customerName || formatContactIdentity(selectedConversation)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <StatusChip status={selectedConversation.status} />
                {selectedConversation.channel !== 'instagram' ? (
                  <RoutingStateChip
                    state={selectedRoutingState}
                    assignedToName={formatAssigneeName(selectedConversation.assignedTo)}
                  />
                ) : (
                  // Instagram has no bot state, so its chip is ownership only —
                  // the same "who has this chat" answer WhatsApp's chip gives.
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      selectedConversation.assignedTo
                        ? `Assigned · ${formatAssigneeName(selectedConversation.assignedTo) || 'teammate'}`
                        : 'Unassigned'
                    }
                    sx={{
                      height: 22,
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      ...(selectedConversation.assignedTo
                        ? { borderColor: '#c7d2fe', color: '#4338ca', bgcolor: '#eef2ff' }
                        : { borderColor: '#e2e8f0', color: '#64748b' }),
                    }}
                  />
                )}
                {selectedConversation.flagged && (
                  <FlaggedChip title={flagLabel(selectedConversation)} />
                )}
                {selectedConversation.channel !== 'instagram' && (
                  <AutomationStateChips conv={selectedConversation} agentsList={agents} />
                )}
                <AdSourceChip conv={selectedConversation} ctwaAttribution={ctwaAttribution} />
                <Typography variant="caption" color="text.secondary">
                  {selectedConversation.messageCount} messages
                </Typography>
                {selectedConversation.tags?.map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
            {/* Desktop already has every one of these actions in the sidebar
                (Export/Archive on the identity card, Stop bot/Assign/Resume in
                Assigned to) — this trigger is only load-bearing on mobile,
                where ContactPanel doesn't render at all. */}
            {isMobile && (
              <Tooltip title="More options">
                <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                  <MoreVert sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Flag review moved to the sidebar (ContactPanel) — a contact-level
              operational fact, same category as Assigned to/Tags/Notes, not
              something that needs to sit on top of the live conversation. */}

          {/* One status bar for what's really one fact: routing.state !== 'bot'.
              notYourAssignment can only be true when isPaused already is (it
              requires routing.state==='human', a subset of isPaused's !=='bot'),
              so this used to always double up with a second banner below the
              thread — folded in here instead as a secondary line. Colors mirror
              RoutingStateChip's existing convention (amber=pending_human,
              indigo=human) rather than a third palette, and the restrained
              left-accent-bar style matches how the flag banner used to read. */}
          {isPaused && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 2,
                py: 0.75,
                borderLeft: `3px solid ${selectedRoutingState === 'pending_human' ? '#e65100' : '#3730a3'}`,
                bgcolor: selectedRoutingState === 'pending_human' ? '#fff3e0' : '#eef2ff',
              }}
            >
              <Person
                sx={{
                  fontSize: 16,
                  flexShrink: 0,
                  mt: 0.125,
                  color: selectedRoutingState === 'pending_human' ? '#e65100' : '#3730a3',
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 600,
                    color: selectedRoutingState === 'pending_human' ? '#7c2d12' : '#312e81',
                  }}
                >
                  {selectedRoutingState === 'pending_human'
                    ? 'AI is paused — this conversation is waiting for a human agent to take over.'
                    : `AI is paused — owned by ${formatAssigneeName(selectedConversation.assignedTo) || 'a human agent'}.`}
                </Typography>
                {/* Why the bot handed over, right where whoever picks the chat up is
                    already looking — the question this answers ("why is this mine?") used
                    to have no answer at all once tenants started editing the phrases. */}
                {handoffReason && (
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                    {handoffReason}
                  </Typography>
                )}
                {canReply && notYourAssignment && (
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                    Your reply will be sent under your own name.
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          <>
              {/* Messages area — WhatsApp wallpaper background */}
              <Box
                ref={messagesContainerRef}
                onScroll={handleMessagesScroll}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 2,
                  // WhatsApp wallpaper color
                  bgcolor: '#E5DDD5',
                  backgroundImage:
                    'radial-gradient(circle, rgba(0,0,0,0.04) 1px, transparent 1px)',
                  backgroundSize: '24px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {loadingOlderMessages && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={18} />
                  </Box>
                )}
                {loadingMsgs ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        justifyContent: i % 2 === 0 ? 'flex-start' : 'flex-end',
                      }}
                    >
                      <Skeleton
                        variant="rounded"
                        width={180 + (i % 3) * 60}
                        height={52}
                        sx={{ borderRadius: 2 }}
                      />
                    </Box>
                  ))
                ) : messages.length === 0 ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{ px: 3, py: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.85)' }}
                    >
                      <Typography variant="body2" color="text.secondary" align="center">
                        No messages yet
                      </Typography>
                    </Paper>
                  </Box>
                ) : (
                  messages.map((msg, i) => {
                    // user = customer (LEFT), assistant = bot outgoing (RIGHT)
                    const isOutgoing = msg.role === 'assistant';
                    const template = parseTemplateMessage(msg.content);
                    const confidence = msg.metadata?.confidence ?? 0;
                    const senderInfo = isOutgoing
                      ? resolveSenderInfo(
                          msg.metadata?.source,
                          msg.metadata?.selectedAgent,
                          msg.metadata?.sentBy,
                          agents,
                          teamMembers,
                          msg.metadata?.campaignName,
                          msg.metadata?.workflow,
                          msg.metadata?.workflowName,
                          msg.metadata?.clientName
                        )
                      : null;

                    return (
                      <Box
                        key={msg._id || i}
                        sx={{
                          display: 'flex',
                          justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
                          alignItems: 'flex-end',
                          gap: 0.75,
                        }}
                      >
                        {/* Customer avatar (left side only) */}
                        {!isOutgoing && (
                          <Avatar
                            sx={{
                              width: 26,
                              height: 26,
                              flexShrink: 0,
                              mb: 0.25,
                              bgcolor: '#64748b',
                              fontSize: '0.6875rem',
                              fontWeight: 700,
                            }}
                          >
                            {contactInitial(selectedConversation)}
                          </Avatar>
                        )}

                        {template ? (
                          /* ── Template: card + timestamp stacked in a column ── */
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              maxWidth: '72%',
                              alignItems: isOutgoing ? 'flex-end' : 'flex-start',
                              gap: 0.5,
                            }}
                          >
                            <TemplateCard
                              parsed={template}
                              fullTemplate={templateMap[template.name]}
                              // A template message carries an attachment only
                              // when the sender picked a file for its header
                              // (see whatsapp.controller's sendTemplateMessage).
                              // That file is what this customer received, so it
                              // outranks the template definition's stock media —
                              // which is all the transcript could show before,
                              // and would now be showing the wrong file.
                              mediaUrl={msg.attachments?.[0]?.s3Url ?? mediaUrls[template.name]}
                              carouselMediaUrls={carouselMediaUrls[template.name]}
                              actualParams={msg.metadata?.templateParams}
                              fallbackName={selectedConversation.customerName}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.5 }}>
                              {isOutgoing && senderInfo ? (
                                <MessageInfoTrigger
                                  time={formatTimeAgo(msg.createdAt)}
                                  status={msg.status}
                                  sendError={msg.metadata?.sendError}
                                  sender={senderInfo}
                                  fullTimestamp={new Date(msg.createdAt).toLocaleString()}
                                  templateName={template.name}
                                  templateLanguage={template.lang}
                                  {...(msg.metadata?.toolsUsed ? { toolsUsed: msg.metadata.toolsUsed } : {})}
                                  {...(msg.metadata?.model ? { model: msg.metadata.model } : {})}
                                />
                              ) : (
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}
                                >
                                  {formatTimeAgo(msg.createdAt)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ) : (
                          /* ── Plain text: styled bubble ── */
                          <Paper
                            elevation={0}
                            sx={{
                              maxWidth: '72%',
                              px: 1.5,
                              pt: 1.25,
                              pb: 0.75,
                              bgcolor: isOutgoing ? '#eef2ff' : '#FFFFFF',
                              color: 'text.primary',
                              borderRadius: isOutgoing
                                ? '12px 2px 12px 12px'
                                : '2px 12px 12px 12px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                            }}
                          >
                            <MessageBody
                              messageType={msg.messageType}
                              content={msg.content}
                              attachments={msg.attachments}
                              location={msg.location}
                              contacts={msg.contacts}
                              order={msg.order}
                              system={msg.system}
                              interactive={msg.interactive}
                              providerErrors={msg.providerErrors}
                              storyContext={msg.metadata?.storyContext}
                              quickReplyPayload={msg.metadata?.quickReplyPayload}
                              isUnsupported={msg.metadata?.isUnsupported}
                              renderText={(text) => (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.55,
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {renderWhatsAppText(text)}
                                </Typography>
                              )}
                            />
                            <MessageReactions reactions={msg.reactions} />
                            <MessageActions tools={msg.metadata?.toolsUsed} onOpenAction={openActionRecord} />
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                mt: 0.5,
                              }}
                            >
                              {isOutgoing && senderInfo ? (
                                <MessageInfoTrigger
                                  time={formatTimeAgo(msg.createdAt)}
                                  status={msg.status}
                                  sendError={msg.metadata?.sendError}
                                  sender={senderInfo}
                                  fullTimestamp={new Date(msg.createdAt).toLocaleString()}
                                  confidence={confidence}
                                  {...(msg.metadata?.matchedPhrase ? { matchedPhrase: msg.metadata.matchedPhrase } : {})}
                                  {...(msg.metadata?.toolsUsed ? { toolsUsed: msg.metadata.toolsUsed } : {})}
                                  {...(msg.metadata?.model ? { model: msg.metadata.model } : {})}
                                />
                              ) : (
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}
                                >
                                  {formatTimeAgo(msg.createdAt)}
                                  {!isOutgoing && confidence > 0 && ` · ${confidence.toFixed(2)}`}
                                </Typography>
                              )}
                            </Box>
                          </Paper>
                        )}

                        {/* Sender avatar (right side only) — AI/agent, human team
                            member, or system, per senderInfo above. */}
                        {isOutgoing && senderInfo && (
                          <Tooltip title={senderInfo.caption}>
                            <Avatar
                              sx={{
                                width: 26,
                                height: 26,
                                flexShrink: 0,
                                mb: 0.25,
                                bgcolor: senderInfo.avatarBg,
                                fontSize: '0.6rem',
                                fontWeight: 700,
                              }}
                            >
                              {senderInfo.avatarLabel}
                            </Avatar>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>

              {/* The "your reply will be sent under your own name" nudge now lives
                  folded into the single paused status bar above the thread —
                  see isPaused's block. */}

              {/* ── Composer — one bar, two modes (text / template) ──────────
                  A closed 24h window no longer hides the composer for either
                  channel: the text field just disables with an explanatory
                  placeholder (see the TextField below). For WhatsApp, the
                  template icon (the only way back in) stays live alongside it.
                  Instagram has no template equivalent — Meta gives no way back
                  in once the window closes (a fresh inbound message is the
                  only thing that reopens it) — so its placeholder says that
                  plainly instead of pointing at a control that doesn't exist. */}
              {(
                <Box
                  sx={{
                    bgcolor: '#F0F2F5',
                    borderTop: '1px solid #e2e8f0',
                    flexShrink: 0,
                  }}
                >
                  {isArchived && (
                    <Alert severity="warning" sx={{ mx: 2, mt: 1.25, borderRadius: 2 }}>
                      This contact is archived — unarchive from the ⋮ menu or the contact panel to
                      send a reply.
                    </Alert>
                  )}

                  {/* ── Live preview — the exact bubble the customer will receive,
                      re-rendered from the same TemplateCard used for sent messages
                      further up the thread, so filling in a variable updates it
                      in real time instead of leaving it as a raw {{n}} token.
                      Height-capped + its own scroll: a template with a media
                      header, footer, and buttons is tall — without a cap it was
                      squeezing the whole message thread out of view above it.
                      The cap now scales with viewport height instead of a flat
                      220px (which was clipping tall templates on any normal
                      monitor), and a bottom fade only appears when there's
                      actually more to scroll to, so a capped preview reads as
                      "scroll for more" instead of "this looks cut off". ── */}
                  {isTemplateMode && (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        ref={previewScrollRef}
                        sx={{
                          px: 2,
                          pt: 1.25,
                          pb: 0.5,
                          display: 'flex',
                          justifyContent: 'center',
                          maxHeight: { xs: 220, sm: 320, md: '38vh' },
                          overflowY: 'auto',
                          '&::-webkit-scrollbar': { width: 6 },
                          '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 3 },
                        }}
                      >
                      <TemplateCard
                        parsed={{ name: activeTemplateName, lang: activeTemplateDef?.language || 'en' }}
                        fullTemplate={activeTemplateDef}
                        // The preview must show the file that will actually be
                        // sent, not the template's stock one — otherwise the
                        // "exact bubble the customer will receive" promise this
                        // preview makes is broken by the one thing the agent
                        // just deliberately changed.
                        mediaUrl={templateHeaderAsset?.s3Url ?? mediaUrls[activeTemplateName]}
                        carouselMediaUrls={carouselMediaUrls[activeTemplateName]}
                        actualParams={{ body: activeTemplateValues }}
                        fallbackName={selectedConversation.customerName}
                      />
                      </Box>
                      {previewOverflowing && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 20,
                            background: 'linear-gradient(to bottom, transparent, #F0F2F5)',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </Box>
                  )}

                  <Box
                    // Delegated Enter-to-send for template/media mode: neither the
                    // per-variable fields nor the dynamic-URL-button field had any
                    // keydown handling at all (Enter did nothing), and the media
                    // caption field's own handler only fired while that ONE field
                    // was focused — none of it covered a template with zero
                    // variables (nothing to focus) or an audio attachment (no
                    // caption field at all). One handler on the whole row, keyed
                    // off mode rather than which specific field has focus, covers
                    // all of that. Plain-text mode is untouched — its TextField's
                    // own onKeyDown (handleInputKeyDown) already handles Enter, and
                    // this handler explicitly excludes that mode so the two can
                    // never both fire for the same keypress.
                    onKeyDown={(e) => {
                      if ((isMediaMode || isTemplateMode) && e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleComposerSend();
                      }
                    }}
                    sx={{
                      px: 2,
                      py: 1.25,
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 1,
                    }}
                  >
                  {selectedConversation.channel !== 'instagram' && (
                    <Tooltip
                      title={
                        isTemplateMode
                          ? 'Back to typing'
                          : isArchived
                            ? 'Contact is archived — unarchive to send a template'
                            : 'Send a template message'
                      }
                    >
                      <span>
                        <IconButton
                          onClick={isTemplateMode ? exitTemplateMode : openTemplateMenu}
                          disabled={isArchived && !isTemplateMode}
                          sx={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            border: '1px solid #e2e8f0',
                            bgcolor: '#fff',
                            '&.Mui-disabled': { bgcolor: '#fff', borderColor: '#e2e8f0' },
                          }}
                        >
                          {isTemplateMode ? (
                            <Close sx={{ fontSize: 18, color: '#64748b' }} />
                          ) : (
                            <Description sx={{ fontSize: 18, color: '#64748b' }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* ── Attach a library file ─────────────────────────────────
                      Shown on both channels as of Phase 2.1 (instagram/send-
                      media exists now — see admin.api.ts's sendInstagramMedia).

                      DISABLED rather than hidden outside the 24h window on
                      EITHER channel: Meta permits free-form media only inside
                      it, so the click genuinely cannot succeed — but hiding
                      the button taught agents "this account cannot send files
                      at all", which is false on WhatsApp: a template with a
                      media header delivers one perfectly well, and that path
                      is one button to the left. A disabled control with the
                      reason on it is what makes that discoverable; an absent
                      one cannot. Instagram has no such template fallback (no
                      DM-equivalent of a HSM), so its own closed-window message
                      says only that the window is closed, not to send a
                      template. (Same call the text field already makes:
                      disable, don't hide, because typing has somewhere to go
                      too.) ─────── */}
                  {(() => {
                    const isInstagramThread = selectedConversation.channel === 'instagram';
                    const closedWindowMessage = isInstagramThread
                      ? "24-hour window closed — Instagram only accepts replies within 24 hours of the customer's last message."
                      : '24-hour window closed — WhatsApp only accepts free-form files inside it. Send a template with an image/video/document header instead.';
                    return (
                      <Tooltip
                        title={
                          isArchived
                            ? 'Contact is archived — unarchive to attach a file'
                            : !canReply
                              ? closedWindowMessage
                              : isMediaMode
                                ? 'Remove file'
                                : 'Attach a file'
                        }
                      >
                        <span>
                          <IconButton
                            onClick={isMediaMode ? exitMediaMode : openMediaMenu}
                            disabled={!canReply || (isArchived && !isMediaMode)}
                            sx={{
                              width: 40,
                              height: 40,
                              flexShrink: 0,
                              border: '1px solid #e2e8f0',
                              bgcolor: '#fff',
                              '&.Mui-disabled': { bgcolor: '#fff', borderColor: '#e2e8f0' },
                            }}
                          >
                            {isMediaMode ? (
                              <Close sx={{ fontSize: 18, color: '#64748b' }} />
                            ) : (
                              <AttachFile sx={{ fontSize: 18, color: canReply ? '#64748b' : '#cbd5e1' }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    );
                  })()}

                  {isMediaMode ? (
                    // ── File staged in the composer. Same full-width card the
                    // template mode uses, so the two modes read as one family:
                    // what is being sent on top, what the agent can still fill
                    // in below. The caption is optional — WhatsApp shows the
                    // file on its own perfectly well without one.
                    <Box
                      sx={{
                        flex: 1,
                        bgcolor: '#fff',
                        border: '1px solid #4f46e5',
                        borderRadius: 3,
                        px: 2,
                        py: 1.25,
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        {/* ── Preview tile ───────────────────────────────────
                            An icon and a filename are not enough to catch the
                            most likely mistake here: picking the wrong file out
                            of a folder of similarly-named ones. So an image
                            shows itself. `s3Url` on a picker asset is a fresh
                            presigned GET (the list/browse endpoints re-sign on
                            every read), and a load failure degrades to the type
                            icon rather than a broken-image glyph.

                            Non-visual types keep the icon tile at the SAME
                            footprint, so the composer never changes height
                            depending on what was attached. ─────────────── */}
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            flexShrink: 0,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid #e2e8f0',
                            bgcolor: '#f8fafc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {activeMedia!.type === 'image' &&
                          activeMedia!.s3Url &&
                          mediaPreviewFailedFor !== activeMedia!._id ? (
                            <Box
                              component="img"
                              src={activeMedia!.s3Url}
                              alt={activeMedia!.name || activeMedia!.originalName}
                              onError={() => setMediaPreviewFailedFor(activeMedia!._id)}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                              }}
                            />
                          ) : (
                            mediaTypeIcon(activeMedia!.type, 26)
                          )}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            alignItems="baseline"
                            spacing={0.75}
                            sx={{ mb: activeMedia!.type === 'audio' ? 0 : 1 }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 700, color: '#4f46e5', minWidth: 0 }}
                              noWrap
                            >
                              {activeMedia!.name || activeMedia!.originalName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.disabled', flexShrink: 0 }}
                            >
                              {formatFileSize(activeMedia!.size)}
                            </Typography>
                          </Stack>
                          {/* No caption field for audio — Meta ignores captions
                              on voice notes, so offering one would promise
                              something the customer will never see. */}
                          {activeMedia!.type !== 'audio' && (
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              maxRows={3}
                              label="Caption (optional)"
                              // Meta's own ceiling — enforced here so the agent
                              // sees the limit while typing, not after a send.
                              inputProps={{ maxLength: 1024 }}
                              value={mediaCaption}
                              onChange={(e) => setMediaCaption(e.target.value)}
                              // Enter-to-send is now handled once, at the composer
                              // row level (delegated — see that Box's onKeyDown),
                              // rather than here too — this field's own handler
                              // would otherwise double-fire the send.
                            />
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  ) : isTemplateMode ? (
                    // ── Template loaded into the composer, WhatsApp-app-style:
                    // picking one from the popover swaps the text field for a
                    // full-width card with its name and one labeled input per
                    // variable — spans the same width the caption bar does in
                    // the real WhatsApp app, rather than a cramped fixed-width
                    // chip. Labels (not placeholders) so the field still says
                    // what it holds after the agent starts typing, and slots
                    // whose template-editor label says "name"/"phone" arrive
                    // prefilled from this conversation (see selectTemplate).
                    // Same Send arrow sends it.
                    <Box
                      sx={{
                        flex: 1,
                        bgcolor: '#fff',
                        border: '1px solid #4f46e5',
                        borderRadius: 3,
                        px: 2,
                        py: 1.25,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        sx={{
                          mb:
                            activeTemplateHeaderMediaType ||
                            activeTemplateVariableTokens.length > 0 ||
                            hasDynamicUrlButton(activeTemplateDef)
                              ? 1
                              : 0,
                        }}
                      >
                        <Description sx={{ fontSize: 15, color: '#4f46e5' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#4f46e5' }}>
                          {formatTemplateName(activeTemplateName)}
                        </Typography>
                      </Stack>

                      {/* ── Header media ─────────────────────────────────────
                          Only for templates that HAVE a media header — the one
                          kind that can put a file in front of a customer with
                          the window shut. Default is the template's own file
                          (unchanged behaviour); "Choose from library" swaps in
                          any library file of the same type, which is what Meta
                          approves — the type, not the file. ─────────────── */}
                      {activeTemplateHeaderMediaType && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.75}
                          sx={{
                            mb:
                              activeTemplateVariableTokens.length > 0 ||
                              hasDynamicUrlButton(activeTemplateDef)
                                ? 1
                                : 0,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1.5,
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            flexWrap: 'wrap',
                            rowGap: 0.5,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            {mediaTypeIcon(activeTemplateHeaderMediaType)}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', flexShrink: 0 }}
                          >
                            Header media:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              flex: 1,
                              minWidth: 80,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {templateHeaderAsset
                              ? templateHeaderAsset.name || templateHeaderAsset.originalName
                              : activeTemplateDef?.components.header?.headerMediaFileName ||
                                'Template default'}
                          </Typography>
                          <Button
                            size="small"
                            onClick={openTemplateHeaderPicker}
                            sx={{ minWidth: 0, px: 1, fontSize: '0.7rem', textTransform: 'none' }}
                          >
                            Choose from library
                          </Button>
                          {templateHeaderAsset && (
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => setTemplateHeaderAsset(null)}
                              sx={{
                                minWidth: 0,
                                px: 1,
                                fontSize: '0.7rem',
                                textTransform: 'none',
                                color: 'text.secondary',
                              }}
                            >
                              Use template default
                            </Button>
                          )}
                        </Stack>
                      )}

                      {(activeTemplateVariableTokens.length > 0 || hasDynamicUrlButton(activeTemplateDef)) && (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(180px, 1fr))' },
                            gap: 1,
                          }}
                        >
                          {activeTemplateVariableTokens.map((token, i) => (
                            <TextField
                              key={`${token}-${i}`}
                              size="small"
                              fullWidth
                              label={activeTemplateDef?.components.body?.variableLabels?.[i] || `Variable {{${token}}}`}
                              value={activeTemplateValues[i] ?? ''}
                              onChange={(e) => {
                                const next = [...activeTemplateValues];
                                next[i] = e.target.value;
                                setActiveTemplateValues(next);
                              }}
                            />
                          ))}
                          {hasDynamicUrlButton(activeTemplateDef) && (
                            <TextField
                              size="small"
                              fullWidth
                              label="Button link value"
                              placeholder="e.g. inv-98214"
                              value={activeButtonUrlValue}
                              onChange={(e) => setActiveButtonUrlValue(e.target.value)}
                              helperText="Not the full URL — just the part that replaces {{1}}."
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      size="small"
                      placeholder={
                        isArchived
                          ? 'Contact archived — unarchive to reply…'
                          : !canReply
                            ? selectedConversation.channel === 'instagram'
                              ? '24-hour window closed — Instagram has no template equivalent; wait for their next message…'
                              : '24h window closed — send a template to reply…'
                            : isPaused
                              ? 'AI is paused — type a manual reply…'
                              : 'Type a message…'
                      }
                      value={messageInput}
                      onChange={(e) => {
                        setMessageInput(e.target.value);
                        // Typing indicator — Instagram only (Phase 2.4).
                        if (selectedConversation?.channel === 'instagram') {
                          if (e.target.value.trim()) {
                            signalTyping(selectedConversation.channelIdentity?.value);
                          } else {
                            // Cleared the field entirely — same "done typing"
                            // signal as sending, so the indicator doesn't
                            // linger for the full auto-off window.
                            stopTypingIndicator();
                          }
                        }
                      }}
                      onBlur={() => {
                        if (selectedConversation?.channel === 'instagram') stopTypingIndicator();
                      }}
                      onKeyDown={handleInputKeyDown}
                      disabled={sendingMessage || !canReply || isArchived}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          bgcolor: '#fff',
                          '& fieldset': { border: '1px solid #e2e8f0' },
                          '&:hover fieldset': { borderColor: '#4f46e5' },
                          '&.Mui-focused fieldset': { borderColor: '#4f46e5', borderWidth: 1 },
                        },
                      }}
                    />
                  )}

                  <Tooltip
                    title={
                      isMediaMode ? 'Send file' : isTemplateMode ? 'Send template' : 'Send (Enter)'
                    }
                  >
                    <span>
                      <IconButton
                        onClick={handleComposerSend}
                        disabled={
                          isMediaMode
                            ? // No caption requirement — a file with no caption
                              // is a perfectly normal WhatsApp message. The
                              // window IS required: free-form media is
                              // undeliverable outside it.
                              sendingMedia || !canReply
                            : isTemplateMode
                              ? sendingTemplate ||
                                activeTemplateValues.some((v) => !v.trim()) ||
                                (hasDynamicUrlButton(activeTemplateDef) && !activeButtonUrlValue.trim())
                              : !messageInput.trim() || sendingMessage || !canReply
                        }
                        sx={{
                          bgcolor: '#4f46e5',
                          color: '#fff',
                          width: 40,
                          height: 40,
                          flexShrink: 0,
                          '&:hover': { bgcolor: '#4338ca' },
                          '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
                        }}
                      >
                        {sendingMessage || sendingTemplate || sendingMedia ? (
                          <CircularProgress size={16} sx={{ color: '#fff' }} />
                        ) : (
                          <Send sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  </Box>
                </Box>
              )}

              {templateSendError && (
                <Box sx={{ px: 2, pb: 1, bgcolor: '#F0F2F5', flexShrink: 0 }}>
                  <Alert severity="error" sx={{ py: 0 }} onClose={() => setTemplateSendError('')}>
                    {templateSendError}
                  </Alert>
                </Box>
              )}

              {mediaSendError && (
                <Box sx={{ px: 2, pb: 1, bgcolor: '#F0F2F5', flexShrink: 0 }}>
                  <Alert severity="error" sx={{ py: 0 }} onClose={() => setMediaSendError('')}>
                    {mediaSendError}
                  </Alert>
                </Box>
              )}

              {/* ── Template picker — a lightweight popover anchored to the
                  composer icon, not a separate dialog (WhatsApp/Gallabox pattern:
                  icon → inline list → selection loads into the composer). ─── */}
              <TemplatePickerPopover
                anchorEl={templateMenuAnchorEl}
                open={Boolean(templateMenuAnchorEl)}
                onClose={closeTemplateMenu}
                templates={sendableTemplates}
                onSelect={(t) => selectTemplate(t.name)}
                editUrlFor={(t) =>
                  `${window.location.pathname.startsWith('/admin') ? '/admin' : ''}` +
                  `/whatsapp-templates/${encodeURIComponent(t.name)}/edit` +
                  `?language=${encodeURIComponent(t.language)}`
                }
              />

              {/* ── Media picker — a folder browser, not a flat list ──────────
                  The library is a tree, so this walks it one level at a time:
                  breadcrumb on top, sub-folders first, then this folder's own
                  files. Search sits above both and flattens everything, which
                  is what stops a deep tree from being slower than the flat list
                  it replaces — browse when you know where it is, search when
                  you don't.

                  Files over WhatsApp's per-type ceiling are shown but not
                  selectable, with the reason spelled out. Hiding them would
                  leave an admin hunting for a file they just uploaded; the
                  reason is the only part that makes it fixable. The same rule
                  covers a wrong-type file when the picker is choosing a
                  template's header (see mediaPickMode). ─────────────────── */}
              <Popover
                anchorEl={mediaMenuAnchorEl}
                open={Boolean(mediaMenuAnchorEl)}
                onClose={closeMediaMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { sx: { width: 320, borderRadius: 2 } } }}
              >
                <Box sx={{ p: 1.25, pb: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder="Search all files…"
                    value={mediaSearch}
                    onChange={(e) => handleMediaSearchChange(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: 16, color: '#94a3b8' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Box>

                {/* Breadcrumb — hidden while searching, where "which folder"
                    is not the question being asked. Every crumb is clickable,
                    so getting back out is never more than one click. */}
                {!mediaQuery && (
                  <>
                    <Box
                      sx={{
                        px: 1.5,
                        pb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 0.25,
                      }}
                    >
                      <ButtonBase
                        onClick={() => void openMediaFolder(null)}
                        disabled={mediaFolderId === null}
                        sx={{
                          borderRadius: 1,
                          px: 0.5,
                          fontSize: '0.72rem',
                          fontWeight: mediaFolderId === null ? 700 : 500,
                          color: mediaFolderId === null ? 'text.primary' : '#4f46e5',
                        }}
                      >
                        Library
                      </ButtonBase>
                      {(currentBrowse?.path || []).map((crumb, i, all) => {
                        const isCurrent = i === all.length - 1;
                        return (
                          <Box key={crumb._id} sx={{ display: 'flex', alignItems: 'center' }}>
                            <ChevronRight sx={{ fontSize: 14, color: '#cbd5e1' }} />
                            <ButtonBase
                              onClick={() => void openMediaFolder(crumb._id)}
                              disabled={isCurrent}
                              sx={{
                                borderRadius: 1,
                                px: 0.5,
                                fontSize: '0.72rem',
                                fontWeight: isCurrent ? 700 : 500,
                                color: isCurrent ? 'text.primary' : '#4f46e5',
                                maxWidth: 120,
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {crumb.name}
                            </ButtonBase>
                          </Box>
                        );
                      })}
                    </Box>
                    <Divider />
                  </>
                )}

                <Box sx={{ maxHeight: 300, overflowY: 'auto', py: 0.5 }}>
                  {mediaListBusy && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}>
                      <CircularProgress size={18} />
                    </Box>
                  )}

                  {!mediaListBusy &&
                    visibleFolders.length === 0 &&
                    visibleAssets.length === 0 && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', px: 2, py: 2, color: 'text.disabled' }}
                      >
                        {mediaQuery
                          ? `No files match “${mediaSearch.trim()}”`
                          : mediaFolderId === null
                            ? 'No files in the media library yet'
                            : 'This folder is empty'}
                      </Typography>
                    )}

                  {/* Folders first — the same ordering every file browser uses,
                      so the list reads as "places, then things". */}
                  {visibleFolders.map((folder) => (
                    <MenuItem
                      key={folder._id}
                      onClick={() => void openMediaFolder(folder._id)}
                      sx={{ gap: 1, py: 0.75 }}
                    >
                      <FolderOutlined sx={{ fontSize: 17, color: '#f59e0b', flexShrink: 0 }} />
                      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {folder.name}
                      </Typography>
                      <ChevronRight sx={{ fontSize: 16, color: '#cbd5e1', flexShrink: 0 }} />
                    </MenuItem>
                  ))}

                  {visibleAssets.map((asset) => {
                    // Template-header mode is WhatsApp-only (Instagram has no
                    // template/HSM concept), so its size table is always the
                    // right one there; the plain 'attach' path is reached from
                    // either channel's composer and must check the CURRENT
                    // conversation's own platform limits.
                    const isInstagramThread = selectedConversation?.channel === 'instagram';
                    const sizeLimitTable =
                      mediaPickMode === 'templateHeader' || !isInstagramThread
                        ? WHATSAPP_MEDIA_SIZE_LIMIT_BYTES
                        : INSTAGRAM_MEDIA_SIZE_LIMIT_BYTES;
                    const platformName =
                      mediaPickMode === 'templateHeader' || !isInstagramThread ? 'WhatsApp' : 'Instagram';
                    const limit = sizeLimitTable[asset.type];
                    const overLimit = limit !== undefined && asset.size > limit;
                    // Header mode only: Meta approved the header's media TYPE,
                    // so a video cannot fill an IMAGE slot however much the
                    // agent wants it to — the server refuses it, and finding
                    // that out after clicking Send is the worst place to.
                    const wrongType =
                      headerPickType !== undefined && asset.type !== headerPickType;
                    const blockedReason = overLimit
                      ? `${formatFileSize(asset.size)} — over ${platformName}'s ${formatFileSize(
                          limit
                        )} limit for ${asset.type}s`
                      : wrongType
                        ? `Template header is ${headerPickType}`
                        : undefined;
                    return (
                      <Tooltip key={asset._id} title={blockedReason ?? ''} placement="left">
                        <Box component="span" sx={{ display: 'block' }}>
                          <MenuItem
                            disabled={overLimit || wrongType}
                            onClick={() => selectMedia(asset)}
                            sx={{ gap: 1, py: 0.75, alignItems: 'flex-start' }}
                          >
                            <Box sx={{ mt: '3px' }}>{mediaTypeIcon(asset.type)}</Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" noWrap>
                                {asset.name || asset.originalName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: blockedReason ? 'warning.dark' : 'text.disabled',
                                  whiteSpace: 'normal',
                                }}
                              >
                                {blockedReason ?? formatFileSize(asset.size)}
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Popover>
            </>
        </>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        // No PageHeader here — "Chat Logs" already titles the list column
        // right below, and the page-level title/subtitle just repeated it
        // while eating vertical space the three columns could use instead.
        //
        // Negative margin cancels AdminLayout's `.page-content` padding
        // (p:{sm:3,lg:4}) on all four sides — every other admin page keeps
        // that padding (it's the app-wide spacing rhythm), but a live inbox
        // is exactly the dense, edge-to-edge view that Front/Intercom/Slack
        // all break their own standard page chrome for. Scoped to this
        // page's own root Box, not AdminLayout, so nothing else moves.
        m: { xs: 0, sm: -3, lg: -4 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {listPanel}
        {chatPanel}
        {selectedConversation && (
          <ContactPanel
            contact={selectedConversation}
            open={showContactPanel}
            onToggle={toggleContactPanel}
            flagged={!!selectedConversation.flagged}
            flagReason={flagLabel(selectedConversation)}
            onMarkReviewed={() => handleClearFlag(selectedConversation._id)}
            markReviewedPending={actioningId === selectedConversation._id}
            {...(contactPanelConversationDetails ? { conversationDetails: contactPanelConversationDetails } : {})}
            routingHistory={contactPanelRoutingHistory}
            canEdit={selectedConversation.channel === 'instagram' ? canEditInstagram : canEditContact}
            canDeleteNotes={
              selectedConversation.channel === 'instagram' ? canDeleteInstagramNotes : canDeleteContactNotes
            }
            canViewContactExtras={
              selectedConversation.channel === 'instagram' ? canViewInstagramExtras : canViewContactExtras
            }
            onTagsSaved={(contactId, tags) => {
              setConversations((prev) => prev.map((c) => (c._id === contactId ? { ...c, tags } : c)));
              setSelectedConversation((prev) => (prev && prev._id === contactId ? { ...prev, tags } : prev));
            }}
            // Same availability rules as the header ⋮ menu — one source of truth.
            actions={{
              // Bot control is WhatsApp-only — Instagram has no bot path yet.
              ...(selectedConversation.channel !== 'instagram' && selectedRoutingState !== 'human'
                ? { onStopBot: () => handleSetBotState(selectedConversation._id, 'stop') }
                : {}),
              ...(selectedConversation.channel !== 'instagram' && canManageAssignment
                ? {
                    onAssign: () => {
                      setSelectedUserId(selectedConversation.assignedTo?._id || '');
                      setAssignDialogOpen(true);
                    },
                  }
                : {}),
              // Instagram's own assign — same dialog, gated on the Instagram
              // module's Full grant instead of activity.chatLogs'.
              ...(selectedConversation.channel === 'instagram' && canManageInstagramAssignment
                ? {
                    onAssign: () => {
                      setSelectedUserId(selectedConversation.assignedTo?._id || '');
                      setAssignDialogOpen(true);
                    },
                  }
                : {}),
              // Claim — Instagram only for now (see handleClaimConversation's
              // doc comment on why WhatsApp isn't wired here too). No extra
              // permission beyond reaching this page: claiming an unclaimed
              // (or already-mine) thread needs no elevated tier, same as the
              // backend's own gate.
              // Claim is offered ONLY on an unassigned thread. The backend only
              // lets you claim an unowned chat — offering it on your own chat
              // was a silent no-op, and on a colleague's it always failed 409.
              // Taking over a colleague's chat is what Reassign is for.
              ...(selectedConversation.channel === 'instagram' && !selectedConversation.assignedTo
                ? {
                    onClaim: () =>
                      handleClaimConversation(selectedConversation._id, selectedConversation.channel),
                  }
                : {}),
              ...(selectedConversation.channel === 'instagram' &&
              canManageInstagramAssignment &&
              selectedConversation.assignedTo
                ? { onUnassign: () => handleUnassignInstagramConversation(selectedConversation._id) }
                : {}),
              ...(selectedConversation.channel !== 'instagram' &&
              selectedRoutingState !== 'bot' &&
              canHandBack(selectedConversation)
                ? { onResumeBot: () => handleSetBotState(selectedConversation._id, 'resume') }
                : {}),
              onExport: () => void handleExportConversation(selectedConversation),
              onArchive: () => void handleArchiveConversation(selectedConversation._id),
              isArchived: selectedConversation.status === 'archived',
            }}
          />
        )}
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedConversation) void handleExportConversation(selectedConversation);
          }}
        >
          Export Conversation
        </MenuItem>
        {/* Bot control — WhatsApp only, no equivalent on Instagram. */}
        {selectedConversation && selectedConversation.channel !== 'instagram' && (
          selectedRoutingState !== 'human' ? (
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleSetBotState(selectedConversation._id, 'stop');
              }}
            >
              Stop bot
            </MenuItem>
          ) : canHandBack(selectedConversation) ? (
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleSetBotState(selectedConversation._id, 'resume');
              }}
            >
              Hand back to bot
            </MenuItem>
          ) : null
        )}
        {selectedConversation && selectedConversation.channel !== 'instagram' && canManageAssignment && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setSelectedUserId(selectedConversation.assignedTo?._id || '');
              setAssignDialogOpen(true);
            }}
          >
            Assign to Team Member
          </MenuItem>
        )}
        {/* Instagram assignment trio — Claim needs no elevated permission
            (self-assign), Assign/Unassign need Full on the instagram module,
            same gates as the ContactPanel actions above. */}
        {selectedConversation && selectedConversation.channel === 'instagram' && !selectedConversation.assignedTo && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              handleClaimConversation(selectedConversation._id, selectedConversation.channel);
            }}
          >
            Assign to me
          </MenuItem>
        )}
        {selectedConversation && selectedConversation.channel === 'instagram' && canManageInstagramAssignment && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setSelectedUserId(selectedConversation.assignedTo?._id || '');
              setAssignDialogOpen(true);
            }}
          >
            Assign to Team Member
          </MenuItem>
        )}
        {selectedConversation &&
          selectedConversation.channel === 'instagram' &&
          canManageInstagramAssignment &&
          selectedConversation.assignedTo && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              handleUnassignInstagramConversation(selectedConversation._id);
            }}
          >
            Unassign
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedConversation) void handleArchiveConversation(selectedConversation._id);
          }}
        >
          {selectedConversation?.status === 'archived' ? 'Unarchive' : 'Archive'}
        </MenuItem>
      </Menu>

      {/* Profile popover — opened from the chat header avatar/name. */}
      <ContactProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        contact={
          selectedConversation
            ? {
                channel: selectedConversation.channel,
                customerName: selectedConversation.customerName,
                username: selectedConversation.username,
                customerPhone: selectedConversation.customerPhone,
                profilePictureUrl: selectedConversation.profilePictureUrl,
                followerCount: selectedConversation.followerCount,
                isUserFollowBusiness: selectedConversation.isUserFollowBusiness,
                isBusinessFollowUser: selectedConversation.isBusinessFollowUser,
                isVerifiedUser: selectedConversation.isVerifiedUser,
              }
            : null
        }
      />

      {/* ── Assign dialog (requires Full access to Chat Logs) ───────────────── */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Conversation</DialogTitle>
        <DialogContent>
          {(() => {
            // Instagram offers only members the backend will accept (Full on
            // the 'instagram' module); anyone else used to fail with a 404
            // after clicking Assign. WhatsApp's list is unchanged.
            const isInstagramAssign = selectedConversation?.channel === 'instagram';
            const options =
              isInstagramAssign && instagramAssignableIds
                ? teamMembers.filter((m) => instagramAssignableIds.includes(m._id))
                : teamMembers;
            return (
              <>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id="chatlogs-assign-dialog-label">Select Team Member</InputLabel>
                  <Select
                    labelId="chatlogs-assign-dialog-label"
                    label="Select Team Member"
                    value={options.some((m) => m._id === selectedUserId) ? selectedUserId : ''}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                  >
                    {options.map((member) => (
                      <MenuItem key={member._id} value={member._id}>
                        {member.firstName} {member.lastName} ({member.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {isInstagramAssign && instagramAssignableIds && options.length === 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No team member has Full access to Instagram yet. Grant it in User Management →
                    Roles, then assign.
                  </Alert>
                )}
                {isInstagramAssign && options.length > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Only members with Full access to Instagram are listed.
                  </Typography>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAssignSubmit} variant="contained" disabled={assigning || !selectedUserId}>
            {assigning ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign / claim / hand-back failures — e.g. a claim lost to another
          admin (409), or a hand-back attempted by someone who isn't the
          assignee or the owner (404) — plus conversation-list fetch failures
          (e.g. a role without 'instagram' or 'activity.chatLogs' permission),
          so a permission denial reads as an error, not an empty inbox. */}
      <Snackbar
        open={!!actionNotice}
        autoHideDuration={3000}
        onClose={() => setActionNotice('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setActionNotice('')} severity="success" variant="filled">
          {actionNotice}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!actionError}
        autoHideDuration={6000}
        onClose={() => setActionError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setActionError('')} severity="error" variant="filled">
          {actionError}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!templateUnfitToast}
        autoHideDuration={10000}
        onClose={() => setTemplateUnfitToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          icon={<WarningAmberRounded fontSize="small" />}
          onClose={() => setTemplateUnfitToast(null)}
          sx={{
            bgcolor: '#ffffff',
            color: '#1e293b',
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            boxShadow: '0 8px 28px rgba(15,23,42,0.14)',
            alignItems: 'center',
            '& .MuiAlert-icon': { color: '#f59e0b' },
          }}
          action={
            <Button
              size="small"
              variant="contained"
              href={templateUnfitToast?.editUrl || ''}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setTemplateUnfitToast(null)}
              sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
            >
              Edit template
            </Button>
          }
        >
          Template unfit to send — please upload/re-upload its media.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatLogs;
